/**
 * DecomBowlerMetrics - Monthly KPI bowler chart for Decommissioning.
 * Shows Target / Forecast / Actual / PY rows for each month of the selected year.
 * Actuals are derived from the decommissioningData prop (decom_date field).
 * Targets & Forecasts are fetched from the backend (graceful fallback to blank).
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    Tooltip,
    Popover,
    Checkbox,
    ListItemText,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
} from '@mui/material';
import { Close, Search, Download, FilterAlt, FilterAltOutlined, Settings } from '@mui/icons-material';
import { authFetch } from '../services/authFetch';
import { API_BASE_URL } from '../config/api';

// --- Types --------------------------------------------------------------------

interface DecommissioningItem {
    portfolio: string;
    platform: string;
    app_id: string;
    app_name: string;
    decom_date: string | null;
    decom_status: number;
    status: string;
    /** MAC "suspended" flag. Inactive + suspended = system-decommissioned (excluded from the bowler). */
    suspended?: boolean | null;
    erm_tdd: string | null;
    [key: string]: any;
}

interface KpiRow {
    kpiId: string;
    kpiLabel: string;
    /** Scorecard category shown in the Type column */
    type: 'Outcome' | 'Leading';
    owner: string;
    uom: string;
    /** Display string shown in the FY Target column (e.g. '95%', '$xx', '<0.5m') */
    fyTargetDisplay?: string;
    trigger: number;
    /** 'up' = higher is better (green = target); 'down' = lower is better (green = target) */
    direction: 'up' | 'down';
    // monthKey ? value (null = blank)
    target: Record<string, number | null>;
    forecast: Record<string, number | null>;
}

interface BackendKpiRow {
    kpi_id: string;
    kpi_label: string;
    owner: string;
    uom: string;
    trigger: number;
    monthly: Array<{
        month_key: string; // "202601"
        target: number | null;
        forecast: number | null;
    }>;
}

interface Props {
    decommissioningData: DecommissioningItem[];
}

// --- Constants ----------------------------------------------------------------

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

const ROW_LABELS: Array<{ key: 'target' | 'forecast' | 'actual' | 'py' | 'actual_pct' | 'actual_count'; label: string }> = [
    { key: 'target', label: 'Target' },
    { key: 'actual', label: 'Actual' },
];

const COST_SAVINGS_ROW_LABELS: Array<{ key: 'target' | 'forecast' | 'actual' | 'py' | 'actual_pct' | 'actual_count'; label: string }> = [
    { key: 'target',   label: 'Target' },
    { key: 'actual',   label: 'Actual' },
    { key: 'forecast', label: 'Forecast' },
];

const DECOM_RATE_ROW_LABELS: Array<{ key: 'target' | 'forecast' | 'actual' | 'py' | 'actual_pct' | 'actual_count'; label: string }> = [
    { key: 'target',   label: 'Target' },
    { key: 'actual',   label: 'Actual' },
    { key: 'forecast', label: 'Forecast' },
];

const SLIPPAGE_ROW_LABELS: Array<{ key: 'target' | 'actual_pct' | 'actual_count'; label: string }> = [
    { key: 'target',       label: 'Target' },
    { key: 'actual_pct',   label: 'Actual in %' },
    { key: 'actual_count', label: 'Actual' },
];

/** Which row labels are visible for a given KPI */
const getVisibleRows = (kpi: KpiRow) =>
    kpi.kpiId === 'slippage_rate' ? SLIPPAGE_ROW_LABELS
    : kpi.kpiId === 'decom_completion_rate' ? DECOM_RATE_ROW_LABELS
    : kpi.kpiId === 'cost_savings' ? COST_SAVINGS_ROW_LABELS
    : ROW_LABELS;

// --- Helpers ------------------------------------------------------------------

const parseDate = (v: any): Date | null => {
    if (!v || v === 'null') return null;
    const s = typeof v === 'string' ? v : String(v);
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
};

/** Returns "YYYYMM" string for a date, e.g. "202604" */
const monthKey = (d: Date): string =>
    `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}`;

/**
 * Parses an ERM decomm tag like "ERM-OP 2026(May)" ? { year: 2026, month: 5 }.
 * Returns null if the tag doesn't match the expected format.
 */
const parseDecommTagDate = (tag: string | null | undefined): { year: number; month: number } | null => {
    if (!tag) return null;
    const m = String(tag).match(/(\d{4})\s*\((\w+)\)/);
    if (!m) return null;
    const yr = parseInt(m[1], 10);
    const monthIdx = MONTH_NAMES.findIndex((name) => name.toLowerCase().startsWith(m[2].toLowerCase()));
    if (monthIdx === -1) return null;
    return { year: yr, month: monthIdx + 1 };
};

/**
 * Returns the YYYYMM key for an app\'s ERM target decom month.
 * Prefers erm_tdd; falls back to the month in the ERM decomm_tag
 * (e.g. "ERM-OP 2026(May)" when erm_tdd is not set).
 */
const getErmTargetMk = (item: DecommissioningItem, year: number): string | null => {
    const d = parseDate(item.erm_tdd);
    if (d && d.getUTCFullYear() === year) return monthKey(d);
    const tagDate = parseDecommTagDate(item.tag?.decomm_tag);
    if (tagDate && tagDate.year === year) return `${year}${String(tagDate.month).padStart(2, '0')}`;
    return null;
};

type DrillCategory = 'planned_early' | 'planned_ontime' | 'planned_delayed' | 'planned_pending' | 'unplanned_decom';

/**
 * Categorise an app by comparing its ERM target month against its actual decom month.
 * yr is used solely to scope which year's target to look up.
 */
const getDrillCategory = (app: DecommissioningItem, yr: number, _mo: number): DrillCategory => {
    const decomDate = parseDate(app.decom_date);
    const targetMk = getErmTargetMk(app, yr);

    if (!targetMk && decomDate) return 'unplanned_decom';
    if (targetMk && !decomDate) return 'planned_pending';
    if (!targetMk && !decomDate) return 'planned_pending';

    // Both target month and actual decom date exist - compare at month granularity
    const tNum = parseInt(targetMk!, 10); // YYYYMM as number
    const dNum = decomDate!.getUTCFullYear() * 100 + (decomDate!.getUTCMonth() + 1);

    if (dNum < tNum) return 'planned_early';
    if (dNum === tNum) return 'planned_ontime';
    return 'planned_delayed';
};

/** Compute Decomm Status label for any app — used in all drill-down tables. */
const getTargetDecommStatus = (app: DecommissioningItem): string => {
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const decom     = parseDate(app.decom_date);
    const tdd       = parseDate(app.target_date_decom);  // Current/New Target Decomm Date
    const erm       = parseDate(app.erm_tdd);             // Original Target Decomm Date
    const isInactive = app.status === 'Inactive';

    // Decommissioned (has a decom date or is inactive in MAC)
    if (decom || isInactive) {
        if (erm && decom) {
            // Allow up to 7 days grace after the original ERM target — still counts as on-time
            const diffDays = (decom.getTime() - erm.getTime()) / 86400000;
            return diffDays <= 7 ? 'Decommissioned' : 'Decommissioned (Delayed)';
        }
        return 'Decommissioned';
    }

    // Not yet decommissioned:
    if (!tdd) return 'Cancelled';
    if (!erm) return 'To be decommissioned';
    if (tdd > erm) return 'Delayed';             // slipped beyond original — regardless of whether erm is past or future
    if (erm > today) return 'To be decommissioned'; // on track and original date is still in the future
    if (monthKey(erm) === monthKey(tdd)) return 'Awaiting Decomm Confirmation';
    return 'Delayed';
};

const TARGET_DECOMM_STATUS_COLOR: Record<string, string> = {
    'Decommissioned':               '#2e7d32',  // green  – on-time or early
    'Decommissioned (Delayed)':     '#c62828',  // red    – completed late
    'To be decommissioned':         '#9e9e9e',  // grey   – future, no action needed yet
    'Awaiting Decomm Confirmation': '#c62828',  // red    – overdue, not extended
    'Delayed':                      '#c62828',  // red    – overdue, new date set
    'Cancelled':                    '#c62828',  // red    – no target date
};

const TARGET_DECOMM_STATUS_TOOLTIP: Record<string, string> = {
    'To be decommissioned':         'Application planned for decommissioning.',
    'Decommissioned':               'Application status based on MAC inactive (subject to audit validation).',
    'Delayed':                      'Decommission timeline extended beyond the original target date; application remains active.',
    'Cancelled':                    'Application no longer planned for decommissioning; retained for continued use and target decomm. date removed in MAC.',
    'Awaiting Decomm Confirmation': 'Target date has passed; requires owner action to confirm decommission or update timeline in MAC.',
};

/** Returns true when New Target Decomm Date has slipped beyond the Original Target Decomm Date */
const isAppDateSlipped = (app: DecommissioningItem): boolean => {
    if (app.status === 'Inactive') return false; // already decommissioned, no slip
    const orig = parseDate(app.erm_tdd);
    const curr = parseDate(app.target_date_decom);
    if (!curr) return true;  // no target date = cancelled = red
    if (!orig) return false;
    return curr > orig;
};

/** Returns the correct colour for the MAC Status chip */
const getMacStatusColor = (app: DecommissioningItem): string =>
    (app.status === 'Inactive' || !isAppDateSlipped(app)) ? '#2e7d32' : '#c62828';

const CATEGORY_LABEL: Record<DrillCategory, string> = {
    planned_early:   'Planned & Early Decom\'d',
    planned_ontime:  "Planned & On-Time Decom'd",
    planned_delayed: 'Planned & Delayed Decom\'d',
    planned_pending: 'Planned & Pending',
    unplanned_decom: 'Unplanned & Decom\'d',
};
const CATEGORY_COLOR: Record<DrillCategory, string> = {
    planned_early:   '#0288d1',   // sky blue - ahead of schedule
    planned_ontime:  '#4caf50',   // green  - on time
    planned_delayed: '#ff9800',   // orange - late but done
    planned_pending: '#f44336',   // red    - not yet done
    unplanned_decom: '#9c27b0',   // purple - no target set
};
const CATEGORY_TOOLTIP: Record<DrillCategory, string> = {
    planned_early:   "Decommission completed before planned TDD.",
    planned_ontime:  "Applications marked Inactive in MAC (proxy for decommissioned). Pending audit validation for infra/lic/vendor.",
    planned_delayed: "Decommission completed after planned TDD.",
    planned_pending: "Applications with future or upcoming decommission date not yet executed.",
    unplanned_decom: "Decommission completed but no ERM target existed for this app.",
};
/** Compute cumulative actual counts per month for a given year from decommissioning data */
const computeActuals = (
    data: DecommissioningItem[],
    year: number,
): Record<string, number> => {
    // Count apps decommissioned in each calendar month
    const monthly: Record<string, number> = {};
    for (const item of data) {
        const d = parseDate(item.decom_date);
        if (!d) continue;
        if (d.getUTCFullYear() !== year) continue;
        const mk = monthKey(d);
        monthly[mk] = (monthly[mk] ?? 0) + 1;
    }
    // Build cumulative
    const cumulative: Record<string, number> = {};
    let running = 0;
    for (let m = 1; m <= 12; m++) {
        const mk = `${year}${String(m).padStart(2, '0')}`;
        running += monthly[mk] ?? 0;
        if (running > 0 || cumulative[`${year}${String(m - 1).padStart(2, '0')}`] !== undefined) {
            cumulative[mk] = running;
        }
    }
    return cumulative;
};

/** Colour a cell based on direction: 'up' ? green if actual = target; 'down' ? green if actual = target */
const cellBg = (
    type: 'actual' | 'py',
    value: number | null,
    target: number | null,
    direction: 'up' | 'down' = 'up',
): string => {
    if (value == null || Number.isNaN(value)) return 'transparent';
    if (type !== 'actual') return 'transparent'; // PY: plain, no background
    if (target == null) return 'transparent';    // no target set: plain
    const good = direction === 'up' ? value >= target : value <= target;
    return good ? '#c8e6c9' : '#ffcdd2';
};

/** Light background colour for forecast compartment — softer than solid actual colours */
const forecastBg = (
    value: number | null,
    target: number | null,
    direction: 'up' | 'down' = 'up',
): string => {
    if (value == null || Number.isNaN(value)) return 'transparent';
    if (target == null) return 'transparent';
    const good = direction === 'up' ? value >= target : value <= target;
    return good ? '#c8e6c9' : '#ffcdd2';
};

// --- Hardcoded targets/forecasts from the bowler screenshot ------------------

/** Cumulative monthly targets for 2026 (Jan-Dec) */
const HARDCODED_TARGET_2026: Record<string, number> = {
    '202601': 10,
    '202602': 15,
    '202603': 31,
    '202604': 34,
    '202605': 37,
    '202606': 68,
    '202607': 79,
    '202608': 83,
    '202609': 110,
    '202610': 154,
    '202611': 155,
    '202612': 217,
};

/** Cumulative monthly forecasts for 2026 (Apr-Dec; Jan-Mar intentionally blank) */
const HARDCODED_FORECAST_2026: Record<string, number> = {
    '202604': 36,
    '202605': 39,
    '202606': 61,
    '202607': 70,
    '202608': 73,
    '202609': 97,
    '202610': 139,
    '202611': 140,
    '202612': 201,
};

/** Monthly thresholds for Cyber Risk Exposure KPI (non-cumulative targets for 2026) */
const HARDCODED_TARGET_CYBER_2026: Record<string, number> = {
    '202601': 20,
    '202602': 20,
    '202603': 20,
    '202604': 20,
    '202605': 15,
    '202606': 15,
    '202607': 10,
    '202608': 10,
    '202609': 5,
    '202610': 5,
    '202611': 0,
    '202612': 0,
};

// --- Zero targets for snapshot KPIs -----------------------------------------

const ZERO_TARGET_2026: Record<string, number> = {
    '202601': 0, '202602': 0, '202603': 0, '202604': 0,
    '202605': 0, '202606': 0, '202607': 0, '202608': 0,
    '202609': 0, '202610': 0, '202611': 0, '202612': 0,
};

/** Monthly cost-leakage target: $500k FY budget divided equally across 12 months */
const COST_LEAKAGE_MONTHLY_TARGET_2026: Record<string, number> = {
    '202601': 41667, '202602': 41667, '202603': 41667, '202604': 41667,
    '202605': 41667, '202606': 41667, '202607': 41667, '202608': 41667,
    '202609': 41667, '202610': 41667, '202611': 41667, '202612': 41667,
};

/** 100% monthly targets for percentage completion KPIs (goal = full compliance) */
const HUNDRED_PCT_TARGET_2026: Record<string, number> = {
    '202601': 100, '202602': 100, '202603': 100, '202604': 100,
    '202605': 100, '202606': 100, '202607': 100, '202608': 100,
    '202609': 100, '202610': 100, '202611': 100, '202612': 100,
};

/** 0% monthly targets for slippage KPI (goal = zero slippage) */
const ZERO_PCT_TARGET_2026: Record<string, number> = {
    '202601': 0, '202602': 0, '202603': 0, '202604': 0,
    '202605': 0, '202606': 0, '202607': 0, '202608': 0,
    '202609': 0, '202610': 0, '202611': 0, '202612': 0,
};

const FIVE_PCT_TARGET_2026: Record<string, number> = {
    '202601': 5, '202602': 5, '202603': 5, '202604': 5,
    '202605': 5, '202606': 5, '202607': 5, '202608': 5,
    '202609': 5, '202610': 5, '202611': 5, '202612': 5,
};

// --- Trends-style helpers for revision-aware historical snapshot computation --

/** True if a revision fieldName refers to a target decommissioning date */
const isTargetFieldForDecom = (fieldName: string): boolean => {
    const f = (fieldName || '').toLowerCase();
    return f.includes('target') && f.includes('decommission') && f.includes('date')
        && !f.includes('reason') && !f.includes('extension');
};

const parseISODate = (v: any): Date | null => {
    if (!v || v === 'null') return null;
    const s = typeof v === 'string' ? v : (Array.isArray(v) ? v[0] : String(v));
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
};

/**
 * Find if the TDD was moved from a target-year date to a DIFFERENT date within [start, end].
 * Mirrors DecommissionTrends.getDelayedInWindow - "Delayed" column in Trends tab.
 */
const getDelayedInWindow = (
    item: any,
    start: Date,
    end: Date,
    year: number,
): { date: Date; oldValue: string; newValue: string } | null => {
    if (!item.revisions || !Array.isArray(item.revisions)) return null;
    let best: { date: Date; oldValue: string; newValue: string } | null = null;
    for (const rev of item.revisions) {
        if (!rev.values || !Array.isArray(rev.values)) continue;
        const revDate = parseISODate(rev.modifiedDate);
        if (!revDate || revDate < start || revDate > end) continue;
        for (const v of rev.values) {
            if (!isTargetFieldForDecom(v.fieldName || '')) continue;
            const parsedOld = parseISODate(v.oldValue);
            const parsedNew = parseISODate(v.newValue);
            // Old TDD must be in the target year, moved to a genuinely different date
            if (
                parsedOld && parsedOld.getUTCFullYear() === year &&
                parsedNew && parsedOld.getTime() !== parsedNew.getTime()
            ) {
                if (!best || revDate > best.date) {
                    best = { date: revDate, oldValue: String(v.oldValue), newValue: String(v.newValue) };
                }
            }
        }
    }
    return best;
};

/**
 * Reconstruct the target decommissioning date as it was at the END of a given month
 * by undoing any revisions made AFTER monthEnd (mirrors DecommissionTrends logic).
 */
const getTargetAtMonthEnd = (item: any, monthEnd: Date): Date | null => {
    const revisions: { modifiedDate: Date; oldValue: any }[] = [];
    if (item.revisions && Array.isArray(item.revisions)) {
        for (const rev of item.revisions) {
            if (!rev.values || !Array.isArray(rev.values)) continue;
            for (const v of rev.values) {
                if (!isTargetFieldForDecom(v.fieldName || '')) continue;
                const d = parseISODate(rev.modifiedDate);
                if (d) revisions.push({ modifiedDate: d, oldValue: v.oldValue });
            }
        }
    }

    let effectiveStr: string | null = null;
    for (const f of ['targetDecommissioningDate', 'target_date_decom', 'targetDate', 'target_date']) {
        const val = item[f];
        if (val && val !== 'null') { effectiveStr = String(val); break; }
    }

    revisions.sort((a, b) => b.modifiedDate.getTime() - a.modifiedDate.getTime());
    for (const rev of revisions) {
        if (rev.modifiedDate > monthEnd) {
            const ov = rev.oldValue;
            effectiveStr = (ov === null || ov === undefined || ov === 'null') ? null : String(ov);
        }
    }
    return parseISODate(effectiveStr);
};

/**
 * Returns the apps matching a snapshot KPI as of the end of a given month.
 *
 * For delayed_apps / delayed_mvc_apps:
 *   An app is counted as "delayed" in a month if it is still open AND either:
 *   - "Delayed Overdue" : histTarget = cutoff (target date has already passed)
 *   - "Delayed (persistent)": had a TDD date-change event any time from Jan 1 ? monthEnd
 *     - keeps the app in the count every subsequent month until it is decommissioned
 *     or cancelled, mirroring the expected bowler behaviour.
 *
 * For bia_expired / mvc_failover_overdue / mvc_restore_overdue - same persistence
 * logic (overdue OR date-changed YTD), plus their own compliance checks.
 */
const getSnapshotAppsAsOf = (
    data: DecommissioningItem[],
    year: number,
    month: number, // 1-based
    kpiId: string,
): DecommissioningItem[] => {
    const yearStart = new Date(Date.UTC(year, 0, 1));          // Jan 1 of selected year
    const monthEnd = new Date(Date.UTC(year, month, 0, 23, 59, 59, 999));
    const now = new Date();
    now.setUTCHours(0, 0, 0, 0);
    // For past months use end-of-month as cutoff; for current/future use today
    const cutoff = now < monthEnd ? now : monthEnd;

    return data.filter((item) => {
        const statusLower = (item.status || '').toLowerCase();
        // Mirror calculateDecommissioningStatus: Inactive = Decommissioned; Cancelled = dropped
        const isClosed = statusLower === 'cancelled' || statusLower === 'inactive';
        const decom = parseDate(item.decom_date);
        const openInMonth = !isClosed && !(decom && decom <= monthEnd);

        // -- delayed_apps / delayed_mvc_apps ----------------------------------
        if (kpiId === 'delayed_apps' || kpiId === 'delayed_mvc_apps') {
            const mvcOk = kpiId === 'delayed_mvc_apps' ? !!item.mvc_group : true;
            if (!mvcOk || !openInMonth) return false;

            const histTarget = getTargetAtMonthEnd(item, monthEnd);
            if (!histTarget || histTarget.getUTCFullYear() !== year) return false;

            // Overdue: target date has already passed
            if (histTarget <= cutoff) return true;

            // Persistent delayed: TDD was pushed any time from Jan 1 up to this month-end
            // - app stays in the count every subsequent month until decommissioned/cancelled
            return getDelayedInWindow(item, yearStart, monthEnd, year) !== null;
        }

        // -- compliance KPIs ----------------------------------------------------
        if (!openInMonth) return false;
        const histTarget = getTargetAtMonthEnd(item, monthEnd);
        if (!histTarget || histTarget.getUTCFullYear() !== year) return false;

        if (kpiId === 'bia_expired') {
            // Same persistent logic as delayed_apps: overdue OR date was pushed since Jan 1
            const isDelayed = histTarget <= cutoff || getDelayedInWindow(item, yearStart, monthEnd, year) !== null;
            if (!isDelayed) return false;
            return item.bia_status === false;
        }

        if (kpiId === 'mvc_failover_overdue' || kpiId === 'mvc_restore_overdue') {
            if (!item.mvc_group) return false;
            // Count any delayed MVC app: overdue OR date was pushed (same as delayed_mvc_apps)
            const isDelayed = histTarget <= cutoff || getDelayedInWindow(item, yearStart, monthEnd, year) !== null;
            if (!isDelayed) return false;
            // Test is overdue if: no date at all OR last test was more than 12 months before cutoff
            const twelveMonthsAgo = new Date(cutoff);
            twelveMonthsAgo.setUTCFullYear(twelveMonthsAgo.getUTCFullYear() - 1);
            if (kpiId === 'mvc_failover_overdue') {
                // Combined metric: overdue on failover OR restore (either gap = test gap)
                const fd = parseDate(item.failover_tested_date_last_12_months);
                const rd = parseDate(item.backup_tested_last_12_months);
                return (!fd || fd < twelveMonthsAgo) || (!rd || rd < twelveMonthsAgo);
            }
            const rd = parseDate(item.backup_tested_last_12_months);
            return !rd || rd < twelveMonthsAgo;
        }
        return false;
    });
};

// --- Percentage-based rate KPI actuals computation ---------------------------

/**
 * Computes monthly % actuals for the four rate KPIs, scoped to ERM-tagged apps.
 *
 * In-scope: not cancelled, has a TDD in `year` as of the end of each month
 * (revision-aware via getTargetAtMonthEnd).
 *
 * - decom_completion_rate  - decommissioned / total in-scope * 100 (cumulative)
 * - failover_completeness  - MVC apps w/ valid failover test / total active MVC * 100
 * - restore_completeness   - MVC apps w/ valid restore test / total active MVC * 100
 * - bia_validity           - apps w/ valid BIA / total active in-scope * 100
 */
const computeRateActuals = (
    data: DecommissioningItem[],
    year: number,
    kpiId: string,
): Record<string, number> => {
    const result: Record<string, number> = {};
    const now = new Date();
    now.setUTCHours(0, 0, 0, 0);
    const currentYear = now.getUTCFullYear();
    const currentMonth = now.getUTCMonth() + 1;

    // Dynamic FY target: count of apps with an ERM target month in the given year
    // (covers both apps with a decomm_tag and apps with only an erm_tdd field)
    const fyErmTotal = data.filter((item) => getErmTargetMk(item, year) !== null).length;

    for (let mo = 1; mo <= 12; mo++) {
        if (year > currentYear) break;
        if (year === currentYear && mo > currentMonth) break;

        const mk = `${year}${String(mo).padStart(2, '0')}`;
        const monthEnd = new Date(Date.UTC(year, mo, 0, 23, 59, 59, 999));
        const cutoff = now < monthEnd ? now : monthEnd;
        const twelveMonthsAgo = new Date(cutoff);
        twelveMonthsAgo.setUTCFullYear(twelveMonthsAgo.getUTCFullYear() - 1);

        // Apps with TDD in the target year (not cancelled)
        const inScope = data.filter((item) => {
            if ((item.status || '').toLowerCase() === 'cancelled') return false;
            const histTarget = getTargetAtMonthEnd(item, monthEnd);
            return histTarget && histTarget.getUTCFullYear() === year;
        });

        if (kpiId === 'decom_completion_rate') {
            // Rate = cumulative decommissioned ERM apps by month-end
            //        - total apps with erm_tdd in year (dynamic, from ERM decomm tags) - 100
            // Denominator = full plan (unfiltered). Numerator excludes system-decommissioned
            // (Inactive + suspended) apps so they are never credited as a real decommission.
            if (!fyErmTotal) continue;
            const decommissioned = data.filter((item) => {
                if (item.status === 'Inactive' && item.suspended === true) return false;
                const d = parseDate(item.decom_date);
                return d && d <= monthEnd;
            });
            result[mk] = Math.round((decommissioned.length / fyErmTotal) * 100);
            continue;
        }

        // Active (not yet decommissioned) in-scope apps
        const activeInScope = inScope.filter((item) => {
            const sl = (item.status || '').toLowerCase();
            if (sl === 'inactive') return false;
            const d = parseDate(item.decom_date);
            return !d || d > monthEnd;
        });

        if (kpiId === 'failover_completeness') {
            // Denominator: Delayed MVC apps (slipped + MVC) cumulative up to this month
            const delayedMvcApps = data.filter((item) => {
                if (!item.mvc_group) return false;
                if (getErmTargetMk(item, year) === null) return false;
                const slipMk = getFirstSlipMk(item, year);
                return slipMk !== null && slipMk <= mk;
            });
            if (delayedMvcApps.length === 0) continue;
            const compliant = delayedMvcApps.filter((item) => {
                const fd = parseDate(item.failover_tested_date_last_12_months);
                return fd && fd >= twelveMonthsAgo;
            });
            result[mk] = Math.round((compliant.length / delayedMvcApps.length) * 100);
            continue;
        }

        if (kpiId === 'restore_completeness') {
            // Denominator: Delayed MVC apps (slipped + MVC) cumulative up to this month
            const delayedMvcApps = data.filter((item) => {
                if (!item.mvc_group) return false;
                if (getErmTargetMk(item, year) === null) return false;
                const slipMk = getFirstSlipMk(item, year);
                return slipMk !== null && slipMk <= mk;
            });
            if (delayedMvcApps.length === 0) continue;
            const compliant = delayedMvcApps.filter((item) => {
                const rd = parseDate(item.backup_tested_last_12_months);
                return rd && rd >= twelveMonthsAgo;
            });
            result[mk] = Math.round((compliant.length / delayedMvcApps.length) * 100);
            continue;
        }

        if (kpiId === 'bia_validity') {
            // Denominator: same active-slipped set as slippage rate
            // (excludes decommissioned, cancelled TDD, and apps with no TDD/decom date)
            const delayedApps = data.filter((item) => {
                if (getErmTargetMk(item, year) === null) return false;
                const slipMk = getFirstSlipMk(item, year);
                if (slipMk === null || slipMk > mk) return false;
                const decomDate = parseDate(item.decom_date);
                if (decomDate && decomDate <= monthEnd) return false;
                const cancelledMk = getTddCancelledMk(item, year);
                if (cancelledMk && cancelledMk <= mk) return false;
                if (!item.target_date_decom && !decomDate) return false;
                return true;
            });
            if (delayedApps.length === 0) continue;
            const compliant = delayedApps.filter((item) => item.bia_status !== false);
            result[mk] = Math.round((compliant.length / delayedApps.length) * 100);
        }
    }
    return result;
};

// --- Default KPI rows (used when backend has no data) ------------------------

/** Format a USD value for table cells - rounded to nearest $10, full number with commas */
const formatUSD = (value: number): string => {
    const absVal = Math.abs(value);
    let rounded: number;
    if (absVal < 10) rounded = Math.round(value);
    else if (absVal < 100) rounded = Math.round(value / 10) * 10;
    else if (absVal < 1000) rounded = Math.round(value / 100) * 100;
    else rounded = Math.round(value / 10) * 10;
    return `$${rounded.toLocaleString('en-US')}`;
};

/**
 * Computes cumulative cost savings per month for ERM-tagged apps.
/**
 * Computes forecast % for decom_completion_rate for future months.
 * For each month from the current month onwards:
 *   (apps already decommissioned this year up to the current month
 *    + not-yet-decommissioned in-scope apps whose current target_date_decom
 *      falls between the current month and that month)
 *   divided by total ERM in-scope apps for the year × 100.
 * Builds on top of actuals so the forecast never dips below what is already delivered.
 */
const computeDecomRateForecast = (
    data: DecommissioningItem[],
    year: number,
): Record<string, number> => {
    const now = new Date();
    now.setUTCHours(0, 0, 0, 0);
    const curMo = now.getUTCMonth() + 1;

    // Total ERM in-scope apps for the year (denominator)
    const fyErmTotal = data.filter((item) => getErmTargetMk(item, year) !== null).length;
    if (!fyErmTotal) return {};

    const curMoMk = `${year}${String(curMo).padStart(2, '0')}`;

    // 1) Base: count of in-scope apps ACTUALLY decommissioned this year, up to the current month.
    //    System-decommissioned (Inactive + suspended) apps are excluded — not a real decommission.
    let actualBaseCount = 0;
    for (const item of data) {
        if (getErmTargetMk(item, year) === null) continue;
        if (item.status === 'Inactive' && item.suspended === true) continue;
        const d = parseDate(item.decom_date);
        if (!d || d.getUTCFullYear() !== year) continue;
        const dMk = `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
        if (dMk > curMoMk) continue; // only up to the current month
        actualBaseCount += 1;
    }

    // 2) Remaining plan: not-yet-decommissioned in-scope apps, bucketed by their current
    //    New Target Decomm Date month, from the current month onwards only.
    const pendingByMonth: Record<string, number> = {};
    for (const item of data) {
        if (getErmTargetMk(item, year) === null) continue;
        if (item.status === 'Inactive' && item.suspended === true) continue; // system decom — not counted
        if (parseDate(item.decom_date)) continue; // already decommissioned -> counted in actualBaseCount
        const tdd = parseDate(item.target_date_decom);
        if (!tdd) continue;
        const tddMk = `${tdd.getUTCFullYear()}${String(tdd.getUTCMonth() + 1).padStart(2, '0')}`;
        if (tddMk < curMoMk) continue; // only current month onwards
        pendingByMonth[tddMk] = (pendingByMonth[tddMk] ?? 0) + 1;
    }

    // 3) Forecast%[m] = (actualBaseCount + cumulative planned from current month → m) / fyErmTotal × 100
    const result: Record<string, number> = {};
    let running = actualBaseCount;
    for (let m = curMo; m <= 12; m++) {
        const mk = `${year}${String(m).padStart(2, '0')}`;
        running += pendingByMonth[mk] ?? 0;
        result[mk] = Math.round((running / fyErmTotal) * 100);
    }
    return result;
};

/**
 * Computes cumulative cost savings FORECAST per month (current month → Dec).
 *
 * Logic (mirrors computeDecomRateForecast):
 *   - Bucket each ERM in-scope app by the month of its MAC New Target Decomm Date (target_date_decom).
 *   - Only include months from the current month onwards (no historical baseline carry-forward).
 *   - For each month starting at current month, add the cost of apps scheduled in that month,
 *     accumulating a running cumulative total.
 *   - Apps with null/missing cost contribute $0 to the dollar sum but are still counted.
 *   - Apps with no target_date_decom are excluded (no scheduled month to place them in).
 */
/**
 * Returns the annualised cost for an app.
 *
 * Annualised Cost = SUM(Monthly Cost) × (12 ÷ Count of Months with Cost Data)
 *   - Only months containing valid (non-null) cost data are included in both the
 *     sum and the month count.
 *   - Derives the average monthly cost across all available months and multiplies by 12.
 *   - Falls back to itfm_total_cost when no per-month breakdown is available.
 * Returns null when no cost data is available.
 */
const ITFM_MONTH_KEYS = [
    'itfm_jan_cost', 'itfm_feb_cost', 'itfm_mar_cost', 'itfm_apr_cost',
    'itfm_may_cost', 'itfm_jun_cost', 'itfm_jul_cost', 'itfm_aug_cost',
    'itfm_sep_cost', 'itfm_oct_cost', 'itfm_nov_cost', 'itfm_dec_cost',
];

const getItemCost = (item: DecommissioningItem): number | null => {
    let monthlySum = 0;
    let monthsWithData = 0;
    for (const key of ITFM_MONTH_KEYS) {
        const raw = item[key];
        if (raw == null) continue;
        const val = parseFloat(raw);
        if (isNaN(val)) continue;
        monthlySum += val;
        monthsWithData += 1;
    }

    if (monthsWithData > 0) {
        // Annualise: average monthly cost × 12
        return monthlySum * (12 / monthsWithData);
    }

    // Fallback to total_cost when no per-month breakdown is present.
    const itfm = item.itfm_total_cost != null ? parseFloat(item.itfm_total_cost) : NaN;
    return !isNaN(itfm) ? itfm : null;
};

/**
 * Returns the raw ITFM total_cost for an app, straight from the DB (no annualisation).
 * Used where the drill-down should display the actual total_cost value.
 */
const getItemTotalCost = (item: DecommissioningItem): number | null => {
    const v = item.itfm_total_cost != null ? parseFloat(item.itfm_total_cost) : NaN;
    return !isNaN(v) ? v : null;
};

/** Power BI "Cost Transparency - Summary" report used for app-level cost drill-through. */
const COST_TRANSPARENCY_URL = 'https://app.powerbi.com/groups/me/apps/1161082c-c885-4a6f-b5df-043860c73439/reports/786adebb-a253-4ebd-a30c-d29fd8ad4070/ReportSection94ba8c76798120510b97?ctid=05d75c05-fa1a-42e7-9cf1-eb416c396f2d&experience=power-bi';
const COST_TRANSPARENCY_TOOLTIP = "Clicking on this will take you to 'Cost Transparency - Summary'. Filter on App Name or App ID to look for details of the app cost";

/** Renders a cost value as a hyperlink to the Power BI Cost Transparency report with an explanatory tooltip. */
const renderCostLink = (value: number, color = '#1565c0'): React.ReactNode => (
    <Tooltip title={COST_TRANSPARENCY_TOOLTIP} arrow placement="top">
        <a
            href={COST_TRANSPARENCY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ fontWeight: 600, color, textDecoration: 'underline', textDecorationStyle: 'dotted', cursor: 'pointer' }}
        >
            ${Math.round(value).toLocaleString('en-US')}
        </a>
    </Tooltip>
);


const computeCostForecast = (
    data: DecommissioningItem[],
    year: number,
): Record<string, number> => {
    const now = new Date();
    now.setUTCHours(0, 0, 0, 0);
    const curMo = now.getUTCMonth() + 1;
    const curMoMk = `${year}${String(curMo).padStart(2, '0')}`;

    // 1) Base: cumulative Actual total_cost already realised this year, up to the current month.
    //    (Apps actually decommissioned this year, using raw DB total_cost.)
    let actualBase = 0;
    for (const item of data) {
        const d = parseDate(item.decom_date);
        if (!d || d.getUTCFullYear() !== year) continue;
        const dMk = `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
        if (dMk > curMoMk) continue; // only up to the current month
        const cost = getItemTotalCost(item);
        if (cost != null) actualBase += cost;
    }

    // 2) Remaining plan: not-yet-decommissioned in-scope apps, bucketed by their current
    //    New Target Decomm Date month (current month onwards only), using raw DB total_cost.
    const plannedByMonth: Record<string, number> = {};
    for (const item of data) {
        if (getErmTargetMk(item, year) === null) continue;
        if (parseDate(item.decom_date)) continue; // already decommissioned -> counted in actualBase
        const tdd = parseDate(item.target_date_decom);
        if (!tdd) continue;
        const tddMk = `${tdd.getUTCFullYear()}${String(tdd.getUTCMonth() + 1).padStart(2, '0')}`;
        if (tddMk < curMoMk) continue; // only current month onwards
        const cost = getItemTotalCost(item);
        plannedByMonth[tddMk] = (plannedByMonth[tddMk] ?? 0) + (cost != null ? cost : 0);
    }

    // 3) Forecast[m] = actualBase + cumulative planned from the current month through m.
    const result: Record<string, number> = {};
    let cumulative = actualBase;
    for (let m = curMo; m <= 12; m++) {
        const mk = `${year}${String(m).padStart(2, '0')}`;
        cumulative += plannedByMonth[mk] ?? 0;
        result[mk] = cumulative;
    }
    return result;
};

/**
 * Computes cumulative cost savings per month for ERM-tagged apps.
 * Only apps with a non-null total_cost contribute to the sum.
 * Apps without cost data are excluded from the total (shown as N/A in drill-down).
 */
const computeCostActuals = (
    data: DecommissioningItem[],
    year: number,
): Record<string, number> => {
    const monthlySum: Record<string, number> = {};      // sum of non-null costs
    const monthlyHasDecom: Set<string> = new Set();     // months with any ERM decommission

    for (const item of data) {
        const d = parseDate(item.decom_date);
        if (!d || d.getUTCFullYear() !== year) continue;
        const mk = monthKey(d);
        monthlyHasDecom.add(mk);
        const cost = getItemCost(item);
        if (cost != null) {
            monthlySum[mk] = (monthlySum[mk] ?? 0) + cost;
        }
    }
    // Return monthly values: NaN = has decommissions but all-null cost (shows as N/A, still clickable)
    const result: Record<string, number> = {};
    monthlyHasDecom.forEach((mk) => {
        result[mk] = monthlySum[mk] ?? NaN;
    });
    return result;
};

/** Compute cumulative app-count targets: cumulative count of apps whose ERM target month falls on or before each month */
const computeDecomAppTargets = (
    data: DecommissioningItem[],
    year: number,
): Record<string, number> => {
    const monthly: Record<string, number> = {};
    for (const item of data) {
        const mk = getErmTargetMk(item, year);
        if (!mk) continue;
        monthly[mk] = (monthly[mk] ?? 0) + 1;
    }
    // Build cumulative (same pattern as computeActuals)
    const cumulative: Record<string, number> = {};
    let running = 0;
    for (let m = 1; m <= 12; m++) {
        const mk = `${year}${String(m).padStart(2, '0')}`;
        running += monthly[mk] ?? 0;
        if (running > 0 || cumulative[`${year}${String(m - 1).padStart(2, '0')}`] !== undefined) {
            cumulative[mk] = running;
        }
    }
    return cumulative;
};

/** Compute monthly cost targets per month: sum of total_cost for ERM apps whose ERM TDD (erm_tdd) falls in that month */
const computeCostTargets = (
    data: DecommissioningItem[],
    year: number,
): Record<string, number> => {
    const result: Record<string, number> = {};
    for (const item of data) {
        const mk = getErmTargetMk(item, year);
        if (!mk) continue;
        // Initialise with NaN ("N/A") so the cell appears even when no cost data is present
        if (result[mk] === undefined) result[mk] = NaN;
        const cost = getItemCost(item);
        if (cost != null) {
            // First valid cost converts the NaN sentinel to a real sum
            result[mk] = (Number.isNaN(result[mk]) ? 0 : result[mk]) + cost;
        }
    }
    return result;
};

/**
 * Slippage Rate (%) - YTD cumulative % of apps whose current TDD (target_date_decom)
 * has been pushed out beyond their original ERM TDD (erm_tdd).
 * Denominator = total ERM apps committed for the year.
 */

type AppRevision = {
    modifiedDate: string;
    values: Array<{ fieldName: string; newValue: string; oldValue?: string }>;
};

/** Returns the TDD value that was set at the end of the first slip month (delegates to computeSlipInfo). */
const getSlipRevisionTdd = (item: DecommissioningItem, year: number): string | null =>
    computeSlipInfo(item, year)?.tdd ?? null;

/**
 * Tracks the TDD value month-by-month through revision history and returns the
 * first month (YYYYMM) where the TDD at END OF MONTH is > erm_tdd, together with
 * that TDD value. A slip that is corrected within the same month is NOT counted.
 *
 * Falls back to { mk: `${year}01`, tdd: target_date_decom } when no in-year
 * revision exists but target_date_decom > erm_tdd (slipped before year started).
 * Returns null if the app never slipped.
 */
const computeSlipInfo = (
    item: DecommissioningItem,
    year: number,
): { mk: string; tdd: string } | null => {
    const orig = parseDate(item.erm_tdd);
    if (!orig) return null;

    const revisions = item.revisions as AppRevision[] | null | undefined;

    if (!revisions?.length) {
        // No revision history - rely on current state
        const curr = parseDate(item.target_date_decom);
        return (curr && curr > orig)
            ? { mk: `${year}01`, tdd: item.target_date_decom ?? '' }
            : null;
    }

    // Sort all revisions oldest-first so we can replay state chronologically
    const sorted = [...revisions].sort(
        (a, b) => new Date(a.modifiedDate).getTime() - new Date(b.modifiedDate).getTime(),
    );

    // Establish TDD value at the start of the target year by replaying pre-year revisions
    let tdd: Date | null = null;
    let tddStr: string | null = null;
    for (const rev of sorted) {
        if (!rev.modifiedDate) continue;
        if (new Date(rev.modifiedDate).getUTCFullYear() >= year) break;
        for (const val of rev.values) {
            if (val.fieldName !== 'targetDecommissioningDate') continue;
            tdd = parseDate(val.newValue);
            tddStr = val.newValue;
        }
    }

    const now = new Date();
    const currentYear = now.getUTCFullYear();
    const currentMonth = now.getUTCMonth() + 1;

    // Walk month-by-month; apply all revisions in that month then check end-of-month state
    for (let mo = 1; mo <= 12; mo++) {
        if (year > currentYear) break;
        if (year === currentYear && mo > currentMonth) break;
        const mk = `${year}${String(mo).padStart(2, '0')}`;

        for (const rev of sorted) {
            if (!rev.modifiedDate) continue;
            const rd = new Date(rev.modifiedDate);
            if (rd.getUTCFullYear() !== year || rd.getUTCMonth() + 1 !== mo) continue;
            for (const val of rev.values) {
                if (val.fieldName !== 'targetDecommissioningDate') continue;
                tdd = parseDate(val.newValue);
                tddStr = val.newValue;
            }
        }

        // End-of-month check: is TDD still past erm_tdd AND the app not yet decommissioned?
        // If the owner decommissioned the app in the same month the TDD was pushed,
        // it is NOT a real slip - exclude it.
        const monthEnd = new Date(Date.UTC(year, mo, 0, 23, 59, 59, 999));
        const decomDate = parseDate(item.decom_date);
        const decommissionedByMonthEnd = !!(decomDate && decomDate <= monthEnd);
        if (tdd && tddStr && tdd > orig && !decommissionedByMonthEnd) return { mk, tdd: tddStr };
    }

    // No in-year slip found - check if pre-year slip still persists today
    // (exclude if app has already been decommissioned)
    const decomDate = parseDate(item.decom_date);
    const curr = parseDate(item.target_date_decom);
    return (curr && curr > orig && !decomDate)
        ? { mk: `${year}01`, tdd: item.target_date_decom ?? '' }
        : null;
};

const getFirstSlipMk = (item: DecommissioningItem, year: number): string | null =>
    computeSlipInfo(item, year)?.mk ?? null;

/**
 * Returns the YYYYMM when targetDecommissioningDate was first set to blank/null
 * within the given year (cancelled decom plan), or null if never cleared in-year.
 */
const getTddCancelledMk = (item: DecommissioningItem, year: number): string | null => {
    const revisions = item.revisions as AppRevision[] | null | undefined;
    if (!revisions?.length) return null;
    const sorted = [...revisions].sort(
        (a, b) => new Date(a.modifiedDate).getTime() - new Date(b.modifiedDate).getTime(),
    );
    for (const rev of sorted) {
        if (!rev.modifiedDate) continue;
        const rd = new Date(rev.modifiedDate);
        if (rd.getUTCFullYear() !== year) continue;
        for (const val of rev.values) {
            if (val.fieldName !== 'targetDecommissioningDate') continue;
            if (!val.newValue || val.newValue.trim() === '') {
                return `${year}${String(rd.getUTCMonth() + 1).padStart(2, '0')}`;
            }
        }
    }
    return null;
};

const computeSlippageActuals = (
    data: DecommissioningItem[],
    year: number,
): Record<string, number> => {
    const fyTotal = data.filter((item) => getErmTargetMk(item, year) !== null).length;
    if (!fyTotal) return {};

    // Pre-compute slipMk and cancelledMk for each in-scope app
    const slippedItems = data
        .filter((item) => getErmTargetMk(item, year) !== null)
        .map((item) => ({ item, slipMk: getFirstSlipMk(item, year), cancelledMk: getTddCancelledMk(item, year) }))
        .filter((x): x is { item: DecommissioningItem; slipMk: string; cancelledMk: string | null } => x.slipMk !== null);

    const result: Record<string, number> = {};
    const now = new Date();
    const currentYear = now.getUTCFullYear();
    const currentMonth = now.getUTCMonth() + 1;
    for (let mo = 1; mo <= 12; mo++) {
        if (year > currentYear) break;
        if (year === currentYear && mo > currentMonth) break;
        const mk = `${year}${String(mo).padStart(2, '0')}`;
        const monthEnd = new Date(Date.UTC(year, mo, 0, 23, 59, 59, 999));
        // Active slipped: slipped on/before this month, not decommissioned, and TDD not cancelled
        const activeSlipped = slippedItems.filter(({ item, slipMk, cancelledMk }) => {
            if (slipMk > mk) return false;
            const decomDate = parseDate(item.decom_date);
            if (decomDate && decomDate <= monthEnd) return false;
            if (cancelledMk && cancelledMk <= mk) return false;
            if (!item.target_date_decom && !decomDate) return false;
            return true;
        });
        result[mk] = Math.round((activeSlipped.length / fyTotal) * 100);
    }
    return result;
};

/** Count of slipped apps - cumulative per month based on revision history */
const computeSlippageCountActuals = (
    data: DecommissioningItem[],
    year: number,
): Record<string, number> => {
    // Pre-compute slipMk and cancelledMk for each in-scope app
    const slippedItems = data
        .filter((item) => getErmTargetMk(item, year) !== null)
        .map((item) => ({ item, slipMk: getFirstSlipMk(item, year), cancelledMk: getTddCancelledMk(item, year) }))
        .filter((x): x is { item: DecommissioningItem; slipMk: string; cancelledMk: string | null } => x.slipMk !== null);

    const result: Record<string, number> = {};
    const now = new Date();
    const currentYear = now.getUTCFullYear();
    const currentMonth = now.getUTCMonth() + 1;
    for (let mo = 1; mo <= 12; mo++) {
        if (year > currentYear) break;
        if (year === currentYear && mo > currentMonth) break;
        const mk = `${year}${String(mo).padStart(2, '0')}`;
        const monthEnd = new Date(Date.UTC(year, mo, 0, 23, 59, 59, 999));
        // Active slipped: slipped on/before this month, not decommissioned, and TDD not cancelled
        result[mk] = slippedItems.filter(({ item, slipMk, cancelledMk }) => {
            if (slipMk > mk) return false;
            const decomDate = parseDate(item.decom_date);
            if (decomDate && decomDate <= monthEnd) return false;
            if (cancelledMk && cancelledMk <= mk) return false;
            if (!item.target_date_decom && !decomDate) return false;
            return true;
        }).length;
    }
    return result;
};

// --- Component ---------------------------------------------------------------

/**
 * An app is "delayed" when it is Active, has a target decom date, and that date
 * has already passed - mirrors the core condition in calculateDecommissioningStatus.
 */
const isItemDelayed = (item: any): boolean => {
    if (item.status !== 'Active') return false;
    if (!item.target_date_decom) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(item.target_date_decom) < today;
};

/** Short formula descriptions shown on the ? button for each KPI */
const KPI_INFO: Record<string, string> = {
    decom_apps:             'Applications marked Inactive in MAC (proxy for decommissioned). Pending audit validation for infra/lic/vendor.',
    decom_completion_rate:  'Cumulative ERM apps decommissioned by month-end - Total ERM apps targeted for the year - 100. (% Apps Decommissioned vs Plan)',
    slippage_rate:          'ERM apps whose TDD was pushed past their original erm_tdd (YTD cumulative) - Total ERM apps for the year - 100.',
    failover_completeness:  'Delayed MVC apps with a valid failover test (last 12 months) - Total delayed MVC apps - 100.',
    restore_completeness:   'Delayed MVC apps with a valid restore test (last 12 months) - Total delayed MVC apps - 100.',
    bia_validity:           'Delayed / Cancelled apps with an expired or missing BIA - Total delayed apps - 100. (Delayed/ Cancelled Apps with Expired BIAs)',
    cost_savings:           'Sum of total cost (USD m) for ERM apps decommissioned - Cost realised from decommissioning.',
    cyber_risk_exposure:    'Number of Exemption-Revoked apps with open cyber risks that are currently delayed (slipped) - lower is better. (Cyber Risk Re-Exposure)',
    cost_leakage:           'Estimated cost leakage in USD m attributable to delayed / cancelled decommissioning events - lower is better.',
    mvc_failover_overdue:   'Count of Exemption-Revoked MVC apps where either failover or restore test is overdue (last test > 12 months) - Resilience Test Gap, target 0.',
};

/** Detailed tooltips shown on hover over the KPI label in the bowler table */
const KPI_TOOLTIP: Record<string, string> = {
    cost_savings:          'Total cost savings delivered from decommissioned applications, based on their annualized cost (2025 cost data from Application Direct Cost of Ownership - Cost Transparency)',
    cyber_risk_exposure:   'Number of apps carrying open cyber risks where the risk was originally accepted on the basis of imminent decommission, but the decommission has since been delayed or cancelled.\n\nNote: Decommission-based exemption logic is currently under discussion and may be refined.',
    cost_leakage:          'Estimated cost impact due to delays in decommissioning, based on annualized cost of impacted applications.',
    decom_completion_rate: 'Percentage of applications decommissioned compared to planned targets. Reflects overall execution progress.',
    mvc_failover_overdue:  'Number of MVC applications that are currently overdue on either failover and/or restore testing because their decommission-based test exemption (decomm date ≤ 6 months) has been invalidated by a delay or cancellation.\n\nNote: Decommission-based exemption logic is currently under discussion and may be refined.',
    bia_validity:          'Number of delayed or cancelled MVC applications where BIA is expired, reducing risk visibility.',
};

/** Returns the active KPI rows for the given year, ordered to match the bowler scorecard layout */
const buildDefaultKpiRows = (year: number): KpiRow[] => [
    // -- COMMENTED OUT - # Apps Decommissioned --------------------------------
    // {
    //     kpiId: 'decom_apps',
    //     kpiLabel: '# Apps Decommissioned',
    //     owner: 'PTL',
    //     uom: '# apps',
    //     trigger: 3,
    //     direction: 'up',
    //     target: year === 2026 ? { ...HARDCODED_TARGET_2026 } : {},
    //     forecast: year === 2026 ? { ...HARDCODED_FORECAST_2026 } : {},
    // },

    // -- 1. Outcome KPIs -------------------------------------------------------
    {
        kpiId: 'decom_completion_rate',
        kpiLabel: '% Apps Decommissioned vs Plan',
        type: 'Leading',
        owner: 'Portfolio Head',
        uom: '%',
        fyTargetDisplay: '100%',
        trigger: 3,
        direction: 'up',
        target: year === 2026 ? { ...HUNDRED_PCT_TARGET_2026 } : {},
        forecast: {},
    },
    {
        kpiId: 'cost_savings',
        kpiLabel: 'Annualised Cost realised from decommissioning',
        type: 'Outcome',
        owner: 'Portfolio Head',
        uom: 'USD',
        fyTargetDisplay: '$xx',
        trigger: 3,
        direction: 'up',
        target: {}, // set from backend when available
        forecast: {},
    },
    {
        kpiId: 'cyber_risk_exposure',
        kpiLabel: 'Delayed/ Cancelled apps with Cyber Risk',
        type: 'Outcome',
        owner: 'Cyber Lead',
        uom: '# of apps',
        fyTargetDisplay: '0',
        trigger: 3,
        direction: 'down',
        target: year === 2026 ? { ...ZERO_TARGET_2026 } : {},
        forecast: {},
    },

    // -- 2. Leading KPIs -------------------------------------------------------
    //
    {
        kpiId: 'mvc_failover_overdue',
        kpiLabel: 'Delayed/ Cancelled apps with overdue Resilience Tests',
        type: 'Leading',
        owner: 'Portfolio Head',
        uom: '# of MVCs',
        fyTargetDisplay: '0',
        trigger: 3,
        direction: 'down',
        target: year === 2026 ? { ...ZERO_TARGET_2026 } : {},
        forecast: {},
    },
    {
        kpiId: 'bia_validity',
        kpiLabel: 'Delayed/ Cancelled Apps with Expired BIAs',
        type: 'Leading',
        owner: 'Portfolio Head',
        uom: '# of MVCs',
        fyTargetDisplay: '0',
        trigger: 3,
        direction: 'down',
        target: year === 2026 ? { ...ZERO_TARGET_2026 } : {},
        forecast: {},
    },
    {
        kpiId: 'cost_leakage',
        kpiLabel: 'Cost Leakage from Delays',
        type: 'Outcome',
        owner: 'Portfolio Head',
        uom: 'USD',
        fyTargetDisplay: '<0.5m',
        trigger: 3,
        direction: 'down',
        target: year === 2026 ? { ...COST_LEAKAGE_MONTHLY_TARGET_2026 } : {},
        forecast: {},
    },
    {
        kpiId: 'delayed_cancelled_critical',
        kpiLabel: 'Delayed/Cancelled Apps with Critically Exposed Assets',
        type: 'Leading',
        owner: 'Portfolio Head',
        uom: '# of apps',
        fyTargetDisplay: '0',
        trigger: 3,
        direction: 'down',
        target: {},
        forecast: {},
    },

    // -- COMMENTED OUT - Slippage Rate ----------------------------------------
    // {
    //     kpiId: 'slippage_rate',
    //     kpiLabel: 'Slippage Rate (%) - Apps Delayed YTD',
    //     owner: 'PTL',
    //     uom: '%',
    //     trigger: 3,
    //     direction: 'down',
    //     target: year === 2026 ? { ...FIVE_PCT_TARGET_2026 } : {},
    //     forecast: {},
    // },
    // -- COMMENTED OUT - Failover Test Completeness ---------------------------
    // {
    //     kpiId: 'failover_completeness',
    //     kpiLabel: 'Failover Test Completeness (%)',
    //     owner: 'PTL',
    //     uom: '%',
    //     trigger: 3,
    //     direction: 'up',
    //     target: year === 2026 ? { ...HUNDRED_PCT_TARGET_2026 } : {},
    //     forecast: {},
    // },
    // -- COMMENTED OUT - Restore Test Completeness ----------------------------
    // {
    //     kpiId: 'restore_completeness',
    //     kpiLabel: 'Restore Test Completeness (%)',
    //     owner: 'PTL',
    //     uom: '%',
    //     trigger: 3,
    //     direction: 'up',
    //     target: year === 2026 ? { ...HUNDRED_PCT_TARGET_2026 } : {},
    //     forecast: {},
    // },
];

// --- Drill-down extra column config per KPI ---------------------------------
const getDrillExtraCol = (kpiId: string, mk: string): Array<{
    header: string;
    renderCell: (app: DecommissioningItem) => React.ReactNode;
}> | null => {
    // Shared date context derived from the month key
    const yr = parseInt(mk.slice(0, 4), 10);
    const mo = parseInt(mk.slice(4), 10);
    const yearStart = new Date(Date.UTC(yr, 0, 1));
    const monthEnd = new Date(Date.UTC(yr, mo, 0, 23, 59, 59, 999));
    const now = new Date(); now.setUTCHours(0, 0, 0, 0);
    const cutoff = now < monthEnd ? now : monthEnd;
    // A test is overdue if it was done more than 12 months before the cutoff
    const twelveMonthsAgo = new Date(cutoff);
    twelveMonthsAgo.setUTCFullYear(twelveMonthsAgo.getUTCFullYear() - 1);

    const delayReasonCol = {
        header: 'Reason for Delay',
        renderCell: (app: DecommissioningItem) => {
            const event = getDelayedInWindow(app, yearStart, monthEnd, yr);
            if (event) {
                const oldD = parseISODate(event.oldValue);
                const newD = parseISODate(event.newValue);
                const oldStr = oldD ? oldD.toLocaleDateString() : event.oldValue;
                const newStr = newD ? newD.toLocaleDateString() : event.newValue;
                return (
                    <Chip label="Date Changed" size="small" sx={{ fontSize: '0.7rem', height: 20, bgcolor: '#fff3e0', color: '#e65100' }} />
                );
            }
            return <Chip label="Overdue" size="small" sx={{ fontSize: '0.7rem', height: 20, bgcolor: '#ffcdd2', color: '#c62828' }} />;
        },
    };

    const biaStatusCol = {
        header: 'BIA Status',
        renderCell: (app: DecommissioningItem) => {
            const expired = app.bia_status === false;
            const biaColor = expired ? '#c62828' : app.bia_status == null ? '#888' : '#2e7d32';
            return (
                <Chip
                    label={expired ? 'Expired' : (app.bia_status == null ? '-' : 'Valid')}
                    size="small"
                    sx={{
                        fontSize: '0.7rem', height: 20, fontWeight: 600,
                        bgcolor: 'transparent !important',
                        color: `${biaColor} !important`,
                        border: `1px solid ${app.bia_status == null ? '#ccc' : biaColor}`,
                        '& .MuiChip-label': { color: `${biaColor} !important` },
                    }}
                />
            );
        },
    };

    // Additional columns for the BIA validity drill-down
    const originalTddCol = {
        header: 'Original Target Decomm Date',
        renderCell: (app: DecommissioningItem) => {
            const td = parseDate(app.erm_tdd);
            if (!td) return <span style={{ color: '#bbb' }}>-</span>;
            const s = `${String(td.getUTCDate()).padStart(2, '0')}-${String(td.getUTCMonth() + 1).padStart(2, '0')}-${td.getUTCFullYear()}`;
            return <span style={{ color: '#555' }}>{s}</span>;
        },
    };

    const annualCostCol = {
        header: 'Annual Cost (USD)',
        renderCell: (app: DecommissioningItem) => {
            const c = getItemCost(app);
            if (c == null) return <span style={{ color: '#888', fontStyle: 'italic' }}>N/A</span>;
            return renderCostLink(c);
        },
    };

    const potential6mCol = {
        header: 'Potential Cost Impact',
        renderCell: (app: DecommissioningItem) => {
            const c = getItemCost(app);
            if (c == null) return <span style={{ color: '#888', fontStyle: 'italic' }}>N/A</span>;
            const orig = parseDate(app.erm_tdd);
            const curr = parseDate(app.target_date_decom);
            const delayMs = (orig && curr) ? (curr.getUTCFullYear() - orig.getUTCFullYear()) * 12 + (curr.getUTCMonth() - orig.getUTCMonth()) : 0;
            if (delayMs <= 0) return <span style={{ color: '#888', fontStyle: 'italic' }}>N/A</span>;
            const impact = Math.round((c / 12) * delayMs);
            return <span style={{ fontWeight: 600, color: '#e65100' }}>${impact.toLocaleString('en-US')}</span>;
        },
    };

    const delayInMonthsBiaCol = {
        header: 'Delay in Months',
        renderCell: (app: DecommissioningItem) => {
            const orig = parseDate(app.erm_tdd);
            const curr = parseDate(app.target_date_decom);
            if (!orig || !curr) return <span style={{ color: '#bbb' }}>-</span>;
            const months = (curr.getUTCFullYear() - orig.getUTCFullYear()) * 12 + (curr.getUTCMonth() - orig.getUTCMonth());
            if (months <= 0) return <span style={{ color: '#bbb' }}>-</span>;
            return <Chip label={`+${months}mo`} size="small" sx={{ fontSize: '0.68rem', height: 20, bgcolor: months > 3 ? '#c62828 !important' : '#ef6c00 !important', color: '#fff !important', '& .MuiChip-label': { color: '#fff !important' } }} />;
        },
    };

    // Shows green if tested within last 12 months; red if null or test was > 12 months ago
    const failoverDateCol = {
        header: 'Last Failover Test Date',
        renderCell: (app: DecommissioningItem) => {
            const fd = parseDate(app.failover_tested_date_last_12_months);
            if (!fd) return <span style={{ color: '#c62828', fontStyle: 'italic' }}>Not tested</span>;
            const overdue = fd < twelveMonthsAgo;
            const label = String(app.failover_tested_date_last_12_months).split(' ')[0].split('T')[0];
            return <span style={{ color: overdue ? '#c62828' : '#2e7d32', fontWeight: overdue ? 600 : 400 }}>{label}</span>;
        },
    };

    const restoreTestDateCol = {
        header: 'Last Restore Test Date',
        renderCell: (app: DecommissioningItem) => {
            const rd = parseDate(app.backup_tested_last_12_months);
            if (!rd) return <span style={{ color: '#c62828', fontStyle: 'italic' }}>Not tested</span>;
            const overdue = rd < twelveMonthsAgo;
            const label = String(app.backup_tested_last_12_months).split(' ')[0].split('T')[0];
            return <span style={{ color: overdue ? '#c62828' : '#2e7d32', fontWeight: overdue ? 600 : 400 }}>{label}</span>;
        },
    };

    const plannedFailoverCol = {
        header: 'Planned Failover Test Date',
        renderCell: (_app: DecommissioningItem) => <span style={{ color: '#9e9e9e', fontStyle: 'italic', fontWeight: 600 }}>Coming Soon</span>,
    };

    const plannedRestoreCol = {
        header: 'Planned Restore Test Date',
        renderCell: (_app: DecommissioningItem) => <span style={{ color: '#9e9e9e', fontStyle: 'italic', fontWeight: 600 }}>Coming Soon</span>,
    };

    if (kpiId === 'delayed_apps') {
        return [delayReasonCol];
    }
    if (kpiId === 'delayed_mvc_apps') {
        // Use YTD window so the chip shows correctly even if the date change was in an earlier month
        return [delayReasonCol, biaStatusCol, failoverDateCol, restoreTestDateCol];
    }
    if (kpiId === 'bia_expired') {
        return [biaStatusCol];
    }
    if (kpiId === 'mvc_failover_overdue') {
        return [failoverDateCol, plannedFailoverCol, restoreTestDateCol, plannedRestoreCol];
    }
    if (kpiId === 'mvc_restore_overdue') {
        return [{
            header: 'Last Restore Test',
            renderCell: (app) => {
                const rd = parseDate(app.backup_tested_last_12_months);
                if (!rd) return <span style={{ color: '#c62828', fontStyle: 'italic' }}>Not tested</span>;
                const overdue = rd < twelveMonthsAgo;
                const label = String(app.backup_tested_last_12_months).split(' ')[0].split('T')[0];
                return <span style={{ color: overdue ? '#c62828' : '#2e7d32', fontWeight: overdue ? 600 : 400 }}>{label}</span>;
            },
        }];
    }

    // -- Rate / percentage KPIs ------------------------------------------------
    // decom_completion_rate uses the same dedicated rendering as decom_apps (handled in the table body)
    const isMvcCol = {
        header: 'Is MVC',
        renderCell: (app: DecommissioningItem) =>
            app.mvc_group
                ? <Chip label="MVC" size="small" sx={{ fontSize: '0.68rem', height: 20, bgcolor: '#1565c0 !important', color: '#fff !important', '& .MuiChip-label': { color: '#fff !important' } }} />
                : <span style={{ color: '#bbb' }}>-</span>,
    };

    if (kpiId === 'failover_completeness') {
        return [isMvcCol, originalTddCol, failoverDateCol];
    }
    if (kpiId === 'restore_completeness') {
        return [isMvcCol, originalTddCol, restoreTestDateCol];
    }
    if (kpiId === 'bia_validity') {
        return [isMvcCol, originalTddCol, annualCostCol, delayInMonthsBiaCol, potential6mCol, biaStatusCol];
    }
    if (kpiId === 'cost_savings') {
        const savingsCol = {
            header: 'Annualised Savings (USD)',
            renderCell: (app: DecommissioningItem) => {
                const cost = getItemCost(app);
                if (cost == null)
                    return <span style={{ color: '#888', fontStyle: 'italic' }}>N/A</span>;
                return renderCostLink(cost);
            },
        };
        const extensionReasonCol = {
            header: 'Reason for Delay',
            renderCell: (app: DecommissioningItem) => (
                <span style={{ color: '#555', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.5 }}>
                    {app.decom_extension_reason ?? <span style={{ color: '#bbb', fontStyle: 'italic' }}>-</span>}
                </span>
            ),
        };
        return [extensionReasonCol, savingsCol];
    }

    if (kpiId === 'cyber_risk_exposure') {
        const cyberRiskCol = {
            header: 'Cyber Risk Exposure',
            renderCell: (app: DecommissioningItem) => {
                const v = app.risk_exposure_all;
                if (v == null) return <span style={{ color: '#888', fontStyle: 'italic' }}>-</span>;
                return <span style={{ fontWeight: 600, color: '#1565c0' }}>{Number(v).toLocaleString('en-US')}</span>;
            },
        };
        const cyberHighCol = {
            header: 'Cyber High Risk? Y/N',
            renderCell: (app: DecommissioningItem) => {
                const v = app.risk_exposure_h;
                if (v == null) return <span style={{ color: '#888', fontStyle: 'italic' }}>-</span>;
                return <span style={{ fontWeight: 700, color: v ? '#c62828' : '#2e7d32' }}>{v ? 'Y' : 'N'}</span>;
            },
        };
        const extensionReasonCol = {
            header: 'Reason for Delay',
            renderCell: (app: DecommissioningItem) => (
                <span style={{ color: '#555', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.5 }}>
                    {app.decom_extension_reason ?? <span style={{ color: '#bbb', fontStyle: 'italic' }}>-</span>}
                </span>
            ),
        };
        return [extensionReasonCol, cyberRiskCol];
    }

    return null; // decom_apps - uses existing Decom Date column
};

const DecomBowlerMetrics: React.FC<Props> = ({ decommissioningData: decommissioningDataProp }) => {
    // Exclude system-decommissioned apps from the Decom Bowler.
    // status = 'Inactive' AND suspended = true means MAC auto/system decommissioned the app
    // (e.g. suspended after failed verification) — these are NOT real/manual decoms and must be
    // excluded from every KPI, actual, and listing here. Inactive + suspended false/null are kept.
    const decommissioningData = useMemo(
        () => decommissioningDataProp.filter(
            item => !(item.status === 'Inactive' && item.suspended === true)
        ),
        [decommissioningDataProp],
    );

    const CURRENT_YEAR = new Date().getUTCFullYear();
    const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
    const [kpiRows, setKpiRows] = useState<KpiRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);

    // -- Drill-down state ------------------------------------------------------
    const [drillDown, setDrillDown] = useState<{ kpiId: string; mk: string; monthLabel: string; rowType: 'actual' | 'target' | 'forecast'; subKey?: string } | null>(null);
    const [drillSearch, setDrillSearch] = useState('');
    const [drillPortfolio, setDrillPortfolio] = useState('');
    const [drillPlatform, setDrillPlatform] = useState('');
    const [drillAppId, setDrillAppId] = useState('');
    const [drillAppName, setDrillAppName] = useState('');
    const [drillStatus, setDrillStatus] = useState('');
    const [drillDecommStatus, setDrillDecommStatus] = useState('');
    const [drillOwner, setDrillOwner] = useState('');
    const [drillAppType, setDrillAppType] = useState('');
    const [drillSortCol, setDrillSortCol] = useState<string | null>(null);
    const [drillSortDir, setDrillSortDir] = useState<'asc' | 'desc'>('asc');
    // Per-column funnel filters: header -> selected display values
    const [colFilters, setColFilters] = useState<Record<string, string[]>>({});
    const [filterMenuCol, setFilterMenuCol] = useState<string | null>(null);
    const [filterMenuAnchor, setFilterMenuAnchor] = useState<HTMLElement | null>(null);
    const [filterMenuSearch, setFilterMenuSearch] = useState('');
    // Column show/hide for the drill-down table (empty = all visible)
    // Column show/hide for the drill-down table. 'Suspended' is hidden by default — users opt in
    // via Column Settings to view the MAC suspended flag (Yes/No) per app.
    const [hiddenDrillCols, setHiddenDrillCols] = useState<string[]>(['Suspended']);
    const [colSettingsOpen, setColSettingsOpen] = useState(false);
    const DEBUG_ALIGN = false; // set true to show debug borders for header/filter/body alignment

    // -- Live MSA failover / restore dates (fetched once on mount) ------------
    // MSA is the ONLY authoritative source - DB values are never used as fallback.
    const [msaLookup, setMsaLookup] = useState<Record<string, { failover: string | null; restore: string | null; app_type: string | null }>>({});
    const [msaLoaded, setMsaLoaded] = useState(false);

    useEffect(() => {
        authFetch(`${API_BASE_URL}/api/resiliency-msa`)
            .then(res => (res.ok ? res.json() : null))
            .then(json => {
                if (!json?.data) { setMsaLoaded(true); return; }
                const lookup: Record<string, { failover: string | null; restore: string | null; app_type: string | null }> = {};
                for (const rec of (json.data as Array<{
                    app_id?: string;
                    failover_tested_date_last_12_months?: string;
                    restore_from_backup_tested_last_12_months?: string;
                    app_type?: string;
                }>)) {
                    if (rec.app_id) {
                        lookup[rec.app_id] = {
                            failover: rec.failover_tested_date_last_12_months ?? null,
                            restore: rec.restore_from_backup_tested_last_12_months ?? null,
                            app_type: rec.app_type ?? null,
                        };
                    }
                }
                setMsaLookup(lookup);
                setMsaLoaded(true);
            })
            .catch(() => { setMsaLoaded(true); /* MSA unreachable - show null, not stale DB data */ });
    }, []);

    // Merge live MSA dates into decommissioning items.
    // Before MSA loads: use decommissioningData as-is (brief loading state).
    // After MSA loads: use only MSA values; apps not in MSA get null (no DB fallback).
    const enrichedData = useMemo((): DecommissioningItem[] => {
        if (!msaLoaded) return decommissioningData;
        return decommissioningData.map(item => {
            const msa = msaLookup[item.app_id];
            return {
                ...item,
                failover_tested_date_last_12_months: msa ? (msa.failover ?? null) : null,
                backup_tested_last_12_months: msa ? (msa.restore ?? null) : null,
                app_type: msa ? (msa.app_type ?? null) : (item as any).app_type ?? null,
            };
        });
    }, [decommissioningData, msaLookup, msaLoaded]);

    const months = useMemo(
        () =>
            Array.from({ length: 12 }, (_, i) => ({
                index: i + 1,
                mk: `${selectedYear}${String(i + 1).padStart(2, '0')}`,
                shortLabel: MONTH_NAMES[i].slice(0, 3),
                fullLabel: MONTH_NAMES[i],
            })),
        [selectedYear],
    );

    // Filter to only apps carrying an ERM decommissioning tag OR a direct erm_tdd date
    const ermData = useMemo(
        () => enrichedData.filter((item) =>
            (item.tag?.decomm_tag && String(item.tag.decomm_tag).startsWith('ERM')) ||
            !!item.erm_tdd
        ),
        [enrichedData],
    );

    // Unfiltered chain (INCLUDES system-decommissioned Inactive+suspended apps).
    // Used ONLY for TARGET rows / plan denominators so the FY plan stays complete (e.g. 217 apps).
    // Actuals & forecasts keep using the filtered ermData/enrichedData, which exclude
    // system-suspended apps so they are never credited as real decommissions.
    const enrichedDataAll = useMemo((): DecommissioningItem[] => {
        if (!msaLoaded) return decommissioningDataProp;
        return decommissioningDataProp.map(item => {
            const msa = msaLookup[item.app_id];
            return {
                ...item,
                failover_tested_date_last_12_months: msa ? (msa.failover ?? null) : null,
                backup_tested_last_12_months: msa ? (msa.restore ?? null) : null,
                app_type: msa ? (msa.app_type ?? null) : (item as any).app_type ?? null,
            };
        });
    }, [decommissioningDataProp, msaLookup, msaLoaded]);

    const ermDataAll = useMemo(
        () => enrichedDataAll.filter((item) =>
            (item.tag?.decomm_tag && String(item.tag.decomm_tag).startsWith('ERM')) ||
            !!item.erm_tdd
        ),
        [enrichedDataAll],
    );

    // Compute actuals from ERM-tagged apps that have a target month in the selected year
    // (unplanned decommissions - no ERM target - are excluded from the actual count)
    const actuals = useMemo(
        () => computeActuals(
            ermData.filter((item) => getErmTargetMk(item, selectedYear) !== null),
            selectedYear,
        ),
        [ermData, selectedYear],
    );
    const pyActuals = useMemo(
        () => computeActuals(ermData, selectedYear - 1),
        [ermData, selectedYear],
    );
    const costActuals = useMemo(
        () => computeCostActuals(ermData, selectedYear),
        [ermData, selectedYear],
    );
    const pyCostActuals = useMemo(
        () => computeCostActuals(ermData, selectedYear - 1),
        [ermData, selectedYear],
    );
    const costTargets = useMemo(
        () => computeCostTargets(enrichedDataAll, selectedYear),
        [enrichedDataAll, selectedYear],
    );
    // Cumulative cost targets (running sum up to each month)
    const cumulativeCostTargets = useMemo((): Record<string, number> => {
        const result: Record<string, number> = {};
        let running = 0;
        for (let m = 1; m <= 12; m++) {
            const mk = `${selectedYear}${String(m).padStart(2, '0')}`;
            const v = costTargets[mk];
            if (v != null && !Number.isNaN(v)) running += v;
            if (running > 0 || result[`${selectedYear}${String(m - 1).padStart(2, '0')}`] !== undefined) {
                result[mk] = running;
            }
        }
        return result;
    }, [costTargets, selectedYear]);
    // Cumulative cost actuals (running sum up to each month)
    const cumulativeCostActuals = useMemo((): Record<string, number> => {
        const result: Record<string, number> = {};
        let running = 0;
        for (let m = 1; m <= 12; m++) {
            const mk = `${selectedYear}${String(m).padStart(2, '0')}`;
            const v = costActuals[mk];
            if (v != null && !Number.isNaN(v)) running += v;
            if (running > 0 || result[`${selectedYear}${String(m - 1).padStart(2, '0')}`] !== undefined) {
                result[mk] = running;
            }
        }
        return result;
    }, [costActuals, selectedYear]);
    // Use full enrichedDataAll (unfiltered) so any app with erm_tdd set is counted as a target
    const decomAppTargets = useMemo(
        () => computeDecomAppTargets(enrichedDataAll, selectedYear),
        [enrichedDataAll, selectedYear],
    );

    // Decom Completion Rate target = cumulative target apps / total ERM apps for year * 100
    const decomRateTargets = useMemo((): Record<string, number> => {
        const fyErmTotal = enrichedDataAll.filter((item) => {
            const tag = item.tag?.decomm_tag ? String(item.tag.decomm_tag) : '';
            return tag.startsWith('ERM') && tag.includes(String(selectedYear));
        }).length;
        if (!fyErmTotal) return {};
        const result: Record<string, number> = {};
        for (let mo = 1; mo <= 12; mo++) {
            const mk = `${selectedYear}${String(mo).padStart(2, '0')}`;
            const cumCount = decomAppTargets[mk];
            if (cumCount != null) result[mk] = Math.round((cumCount / fyErmTotal) * 100);
        }
        return result;
    }, [decomAppTargets, enrichedDataAll, selectedYear]);

    // Count of ERM in-scope apps for the selected year — used in the FY Target cell for decom_completion_rate.
    // Uses the UNFILTERED set so the FY plan count is complete (system-suspended apps still count as planned).
    const fyErmAppCount = useMemo(
        () => ermDataAll.filter((item) => getErmTargetMk(item, selectedYear) !== null).length,
        [ermDataAll, selectedYear],
    );

    // -- Drill-down: get apps for the active KPI + month ---------------------
    const currentMk = useMemo(() => {
        const now = new Date();
        return `${now.getUTCFullYear()}${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
    }, []);

    /** Pre-computed snapshot KPI actuals for all months of the selected year using Trends logic */
    const snapshotActuals = useMemo((): Record<string, Record<string, number>> => {
        const result: Record<string, Record<string, number>> = {};
        const snapshotKpiIds = ['delayed_apps', 'delayed_mvc_apps', 'bia_expired', 'mvc_restore_overdue'];
        const now = new Date();
        const currentYear = now.getUTCFullYear();
        const currentMonth = now.getUTCMonth() + 1;

        for (const kpiId of snapshotKpiIds) {
            result[kpiId] = {};
            for (let mo = 1; mo <= 12; mo++) {
                if (selectedYear > currentYear) break;
                if (selectedYear === currentYear && mo > currentMonth) break;
                const mk = `${selectedYear}${String(mo).padStart(2, '0')}`;
                result[kpiId][mk] = getSnapshotAppsAsOf(ermData, selectedYear, mo, kpiId).length;
            }
        }
        return result;
    }, [ermData, selectedYear]);

    /** Percentage-KPI actuals computed from ERM data (decom_completion_rate, failover/restore completeness) */
    const rateActuals = useMemo((): Record<string, Record<string, number>> => {
        const result: Record<string, Record<string, number>> = {};
        // Decom completion rate: denominator = full plan (unfiltered); numerator excludes system-suspended (in fn).
        result['decom_completion_rate'] = computeRateActuals(ermDataAll, selectedYear, 'decom_completion_rate');
        // Failover / restore completeness: actuals exclude system-suspended -> use filtered data.
        result['failover_completeness'] = computeRateActuals(ermData, selectedYear, 'failover_completeness');
        result['restore_completeness'] = computeRateActuals(ermData, selectedYear, 'restore_completeness');
        return result;
    }, [ermData, ermDataAll, selectedYear]);

    /** Forecast % for decom_completion_rate — future months only (next month → Dec) */
    const decomRateForecast = useMemo(
        () => computeDecomRateForecast(ermDataAll, selectedYear),
        [ermDataAll, selectedYear],
    );

    /** Forecast cumulative cost savings — future months only (next month → Dec) */
    const costForecast = useMemo(
        () => computeCostForecast(ermData, selectedYear),
        [ermData, selectedYear],
    );

    /** Slippage Rate actuals - YTD cumulative % of apps whose current TDD > original erm_tdd */
    const slippageActuals = useMemo(
        () => computeSlippageActuals(enrichedData, selectedYear),
        [enrichedData, selectedYear],
    );
    /** Slippage count actuals - YTD cumulative count of slipped apps */
    const slippageCountActuals = useMemo(
        () => computeSlippageCountActuals(enrichedData, selectedYear),
        [enrichedData, selectedYear],
    );

    /** Cyber Risk Exposure actuals - sum of `risk_exposure_all` across active slipped apps (cumulative per month)
     *  Uses the same slipped-app definition as slippage count (first slip month = mk and not decommissioned by month-end)
     */
    const cyberActuals = useMemo((): Record<string, number> => {
        const result: Record<string, number> = {};
        // Pre-compute slipMk and cancelledMk for each in-scope app
        const slippedItems = enrichedData
            .filter((item) => getErmTargetMk(item, selectedYear) !== null)
            .map((item) => ({ item, slipMk: getFirstSlipMk(item, selectedYear), cancelledMk: getTddCancelledMk(item, selectedYear) }))
            .filter((x): x is { item: DecommissioningItem; slipMk: string; cancelledMk: string | null } => x.slipMk !== null);

        const now = new Date();
        const currentYear = now.getUTCFullYear();
        const currentMonth = now.getUTCMonth() + 1;
        for (let mo = 1; mo <= 12; mo++) {
            if (selectedYear > currentYear) break;
            if (selectedYear === currentYear && mo > currentMonth) break;
            const mk = `${selectedYear}${String(mo).padStart(2, '0')}`;
            const monthEnd = new Date(Date.UTC(selectedYear, mo, 0, 23, 59, 59, 999));
            const activeSlipped = slippedItems.filter(({ item, slipMk, cancelledMk }) => {
                if (slipMk > mk) return false;
                const decomDate = parseDate(item.decom_date);
                if (decomDate && decomDate <= monthEnd) return false;
                if (cancelledMk && cancelledMk <= mk) return false;
                if (!item.target_date_decom && !decomDate) return false;
                return true;
            });
            // Count apps with risk_exposure_all > 0
            result[mk] = activeSlipped.filter(({ item }) => Number(item.risk_exposure_all) > 0).length;
        }
        return result;
    }, [enrichedData, selectedYear]);

    /** Cost Leakage actuals - sum of potential cost impact (annual_cost/12 - delay_months)
     *  for ERM apps that have slipped and are still pending decommission, per month. */
    const costLeakageActuals = useMemo((): Record<string, number> => {
        const slippedItems = enrichedData
            .filter((item) => getErmTargetMk(item, selectedYear) !== null)
            .map((item) => ({ item, slipMk: getFirstSlipMk(item, selectedYear), cancelledMk: getTddCancelledMk(item, selectedYear) }))
            .filter((x): x is { item: DecommissioningItem; slipMk: string; cancelledMk: string | null } => x.slipMk !== null);
        const now = new Date();
        const currentYear = now.getUTCFullYear();
        const currentMonth = now.getUTCMonth() + 1;
        const result: Record<string, number> = {};
        for (let mo = 1; mo <= 12; mo++) {
            if (selectedYear > currentYear) break;
            if (selectedYear === currentYear && mo > currentMonth) break;
            const mk = `${selectedYear}${String(mo).padStart(2, '0')}`;
            const monthEnd = new Date(Date.UTC(selectedYear, mo, 0, 23, 59, 59, 999));
            const qualifying = slippedItems.filter(({ item, slipMk, cancelledMk }) => {
                if (slipMk > mk) return false;
                const decomDate = parseDate(item.decom_date);
                if (decomDate && decomDate <= monthEnd) return false;
                if (cancelledMk && cancelledMk <= mk) return false;
                if (!item.target_date_decom && !decomDate) return false;
                return true;
            });
            result[mk] = qualifying.reduce((sum, { item }) => {
                const cost = getItemCost(item);
                if (cost == null || isNaN(cost)) return sum;
                const orig = parseDate(item.erm_tdd);
                const curr = parseDate(item.target_date_decom);
                const delayMonths = (orig && curr)
                    ? Math.max(0, (curr.getUTCFullYear() - orig.getUTCFullYear()) * 12 + (curr.getUTCMonth() - orig.getUTCMonth()))
                    : 0;
                return sum + (cost / 12) * delayMonths;
            }, 0);
        }
        return result;
    }, [enrichedData, selectedYear]);

    /** BIA Count actuals - count of (delayed OR cancelled) ERM apps with expired BIA per month */
    const biaCountActuals = useMemo((): Record<string, number> => {
        const now = new Date();
        const currentYear = now.getUTCFullYear();
        const currentMonth = now.getUTCMonth() + 1;
        const result: Record<string, number> = {};
        const inScope = enrichedData.filter((item) => !!item.mvc_group && getErmTargetMk(item, selectedYear) !== null);
        for (let mo = 1; mo <= 12; mo++) {
            if (selectedYear > currentYear) break;
            if (selectedYear === currentYear && mo > currentMonth) break;
            const mk = `${selectedYear}${String(mo).padStart(2, '0')}`;
            const monthEnd = new Date(Date.UTC(selectedYear, mo, 0, 23, 59, 59, 999));
            result[mk] = inScope.filter((item) => {
                if (item.bia_status !== false) return false;
                const decomDate = parseDate(item.decom_date);
                if (decomDate && decomDate <= monthEnd) return false;
                const slipMk = getFirstSlipMk(item, selectedYear);
                const cancelledMk = getTddCancelledMk(item, selectedYear);
                // Delayed: slipped on/before mk, not cancelled by mk, has a TDD
                const isDelayed = slipMk !== null && slipMk <= mk
                    && !(cancelledMk && cancelledMk <= mk)
                    && !(!item.target_date_decom && !parseDate(item.decom_date));
                // Cancelled: TDD was cleared on/before mk
                const isCancelled = cancelledMk !== null && cancelledMk <= mk;
                return isDelayed || isCancelled;
            }).length;
        }
        return result;
    }, [enrichedData, selectedYear]);

    /** MVC Resilience Test Gap - ERM MVCs whose exemption is revoked (delayed or cancelled)
     *  AND have either failover or restore test overdue (> 12 months), scoped to original ERM
     *  target year so apps that slipped into a later year are still counted. */
    const mvcResilienceActuals = useMemo((): Record<string, number> => {
        const now = new Date();
        const currentYear = now.getUTCFullYear();
        const currentMonth = now.getUTCMonth() + 1;
        const result: Record<string, number> = {};
        const inScope = enrichedData.filter(
            (item) => !!item.mvc_group && getErmTargetMk(item, selectedYear) !== null,
        );
        for (let mo = 1; mo <= 12; mo++) {
            if (selectedYear > currentYear) break;
            if (selectedYear === currentYear && mo > currentMonth) break;
            const mk = `${selectedYear}${String(mo).padStart(2, '0')}`;
            const monthEnd = new Date(Date.UTC(selectedYear, mo, 0, 23, 59, 59, 999));
            const cutoff = now < monthEnd ? now : monthEnd;
            const twelveMonthsAgo = new Date(cutoff);
            twelveMonthsAgo.setUTCFullYear(twelveMonthsAgo.getUTCFullYear() - 1);
            result[mk] = inScope.filter((item) => {
                const decomDate = parseDate(item.decom_date);
                if (decomDate && decomDate <= monthEnd) return false;
                const slipMk = getFirstSlipMk(item, selectedYear);
                const cancelledMk = getTddCancelledMk(item, selectedYear);
                const isDelayed = slipMk !== null && slipMk <= mk
                    && !(cancelledMk && cancelledMk <= mk)
                    && !(!item.target_date_decom && !decomDate);
                const isCancelled = cancelledMk !== null && cancelledMk <= mk;
                if (!isDelayed && !isCancelled) return false;
                const fd = parseDate(item.failover_tested_date_last_12_months);
                const rd = parseDate(item.backup_tested_last_12_months);
                return (!fd || fd < twelveMonthsAgo) || (!rd || rd < twelveMonthsAgo);
            }).length;
        }
        return result;
    }, [enrichedData, selectedYear]);

    const RATE_KPI_IDS = ['decom_completion_rate', 'failover_completeness', 'restore_completeness'];

    const drillApps = useMemo(() => {
        if (!drillDown) return [];
        const activeKpi = kpiRows.find((k) => k.kpiId === drillDown.kpiId);
        if (!activeKpi) return [];

        const yr = parseInt(drillDown.mk.slice(0, 4), 10);
        const mo = parseInt(drillDown.mk.slice(4), 10);

        // Slippage drill-down: apps that slipped on/before the clicked month
        // AND were not decommissioned/cancelled by that month-end (mirrors the cell count).
        if (activeKpi.kpiId === 'slippage_rate') {
            const drillMonthEnd = new Date(Date.UTC(yr, mo, 0, 23, 59, 59, 999));
            return enrichedData.filter((item) => {
                if (getErmTargetMk(item, yr) === null) return false;
                const slipMk = getFirstSlipMk(item, yr);
                if (slipMk === null || slipMk > drillDown.mk) return false;
                const decomDate = parseDate(item.decom_date);
                if (decomDate && decomDate <= drillMonthEnd) return false;
                const cancelledMk = getTddCancelledMk(item, yr);
                if (cancelledMk && cancelledMk <= drillDown.mk) return false;
                if (!item.target_date_decom && !decomDate) return false;
                return true;
            });
        }

        // Cyber Risk Exposure: slipped apps with risk_exposure_all > 0 cumulative to this month
        if (activeKpi.kpiId === 'cyber_risk_exposure') {
            const drillMonthEnd = new Date(Date.UTC(yr, mo, 0, 23, 59, 59, 999));
            return enrichedData.filter((item) => {
                if (getErmTargetMk(item, yr) === null) return false;
                if (!(Number(item.risk_exposure_all) > 0)) return false;
                const slipMk = getFirstSlipMk(item, yr);
                if (slipMk === null || slipMk > drillDown.mk) return false;
                const decomDate = parseDate(item.decom_date);
                if (decomDate && decomDate <= drillMonthEnd) return false;
                const cancelledMk = getTddCancelledMk(item, yr);
                if (cancelledMk && cancelledMk <= drillDown.mk) return false;
                if (!item.target_date_decom && !decomDate) return false;
                return true;
            });
        }

        // Cost Leakage: show apps delayed (slipped) and not yet decommissioned - mirrors costLeakageActuals count
        if (activeKpi.kpiId === 'cost_leakage') {
            const drillMonthEnd = new Date(Date.UTC(yr, mo, 0, 23, 59, 59, 999));
            return enrichedData.filter((item) => {
                if (getErmTargetMk(item, yr) === null) return false;
                const slipMk = getFirstSlipMk(item, yr);
                if (slipMk === null || slipMk > drillDown.mk) return false;
                const decomDate = parseDate(item.decom_date);
                if (decomDate && decomDate <= drillMonthEnd) return false;
                const cancelledMk = getTddCancelledMk(item, yr);
                if (cancelledMk && cancelledMk <= drillDown.mk) return false;
                if (!item.target_date_decom && !decomDate) return false;
                return true;
            });
        }

        // Target drill-down for % Apps Decommissioned vs Plan - show all apps planned up to and including that month (cumulative)
        // Uses the UNFILTERED plan so the target list matches the FY plan count (system-suspended apps still count as planned).
        if (activeKpi.kpiId === 'decom_completion_rate' && drillDown.rowType === 'target') {
            return enrichedDataAll.filter((item) => {
                const ermTgtMk = getErmTargetMk(item, yr);
                return ermTgtMk !== null && ermTgtMk <= drillDown.mk;
            });
        }

        // Forecast drill-down for % Apps Decommissioned vs Plan.
        // Cumulative forecast = apps already decommissioned this year (up to the current month)
        // PLUS not-yet-decommissioned in-scope apps whose New Target Decomm Date month falls
        // between the current month and the clicked month. Mirrors computeDecomRateForecast.
        if (activeKpi.kpiId === 'decom_completion_rate' && drillDown.rowType === 'forecast') {
            const now2 = new Date(); now2.setUTCHours(0, 0, 0, 0);
            const curMoMk = `${now2.getUTCFullYear()}${String(now2.getUTCMonth() + 1).padStart(2, '0')}`;
            return ermData.filter((item) => {
                if (getErmTargetMk(item, yr) === null) return false;
                const d = parseDate(item.decom_date);
                if (d) {
                    // Already decommissioned this year, up to the current month -> part of the cumulative base
                    if (d.getUTCFullYear() !== yr) return false;
                    const dMk = `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
                    return dMk <= curMoMk;
                }
                // Pending: not yet decommissioned, target month from the current month up to the clicked month
                const tdd = parseDate(item.target_date_decom);
                if (!tdd) return false;
                const tddMk = `${tdd.getUTCFullYear()}${String(tdd.getUTCMonth() + 1).padStart(2, '0')}`;
                return tddMk >= curMoMk && tddMk <= drillDown.mk;
            });
        }

        // Forecast drill-down for Cost Savings — mirrors computeCostForecast.
        // Cumulative forecast = cost of apps already decommissioned this year (up to the current month)
        // PLUS not-yet-decommissioned in-scope apps whose New Target Decomm Date month falls between
        // the current month and the clicked month.
        if (activeKpi.kpiId === 'cost_savings' && drillDown.rowType === 'forecast') {
            const now3 = new Date(); now3.setUTCHours(0, 0, 0, 0);
            const curMoMk3 = `${now3.getUTCFullYear()}${String(now3.getUTCMonth() + 1).padStart(2, '0')}`;
            return ermData.filter((item) => {
                if (getErmTargetMk(item, yr) === null) return false;
                const d = parseDate(item.decom_date);
                if (d) {
                    // Already decommissioned this year, up to the current month -> part of the cumulative base
                    if (d.getUTCFullYear() !== yr) return false;
                    const dMk = `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
                    return dMk <= curMoMk3;
                }
                // Pending: not yet decommissioned, target month from the current month up to the clicked month
                const tdd = parseDate(item.target_date_decom);
                if (!tdd) return false;
                const tddMk = `${tdd.getUTCFullYear()}${String(tdd.getUTCMonth() + 1).padStart(2, '0')}`;
                return tddMk >= curMoMk3 && tddMk <= drillDown.mk;
            });
        }

        if (activeKpi.kpiId === 'decom_apps' || activeKpi.kpiId === 'cost_savings') {
            if (drillDown.rowType === 'target') {
                // Use full unfiltered enrichedDataAll - mirrors computeDecomAppTargets so the target list
                // matches the FY plan (apps without ERM tag but with erm_tdd, and system-suspended planned apps, are included)
                const sourceData = enrichedDataAll;
                return sourceData.filter((item) => {
                    const ermTgtMk = getErmTargetMk(item, yr);
                    return ermTgtMk !== null && ermTgtMk <= drillDown.mk;
                });
            }
            // Apps decommissioned cumulative up to and including that month with an ERM target in this year
            const monthEnd = new Date(Date.UTC(yr, mo, 0, 23, 59, 59, 999));
            const decommedApps = ermData.filter((item) => {
                if (getErmTargetMk(item, yr) === null) return false; // must be in target for this year
                const d = parseDate(item.decom_date);
                return !!(d && d <= monthEnd);
            });
            if (activeKpi.kpiId === 'decom_apps') {
                return decommedApps;
            }
            return decommedApps;
        }

        // Rate KPIs
        if (RATE_KPI_IDS.includes(activeKpi.kpiId)) {
            const monthEnd = new Date(Date.UTC(yr, mo, 0, 23, 59, 59, 999));

            if (activeKpi.kpiId === 'decom_completion_rate') {
                return ermData.filter((item) => {
                    if ((item.status || '').toLowerCase() === 'cancelled') return false;
                    const histTarget = getTargetAtMonthEnd(item, monthEnd);
                    // Fall back to ERM tag when target_date_decom has never been set
                    const hasYearTarget = histTarget
                        ? histTarget.getUTCFullYear() === yr
                        : getErmTargetMk(item, yr) !== null;
                    if (!hasYearTarget) return false;
                    const d = parseDate(item.decom_date);
                    return !!(d && d <= monthEnd);
                });
            }

            // Failover / Restore: show delayed MVC apps cumulative to this month
            // (matches the denominator used in computeRateActuals)
            if (activeKpi.kpiId === 'failover_completeness' || activeKpi.kpiId === 'restore_completeness') {
                return enrichedData.filter((item) => {
                    if (!item.mvc_group) return false;
                    if (getErmTargetMk(item, yr) === null) return false;
                    const slipMk = getFirstSlipMk(item, yr);
                    return slipMk !== null && slipMk <= drillDown.mk;
                });
            }

            return ermData.filter((item) => {
                const sl = (item.status || '').toLowerCase();
                if (sl === 'cancelled') return false;
                const histTarget = getTargetAtMonthEnd(item, monthEnd);
                if (!histTarget || histTarget.getUTCFullYear() !== yr) return false;
                if (sl === 'inactive') return false;
                const d = parseDate(item.decom_date);
                if (d && d <= monthEnd) return false;
                return true;
            });
        }

        // BIA Validity: delayed (slipped, not cancelled) OR cancelled (TDD cleared) - only MVC apps with expired BIA
        if (activeKpi.kpiId === 'bia_validity') {
            const monthEnd = new Date(Date.UTC(yr, mo, 0, 23, 59, 59, 999));
            return enrichedData.filter((item) => {
                if (!item.mvc_group) return false;
                if (getErmTargetMk(item, yr) === null) return false;
                if (item.bia_status !== false) return false;
                const decomDate = parseDate(item.decom_date);
                if (decomDate && decomDate <= monthEnd) return false;
                const slipMk = getFirstSlipMk(item, yr);
                const cancelledMk = getTddCancelledMk(item, yr);
                const isDelayed = slipMk !== null && slipMk <= drillDown.mk
                    && !(cancelledMk && cancelledMk <= drillDown.mk)
                    && !(!item.target_date_decom && !decomDate);
                const isCancelled = cancelledMk !== null && cancelledMk <= drillDown.mk;
                return isDelayed || isCancelled;
            });
        }

        // MVC Resilience Test Gap: ERM MVCs with exemption revoked (delayed/cancelled) AND overdue on either test
        if (activeKpi.kpiId === 'mvc_failover_overdue') {
            const monthEnd = new Date(Date.UTC(yr, mo, 0, 23, 59, 59, 999));
            const now = new Date(); now.setUTCHours(0, 0, 0, 0);
            const cutoff = now < monthEnd ? now : monthEnd;
            const twelveMonthsAgo = new Date(cutoff);
            twelveMonthsAgo.setUTCFullYear(twelveMonthsAgo.getUTCFullYear() - 1);
            return enrichedData.filter((item) => {
                if (!item.mvc_group) return false;
                if (getErmTargetMk(item, yr) === null) return false;
                const decomDate = parseDate(item.decom_date);
                if (decomDate && decomDate <= monthEnd) return false;
                const slipMk = getFirstSlipMk(item, yr);
                const cancelledMk = getTddCancelledMk(item, yr);
                const isDelayed = slipMk !== null && slipMk <= drillDown.mk
                    && !(cancelledMk && cancelledMk <= drillDown.mk)
                    && !(!item.target_date_decom && !decomDate);
                const isCancelled = cancelledMk !== null && cancelledMk <= drillDown.mk;
                if (!isDelayed && !isCancelled) return false;
                const fd = parseDate(item.failover_tested_date_last_12_months);
                const rd = parseDate(item.backup_tested_last_12_months);
                return (!fd || fd < twelveMonthsAgo) || (!rd || rd < twelveMonthsAgo);
            });
        }

        // Snapshot KPIs: use the same Trends-style revision-aware filter for every month
        return getSnapshotAppsAsOf(ermData, yr, mo, activeKpi.kpiId);
    }, [drillDown, kpiRows, ermData, enrichedData, enrichedDataAll]);

    const drillPortfolioOptions = useMemo(
        () => Array.from(new Set(drillApps.map((a) => a.portfolio).filter(Boolean))).sort(),
        [drillApps],
    );
    const drillPlatformOptions = useMemo(
        () => Array.from(new Set(drillApps.map((a) => a.platform).filter(Boolean))).sort(),
        [drillApps],
    );

    const drillOwnerOptions = useMemo(
        () => Array.from(new Set(drillApps.map((a) => a.owner_names).filter(Boolean))).sort(),
        [drillApps],
    );
    const drillAppTypeOptions = useMemo(
        () => Array.from(new Set(drillApps.map((a) => (a as any).app_type).filter(Boolean))).sort() as string[],
        [drillApps],
    );

    // Show counts for each option based on current drillApps and other active filters (excluding the field itself)
    const drillYr = drillDown ? parseInt(drillDown.mk.slice(0, 4), 10) : 0;
    const drillMo = drillDown ? parseInt(drillDown.mk.slice(4), 10) : 0;
    const drillPortfolioCounts = useMemo(() => {
        const m = new Map<string, number>();
        for (const a of drillApps) {
            if (drillPlatform && a.platform !== drillPlatform) continue;
            if (drillAppId && !a.app_id?.toLowerCase().includes(drillAppId.toLowerCase())) continue;
            if (drillAppName && !a.app_name?.toLowerCase().includes(drillAppName.toLowerCase())) continue;
            if (drillStatus && a.status !== drillStatus) continue;
            if (drillAppType && (a as any).app_type !== drillAppType) continue;
            if (drillDecommStatus && getTargetDecommStatus(a) !== drillDecommStatus) continue;
            const p = a.portfolio ?? '-';
            m.set(p, (m.get(p) || 0) + 1);
        }
        return m;
    }, [drillApps, drillPlatform, drillAppId, drillAppName, drillStatus, drillAppType, drillDecommStatus, drillYr, drillMo, drillDown]);

    const drillPlatformCounts = useMemo(() => {
        const m = new Map<string, number>();
        for (const a of drillApps) {
            if (drillPortfolio && a.portfolio !== drillPortfolio) continue;
            if (drillAppId && !a.app_id?.toLowerCase().includes(drillAppId.toLowerCase())) continue;
            if (drillAppName && !a.app_name?.toLowerCase().includes(drillAppName.toLowerCase())) continue;
            if (drillStatus && a.status !== drillStatus) continue;
            if (drillAppType && (a as any).app_type !== drillAppType) continue;
            if (drillDecommStatus && getTargetDecommStatus(a) !== drillDecommStatus) continue;
            const p = a.platform ?? '-';
            m.set(p, (m.get(p) || 0) + 1);
        }
        return m;
    }, [drillApps, drillPortfolio, drillAppId, drillAppName, drillStatus, drillAppType, drillDecommStatus, drillYr, drillMo, drillDown]);

    const drillOwnerCounts = useMemo(() => {
        const m = new Map<string, number>();
        for (const a of drillApps) {
            if (drillPortfolio && a.portfolio !== drillPortfolio) continue;
            if (drillPlatform && a.platform !== drillPlatform) continue;
            if (drillAppId && !a.app_id?.toLowerCase().includes(drillAppId.toLowerCase())) continue;
            if (drillAppName && !a.app_name?.toLowerCase().includes(drillAppName.toLowerCase())) continue;
            if (drillStatus && a.status !== drillStatus) continue;
            if (drillAppType && (a as any).app_type !== drillAppType) continue;
            if (drillDecommStatus && getTargetDecommStatus(a) !== drillDecommStatus) continue;
            const p = a.owner_names ?? '-';
            m.set(p, (m.get(p) || 0) + 1);
        }
        return m;
    }, [drillApps, drillPortfolio, drillPlatform, drillAppId, drillAppName, drillStatus, drillAppType, drillDecommStatus, drillYr, drillMo, drillDown]);

    const drillAppTypeCounts = useMemo(() => {
        const m = new Map<string, number>();
        for (const a of drillApps) {
            if (drillPortfolio && a.portfolio !== drillPortfolio) continue;
            if (drillPlatform && a.platform !== drillPlatform) continue;
            if (drillAppId && !a.app_id?.toLowerCase().includes(drillAppId.toLowerCase())) continue;
            if (drillAppName && !a.app_name?.toLowerCase().includes(drillAppName.toLowerCase())) continue;
            if (drillStatus && a.status !== drillStatus) continue;
            if (drillDecommStatus && getTargetDecommStatus(a) !== drillDecommStatus) continue;
            const p = (a as any).app_type ?? '-';
            m.set(p, (m.get(p) || 0) + 1);
        }
        return m;
    }, [drillApps, drillPortfolio, drillPlatform, drillAppId, drillAppName, drillStatus, drillDecommStatus, drillYr, drillMo, drillDown]);

    // Maps a column header to its display string for a given app (used by funnel filters)
    const getColumnValue = useCallback((app: DecommissioningItem, header: string): string => {
        const fmtD = (v: any): string => { const d = parseDate(v); return d ? `${String(d.getUTCDate()).padStart(2, '0')}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${d.getUTCFullYear()}` : '-'; };
        const delayMonths = (): number | null => { const o = parseDate(app.erm_tdd); const c = parseDate(app.target_date_decom); if (!o || !c) return null; return (c.getUTCFullYear() - o.getUTCFullYear()) * 12 + (c.getUTCMonth() - o.getUTCMonth()); };
        switch (header) {
            case 'App ID': return app.app_id ?? '-';
            case 'Application': return app.app_name ?? '-';
            case 'Portfolio': return app.portfolio ?? '-';
            case 'Platform': return app.platform ?? '-';
            case 'Owner': return app.owner_names ?? '-';
            case 'App Type': return (app as any).app_type ?? '-';
            case 'Is MVC': return app.mvc_group ? 'MVC' : '-';
            case 'MAC Status': case 'Status': return app.status ?? '-';
            case 'Suspended': return app.suspended === true ? 'Yes' : app.suspended === false ? 'No' : '-';
            case 'Decomm Status': return getTargetDecommStatus(app);
            case 'Original Target Decomm Date': return fmtD(app.erm_tdd);
            case 'New Target Decomm Date': case 'TDD as on Month': return fmtD(app.target_date_decom);
            case 'Decommissioned Date': return fmtD(app.decom_date);
            case 'Reason for Delay': return app.decom_extension_reason ?? '-';
            case 'BIA Status': return app.bia_status === false ? 'Expired' : app.bia_status == null ? '-' : 'Valid';
            case 'Cyber Risk Exposure': return (app as any).risk_exposure_all != null ? String((app as any).risk_exposure_all) : '-';
            case 'Cyber High Risk? Y/N': return (app as any).risk_exposure_h == null ? '-' : ((app as any).risk_exposure_h ? 'Y' : 'N');
            case 'Delay in Months': { const m = delayMonths(); return m != null && m > 0 ? `+${m}mo` : '-'; }
            case 'Annual Cost (USD)': case 'Annualized Cost (USD)': {
                const c = getItemCost(app); return c != null ? `$${Math.round(c).toLocaleString('en-US')}` : 'N/A';
            }
            case 'Annualised Savings (USD)': {
                const c = getItemTotalCost(app); return c != null ? `$${Math.round(c).toLocaleString('en-US')}` : 'N/A';
            }
            case 'Potential Cost Impact': { const m = delayMonths(); const c = getItemCost(app); return (c != null && m != null && m > 0) ? `$${Math.round((c / 12) * m).toLocaleString('en-US')}` : 'N/A'; }
            case 'Last Failover Test Date': return app.failover_tested_date_last_12_months ? String(app.failover_tested_date_last_12_months).split(' ')[0].split('T')[0] : 'Not tested';
            case 'Last Restore Test Date': case 'Last Restore Test': return app.backup_tested_last_12_months ? String(app.backup_tested_last_12_months).split(' ')[0].split('T')[0] : 'Not tested';
            default: return '-';
        }
    }, []);

    // Value used for funnel filtering. "New Target Decomm Date" filters by red/non-red slip status
    // instead of individual dates, so users can isolate delayed (red) vs on-track apps.
    const getColFilterValue = useCallback((app: DecommissioningItem, header: string): string => {
        if (header === 'New Target Decomm Date') {
            if (!app.target_date_decom) return '-';
            return isAppDateSlipped(app) ? 'Delayed' : 'On track';
        }
        return getColumnValue(app, header);
    }, [getColumnValue]);

    const filteredDrillApps = useMemo(() => {
        const q = drillSearch.toLowerCase();
        const drillYr = drillDown ? parseInt(drillDown.mk.slice(0, 4), 10) : 0;
        const drillMo = drillDown ? parseInt(drillDown.mk.slice(4), 10) : 0;
        return drillApps.filter((a) => {
            if (drillPortfolio && a.portfolio !== drillPortfolio) return false;
            if (drillPlatform && a.platform !== drillPlatform) return false;
            if (drillOwner && a.owner_names !== drillOwner) return false;
            if (drillAppType && (a as any).app_type !== drillAppType) return false;
            if (drillAppId && !a.app_id?.toLowerCase().includes(drillAppId.toLowerCase())) return false;
            if (drillAppName && !a.app_name?.toLowerCase().includes(drillAppName.toLowerCase())) return false;
            if (drillStatus && a.status !== drillStatus) return false;
            if (drillDecommStatus && getTargetDecommStatus(a) !== drillDecommStatus) return false;
            // Per-column funnel filters
            for (const col in colFilters) {
                const vals = colFilters[col];
                if (vals && vals.length && !vals.includes(getColFilterValue(a, col))) return false;
            }
            if (q) {
                const row = `${a.app_id} ${a.app_name} ${a.portfolio} ${a.platform} ${a.decom_date ?? ''} ${a.owner_names ?? ''}`.toLowerCase();
                if (!row.includes(q)) return false;
            }
            return true;
        });
    }, [drillApps, drillSearch, drillPortfolio, drillPlatform, drillOwner, drillAppType, drillAppId, drillAppName, drillStatus, drillDecommStatus, drillDown, colFilters, getColFilterValue]);

    const sortedDrillApps = useMemo(() => {
        if (!drillSortCol) return filteredDrillApps;
        return [...filteredDrillApps].sort((a, b) => {
            const getSortVal = (app: DecommissioningItem): string | number => {
                switch (drillSortCol) {
                    case 'App ID': return app.app_id ?? '';
                    case 'Application': return app.app_name ?? '';
                    case 'Portfolio': return app.portfolio ?? '';
                    case 'Platform': return app.platform ?? '';
                    case 'Owner': return app.owner_names ?? '';
                    case 'MAC Status': case 'Status': return app.status ?? '';
                    case 'Suspended': return app.suspended === true ? 'Yes' : app.suspended === false ? 'No' : '';
                    case 'App Type': return (app as any).app_type ?? '';
                    case 'Is MVC': return app.mvc_group ? 'MVC' : '';
                    case 'Original Target Decomm Date': return parseDate(app.erm_tdd)?.getTime() ?? 0;
                    case 'New Target Decomm Date': return parseDate(app.target_date_decom)?.getTime() ?? 0;
                    case 'Decommissioned Date': return parseDate(app.decom_date)?.getTime() ?? 0;
                    case 'TDD as on Month': return parseDate(app.target_date_decom)?.getTime() ?? 0;
                    case 'Delay in Months': {
                        const orig = parseDate(app.erm_tdd); const curr = parseDate(app.target_date_decom);
                        if (!orig || !curr) return -1;
                        return (curr.getUTCFullYear() - orig.getUTCFullYear()) * 12 + (curr.getUTCMonth() - orig.getUTCMonth());
                    }
                    case 'Potential Cost Impact': {
                        const orig = parseDate(app.erm_tdd); const curr = parseDate(app.target_date_decom);
                        const c = getItemCost(app);
                        if (c == null || !orig || !curr) return -1;
                        const dm = (curr.getUTCFullYear() - orig.getUTCFullYear()) * 12 + (curr.getUTCMonth() - orig.getUTCMonth());
                        return dm > 0 ? Math.round((c / 12) * dm) : -1;
                    }
                    case 'Annual Cost (USD)': case 'Annualized Cost (USD)': { const c = getItemCost(app); return c != null ? c : -1; }
                    case 'Annualised Savings (USD)': { const c = getItemTotalCost(app); return c != null ? c : -1; }
                    case 'Cyber Risk Exposure': return (app as any).risk_exposure_all ?? -1;
                    case 'Decomm Status': return getTargetDecommStatus(app);
                    default: return '';
                }
            };
            const va = getSortVal(a); const vb = getSortVal(b);
            const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
            return drillSortDir === 'asc' ? cmp : -cmp;
        });
    }, [filteredDrillApps, drillSortCol, drillSortDir]);

    // Full ordered list of column headers for the current drill-down (source of truth for header row + column settings)
    const drillHeaders = useMemo<string[]>(() => {
        if (!drillDown) return [];
        const extraColsRaw = getDrillExtraCol(drillDown.kpiId, drillDown.mk);
        const extraCols = (drillDown.kpiId === 'failover_completeness' || drillDown.kpiId === 'restore_completeness')
            ? (extraColsRaw ? extraColsRaw.filter(c => c.header !== 'Original Target Decomm Date') : null)
            : extraColsRaw;
        const isCostSavings = drillDown.kpiId === 'cost_savings';
        const isSlippageDrill = drillDown.kpiId === 'slippage_rate';
        const isCategoryDrill = (drillDown.kpiId === 'decom_apps' || drillDown.kpiId === 'cost_savings' || drillDown.kpiId === 'decom_completion_rate') && drillDown.rowType === 'actual';
        const isCostSavingsForecast = drillDown.kpiId === 'cost_savings' && drillDown.rowType === 'forecast';
        const isDecomAppsTarget = drillDown.kpiId === 'decom_apps' && drillDown.rowType === 'target';
        const isDecomAppsActual = (drillDown.kpiId === 'decom_apps' || drillDown.kpiId === 'decom_completion_rate') && (drillDown.rowType === 'actual' || drillDown.rowType === 'forecast');
        const isDecomRateTarget = drillDown.kpiId === 'decom_completion_rate' && drillDown.rowType === 'target';
        const isDecomRateForecast = drillDown.kpiId === 'decom_completion_rate' && drillDown.rowType === 'forecast';
        const base: string[] = (() => {
        if (isSlippageDrill) {
            return ['Portfolio', 'Platform', 'App ID', 'Application', 'Is MVC', 'Original Target Decomm Date', 'TDD as on Month', 'New Target Decomm Date', 'Delay in Months', 'Reason for Delay', 'MAC Status'];
        } else if (drillDown.kpiId === 'bia_validity') {
            return ['Portfolio', 'Platform', 'App ID', 'Application', 'Original Target Decomm Date', 'New Target Decomm Date', 'BIA Status', 'MAC Status'];
        } else if (drillDown.kpiId === 'cost_leakage') {
            return ['Portfolio', 'Platform', 'App ID', 'Application', 'Original Target Decomm Date', 'New Target Decomm Date', 'Delay in Months', 'Potential Cost Impact', 'MAC Status'];
        } else if (drillDown.kpiId === 'decom_apps' || drillDown.kpiId === 'decom_completion_rate') {
            return isDecomRateTarget
                ? ['Portfolio', 'Platform', 'App ID', 'Application', 'Owner', 'Original Target Decomm Date', 'New Target Decomm Date', 'Decommissioned Date', 'Reason for Delay', 'MAC Status', 'Decomm Status']
                : isDecomRateForecast
                ? ['Portfolio', 'Platform', 'App ID', 'Application', 'Owner', 'Original Target Decomm Date', 'New Target Decomm Date', 'Decommissioned Date', 'MAC Status', 'Decomm Status']
                : ['Portfolio', 'Platform', 'App ID', 'Application', 'Owner', 'Original Target Decomm Date', 'New Target Decomm Date', 'Decommissioned Date', 'MAC Status', ...((isCategoryDrill || isDecomAppsTarget) ? ['Decomm Status'] : [])];
        } else if (drillDown.kpiId === 'failover_completeness' || drillDown.kpiId === 'restore_completeness') {
            return ['Portfolio', 'Platform', 'App ID', 'Application', 'Original Target Decomm Date', 'New Target Decomm Date', ...(extraCols ? extraCols.map(c => c.header) : []), 'MAC Status'];
        } else if (drillDown.kpiId === 'mvc_failover_overdue') {
            return ['Portfolio', 'Platform', 'App ID', 'Application', 'App Type', 'Original Target Decomm Date', 'New Target Decomm Date', ...(extraCols ? extraCols.map(c => c.header) : []), 'MAC Status'];
        } else if (extraCols) {
            return ['Portfolio', 'Platform', 'App ID', 'Application', 'Original Target Decomm Date', 'New Target Decomm Date', ...((isCostSavings || isCostSavingsForecast) ? ['Decommissioned Date'] : []), ...extraCols.map(c => c.header), 'MAC Status', ...((isCategoryDrill || isCostSavingsForecast) ? ['Decomm Status'] : [])];
        }
        return ['Portfolio', 'Platform', 'App ID', 'Application', 'Original Target Decomm Date', 'New Target Decomm Date',
            ...(isDecomAppsTarget || isDecomAppsActual ? ['Decommissioned Date'] : []),
            'Owner', 'MAC Status',
            ...((isCategoryDrill || isDecomAppsTarget) ? ['Decomm Status'] : [])];
        })();
        return [...base, 'Suspended'];
    }, [drillDown]);

    const clearDrillFilters = () => {
        setDrillSearch('');
        setDrillPortfolio('');
        setDrillPlatform('');
        setDrillAppId('');
        setDrillAppName('');
        setDrillStatus('');
        setDrillDecommStatus('');
        setDrillOwner('');
        setDrillAppType('');
        setDrillSortCol(null);
        setDrillSortDir('asc');
        setColFilters({});
    };

    const handleActualClick = (kpiId: string, mk: string, monthLabel: string, rowType: 'actual' | 'target' | 'forecast' = 'actual', subKey?: string) => {
        if (drillDown?.mk === mk && drillDown?.kpiId === kpiId && drillDown?.rowType === rowType) {
            setDrillDown(null); // toggle off
        } else {
            setDrillDown({ kpiId, mk, monthLabel, rowType, subKey });
            clearDrillFilters();
            // Default to Inactive for decom_apps actual
            if (kpiId === 'decom_apps' && rowType === 'actual') setDrillStatus('Inactive');
            // Default sort: Cyber Risk Exposure column descending
            if (kpiId === 'cyber_risk_exposure') { setDrillSortCol('Cyber Risk Exposure'); setDrillSortDir('desc'); }
            // Default sort: New Target Decomm Date descending for all forecast drill-downs
            if (rowType === 'forecast') { setDrillSortCol('New Target Decomm Date'); setDrillSortDir('desc'); }
        }
    };

    const handleExportCsv = () => {
        const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const now = new Date();
        const curMo = now.getUTCMonth() + 1;
        const curMonthMk = `${selectedYear}${String(curMo).padStart(2, '0')}`;
        const RATE_IDS = ['decom_completion_rate', 'failover_completeness', 'restore_completeness'];

        const getActualVal = (kpiId: string, mk: string): number | null => {
            if (mk > currentMk) return null;
            if (kpiId === 'cost_savings') {
                const v = costActuals[mk];
                if ((v === undefined || Number.isNaN(v)) && costTargets[mk] != null && !Number.isNaN(costTargets[mk])) return 0;
                return v ?? null;
            }
            if (kpiId === 'slippage_rate') return slippageActuals[mk] ?? null;
            if (kpiId === 'cyber_risk_exposure') return cyberActuals[mk] ?? null;
            if (kpiId === 'cost_leakage') return costLeakageActuals[mk] ?? null;
            if (kpiId === 'bia_validity') return biaCountActuals[mk] ?? null;
            if (kpiId === 'mvc_failover_overdue') return mvcResilienceActuals[mk] ?? null;
            if (RATE_IDS.includes(kpiId)) return rateActuals[kpiId]?.[mk] ?? null;
            if (kpiId !== 'decom_apps') return snapshotActuals[kpiId]?.[mk] ?? null;
            return actuals[mk] ?? null;
        };

        const fmtVal = (val: number | null, kpiId: string, key: string): string => {
            if (val == null) return '';
            if (kpiId === 'cyber_risk_exposure' && key === 'target') return val === 0 ? '0' : `<${val}`;
            if (RATE_IDS.includes(kpiId) || key === 'actual_pct' || (kpiId === 'slippage_rate' && key === 'target')) return `${val}%`;
            if (kpiId === 'cost_savings') return Number.isNaN(val) ? 'N/A' : formatUSD(val);
            return String(val);
        };

        const fmtDate = (d: any): string => {
            const dt = parseDate(d);
            if (!dt) return '-';
            return `${String(dt.getUTCDate()).padStart(2, '0')}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${dt.getUTCFullYear()}`;
        };

        const csvRows: string[][] = [];

        // -- Main Table --
        csvRows.push([`ERM Bowler Dashboard - ${selectedYear}`]);
        csvRows.push([]);
        csvRows.push(['Metric', 'UoM', 'FY Target', 'Row', ...MONTH_SHORT, 'YTD', 'FY']);

        for (const kpi of kpiRows) {
            for (const { key, label } of getVisibleRows(kpi)) {
                const monthVals = months.map(m => {
                    let val: number | null = null;
                    if (key === 'target') val = kpi.kpiId === 'cost_savings' ? (costTargets[m.mk] ?? null) : kpi.kpiId === 'decom_apps' ? (decomAppTargets[m.mk] ?? null) : kpi.kpiId === 'decom_completion_rate' ? (decomAppTargets[m.mk] ?? null) : (kpi.target[m.mk] ?? null);
                    else if (key === 'actual_pct') val = slippageActuals[m.mk] ?? null;
                    else if (key === 'actual_count') val = slippageCountActuals[m.mk] ?? null;
                    else if (key === 'actual') val = getActualVal(kpi.kpiId, m.mk);
                    return fmtVal(val, kpi.kpiId, key);
                });

                // YTD
                let ytdVal: number | null = null;
                if (key === 'target') {
                    if (kpi.kpiId === 'cost_savings') { let s = 0, ok = false; for (let m = 1; m <= curMo; m++) { const v = costTargets[`${selectedYear}${String(m).padStart(2, '0')}`]; if (v != null && !Number.isNaN(v)) { s += v; ok = true; } } ytdVal = ok ? s : null; }
                    else if (kpi.kpiId === 'decom_apps') ytdVal = decomAppTargets[curMonthMk] ?? null;
                    else if (kpi.kpiId === 'decom_completion_rate') ytdVal = decomAppTargets[curMonthMk] ?? null;
                    else ytdVal = kpi.target[curMonthMk] ?? null;
                } else if (key === 'actual_pct') { for (let m = curMo; m >= 1; m--) { const v = slippageActuals[`${selectedYear}${String(m).padStart(2, '0')}`]; if (v != null) { ytdVal = v; break; } } }
                else if (key === 'actual_count') { for (let m = curMo; m >= 1; m--) { const v = slippageCountActuals[`${selectedYear}${String(m).padStart(2, '0')}`]; if (v != null) { ytdVal = v; break; } } }
                else if (key === 'actual') {
                    if (kpi.kpiId === 'cost_savings') { let s = 0, ok = false; for (let m = 1; m <= curMo; m++) { const mk = `${selectedYear}${String(m).padStart(2, '0')}`; const v = costActuals[mk]; if (v != null && !Number.isNaN(v)) { s += v; ok = true; } else if ((v === undefined || Number.isNaN(v)) && costTargets[mk] != null && !Number.isNaN(costTargets[mk])) { ok = true; } } ytdVal = ok ? s : null; }
                    else { for (let m = curMo; m >= 1; m--) { const v = getActualVal(kpi.kpiId, `${selectedYear}${String(m).padStart(2, '0')}`); if (v != null) { ytdVal = v; break; } } }
                }

                // FY
                const fyMk = `${selectedYear}12`;
                let fyVal: number | null = null;
                if (key === 'target') {
                    if (kpi.kpiId === 'cost_savings') { let s = 0, ok = false; for (let m = 1; m <= 12; m++) { const v = costTargets[`${selectedYear}${String(m).padStart(2, '0')}`]; if (v != null && !Number.isNaN(v)) { s += v; ok = true; } } fyVal = ok ? s : null; }
                    else if (kpi.kpiId === 'decom_apps') fyVal = decomAppTargets[fyMk] ?? null;
                    else if (kpi.kpiId === 'decom_completion_rate') fyVal = decomRateTargets[fyMk] ?? null;
                    else fyVal = kpi.target[fyMk] ?? null;
                } else if (key === 'actual_pct') fyVal = slippageActuals[fyMk] ?? null;
                else if (key === 'actual_count') fyVal = slippageCountActuals[fyMk] ?? null;
                else if (key === 'actual') fyVal = ytdVal;

                csvRows.push([kpi.kpiLabel, kpi.uom, kpi.fyTargetDisplay ?? '', label, ...monthVals, fmtVal(ytdVal, kpi.kpiId, key), fmtVal(fyVal, kpi.kpiId, key)]);
            }
        }

        // -- Drill-Down (if open) --
        if (drillDown && filteredDrillApps.length > 0) {
            const drillKpi = kpiRows.find(k => k.kpiId === drillDown.kpiId);
            const drillTitle = `${drillKpi?.kpiLabel ?? drillDown.kpiId} - ${drillDown.monthLabel} (${drillDown.rowType === 'target' ? 'Target' : 'Actual'})`;
            const yr = parseInt(drillDown.mk.slice(0, 4), 10);
            const isSlippage = drillDown.kpiId === 'slippage_rate';
            const isBia = drillDown.kpiId === 'bia_validity';
            const isDecomApps = drillDown.kpiId === 'decom_apps';
            const isDecomRateTargetCsv = drillDown.kpiId === 'decom_completion_rate' && drillDown.rowType === 'target';
            const isCostSavings = drillDown.kpiId === 'cost_savings';
            const isCyber = drillDown.kpiId === 'cyber_risk_exposure';
            const isCostLeakage = drillDown.kpiId === 'cost_leakage';

            csvRows.push([]);
            csvRows.push([]);
            csvRows.push([`DRILL-DOWN: ${drillTitle}`, `${filteredDrillApps.length} apps`]);

            let headers: string[];
            if (isSlippage) headers = ['Portfolio', 'Platform', 'App ID', 'Application', 'Is MVC', 'Original Target Decomm Date', 'TDD as on Month', 'New Target Decomm Date', 'Delay in Months', 'Reason for Delay', 'MAC Status'];
            else if (isBia) headers = ['Portfolio', 'Platform', 'App ID', 'Application', 'Original Target Decomm Date', 'New Target Decomm Date', 'BIA Status', 'MAC Status'];
            else if (isCostLeakage) headers = ['Portfolio', 'Platform', 'App ID', 'Application', 'Original Target Decomm Date', 'New Target Decomm Date', 'Delay in Months', 'Potential Cost Impact', 'MAC Status'];
            else if (isDecomRateTargetCsv) headers = ['Portfolio', 'Platform', 'App ID', 'Application', 'Owner', 'Original Target Decomm Date', 'New Target Decomm Date', 'Reason for Delay', 'MAC Status', 'Decomm Status'];
            else if (isDecomApps) headers = ['Portfolio', 'Platform', 'App ID', 'Application', 'Owner', 'Original Target Decomm Date', 'New Target Decomm Date', 'Decommissioned Date', 'MAC Status'];
            else if (isCostSavings) headers = ['Portfolio', 'Platform', 'App ID', 'Application', 'Original Target Decomm Date', 'New Target Decomm Date', 'Decommissioned Date', 'Reason for Delay', 'Annualised Savings (USD)', 'MAC Status'];
            else if (isCyber) headers = ['Portfolio', 'Platform', 'App ID', 'Application', 'Original Target Decomm Date', 'New Target Decomm Date', 'Reason for Delay', 'Cyber Risk Exposure', 'MAC Status'];
            else headers = ['Portfolio', 'Platform', 'App ID', 'Application', 'Original Target Decomm Date', 'New Target Decomm Date', 'MAC Status'];

            headers = [...headers, 'Suspended'];
            csvRows.push(headers);

            for (const app of filteredDrillApps) {
                const delayMo = (() => { const o = parseDate(app.erm_tdd); const c = parseDate(app.target_date_decom); if (!o || !c) return null; return (c.getUTCFullYear() - o.getUTCFullYear()) * 12 + (c.getUTCMonth() - o.getUTCMonth()); })();
                const decomDateStr = fmtDate(app.decom_date);
                const targetDecomStr = fmtDate(app.target_date_decom);
                const origTddStr = fmtDate(app.erm_tdd);

                let dataRow: string[];
                if (isSlippage) {
                    dataRow = [app.portfolio ?? '-', app.platform ?? '-', app.app_id ?? '-', app.app_name ?? '-', app.mvc_group ? 'MVC' : '-', origTddStr, fmtDate(getSlipRevisionTdd(app, yr)), targetDecomStr, delayMo != null && delayMo > 0 ? `+${delayMo}mo` : '-', app.decom_extension_reason ?? '-', app.status ?? '-'];
                } else if (isBia) {
                    const c = getItemCost(app);
                    const potCost = (c != null && delayMo != null && delayMo > 0) ? `$${Math.round((c / 12) * delayMo).toLocaleString('en-US')}` : 'N/A';
                    dataRow = [app.portfolio ?? '-', app.platform ?? '-', app.app_id ?? '-', app.app_name ?? '-', origTddStr, targetDecomStr, app.bia_status === false ? 'Expired' : app.bia_status == null ? '-' : 'Valid', app.status ?? '-'];
                } else if (isDecomApps) {
                    dataRow = [app.portfolio ?? '-', app.platform ?? '-', app.app_id ?? '-', app.app_name ?? '-', app.owner_names ?? '-', origTddStr, targetDecomStr, decomDateStr, app.status ?? '-'];
                } else if (isDecomRateTargetCsv) {
                    dataRow = [app.portfolio ?? '-', app.platform ?? '-', app.app_id ?? '-', app.app_name ?? '-', app.owner_names ?? '-', origTddStr, targetDecomStr, app.decom_extension_reason ?? '-', app.status ?? '-', getTargetDecommStatus(app)];
                } else if (isCostSavings) {
                    const c = getItemTotalCost(app);
                    dataRow = [app.portfolio ?? '-', app.platform ?? '-', app.app_id ?? '-', app.app_name ?? '-', origTddStr, targetDecomStr, decomDateStr, app.decom_extension_reason ?? '-', c != null ? `$${c.toLocaleString('en-US')}` : 'N/A', app.status ?? '-'];
                } else if (isCyber) {
                    dataRow = [app.portfolio ?? '-', app.platform ?? '-', app.app_id ?? '-', app.app_name ?? '-', origTddStr, targetDecomStr, app.decom_extension_reason ?? '-', app.risk_exposure_all != null ? String(app.risk_exposure_all) : '-', app.status ?? '-'];
                } else if (isCostLeakage) {
                    const c = getItemCost(app);
                    const potCost = (c != null && delayMo != null && delayMo > 0) ? `$${Math.round((c / 12) * delayMo).toLocaleString('en-US')}` : 'N/A';
                    dataRow = [app.portfolio ?? '-', app.platform ?? '-', app.app_id ?? '-', app.app_name ?? '-', origTddStr, targetDecomStr, delayMo != null && delayMo > 0 ? `+${delayMo}mo` : '-', potCost, app.status ?? '-'];
                } else {
                    dataRow = [app.portfolio ?? '-', app.platform ?? '-', app.app_id ?? '-', app.app_name ?? '-', origTddStr, targetDecomStr, app.status ?? '-'];
                }
                dataRow.push(app.suspended === true ? 'Yes' : app.suspended === false ? 'No' : '-');
                csvRows.push(dataRow);
            }
        }

        // Trigger CSV download with UTF-8 BOM (so Excel renders correctly)
        const csv = '\uFEFF' + csvRows.map(r => r.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ERM_Bowler_${selectedYear}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Fetch targets/forecasts from backend
    const fetchKpiRows = useCallback(async () => {
        setLoading(true);
        setFetchError(null);
        try {
            const res = await authFetch(
                `${API_BASE_URL}/api/reliability-framework/decom/bowler-kpis?year=${selectedYear}`,
            );
            if (res.ok) {
                const json: BackendKpiRow[] = await res.json();
                const rows: KpiRow[] = json.map((r) => {
                    const target: Record<string, number | null> = {};
                    const forecast: Record<string, number | null> = {};
                    for (const m of r.monthly) {
                        target[m.month_key] = m.target;
                        forecast[m.month_key] = m.forecast;
                    }
                    const defaults = buildDefaultKpiRows(selectedYear).find((d) => d.kpiId === r.kpi_id);
                    return {
                        kpiId: r.kpi_id,
                        kpiLabel: r.kpi_label,
                        type: defaults?.type ?? 'Leading',
                        owner: r.owner,
                        uom: r.uom,
                        fyTargetDisplay: defaults?.fyTargetDisplay,
                        trigger: r.trigger,
                        direction: 'up' as const,
                        target,
                        forecast,
                    };
                });
                const REMOVED_KPI_IDS = new Set(['delayed_apps', 'delayed_mvc_apps', 'bia_expired', 'mvc_failover_overdue', 'mvc_restore_overdue']);
                const filtered = rows.filter((r) => !REMOVED_KPI_IDS.has(r.kpiId));
                setKpiRows(filtered.length > 0 ? filtered : buildDefaultKpiRows(selectedYear));
            } else {
                // Endpoint not found or error - fall back to hardcoded targets
                setKpiRows(buildDefaultKpiRows(selectedYear));
            }
        } catch {
            // Network error - fall back to hardcoded targets
            setKpiRows(buildDefaultKpiRows(selectedYear));
        } finally {
            setLoading(false);
        }
    }, [selectedYear]);

    useEffect(() => {
        fetchKpiRows();
    }, [fetchKpiRows]);

    // --- Frozen (sticky) left columns: Metric .. Row -------------------------
    const frozenCols = {
        metric:   { left: 0,   width: 220 },
        uom:      { left: 220, width: 60  },
        fyTarget: { left: 280, width: 80  },
        row:      { left: 360, width: 120 },
    };
    const stickyCol = (c: { left: number; width: number }, opts?: { header?: boolean; last?: boolean }) => ({
        position: 'sticky' as const,
        left: c.left,
        width: c.width, minWidth: c.width, maxWidth: c.width,
        zIndex: opts?.header ? 6 : 2,
        ...(opts?.last ? { boxShadow: '3px 0 5px -2px rgba(0,0,0,0.25)' } : {}),
    });

    return (
        <Box sx={{ mt: 1 }}>
            {/* Header bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0067b3', whiteSpace: 'nowrap' }}>
                    ERM Bowler Dashboard
                </Typography>
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        px: 1.5,
                        py: 0.75,
                        borderLeft: '4px solid #f9a825',
                        backgroundColor: '#fff8e1',
                    }}
                >
                    <Typography
                        sx={{
                            color: '#5d4037',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            lineHeight: 1.35,
                        }}
                    >
                        Note: The Bowler is being updated with the latest list of applications tagged to the ERM.
                    </Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel id="decom-bowler-year-label">Year</InputLabel>
                    <Select
                        labelId="decom-bowler-year-label"
                        label="Year"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                        <MenuItem value={2026}>2026</MenuItem>
                    </Select>
                </FormControl>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={handleExportCsv}
                    startIcon={<Download fontSize="small" />}
                    sx={{ textTransform: 'none' }}
                >
                    Export CSV
                </Button>
            </Box>

            {fetchError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {fetchError} - targets and forecasts unavailable.
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: 'auto' }}>
                    <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: 0, '& .MuiTableCell-root': { border: '1px solid #000 !important', fontSize: '0.825rem', py: '3px', px: '8px' }, '& tr.dcm-row-target td:not([rowspan])': { borderBottom: '1px solid #dcdcdc !important' }, '& tr.dcm-row-actual td:not([rowspan])': { borderTop: '1px solid #dcdcdc !important' } }}>
                        <TableHead>
                            {/* Top header: fixed cols + Month labels */}
                            <TableRow>
                                <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.825rem', backgroundColor: '#42B0D5 !important', color: '#fff !important', border: '1px solid #5bc0de !important', letterSpacing: '0.03em', ...stickyCol(frozenCols.metric, { header: true }) }}>Metric</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.825rem', backgroundColor: '#42B0D5 !important', color: '#fff !important', border: '1px solid #5bc0de !important', letterSpacing: '0.03em', ...stickyCol(frozenCols.uom, { header: true }) }}>UoM</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.825rem', backgroundColor: '#42B0D5 !important', color: '#fff !important', border: '1px solid #5bc0de !important', letterSpacing: '0.03em', ...stickyCol(frozenCols.fyTarget, { header: true }) }}>FY Target</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.825rem', backgroundColor: '#42B0D5 !important', color: '#fff !important', border: '1px solid #5bc0de !important', letterSpacing: '0.03em', ...stickyCol(frozenCols.row, { header: true, last: true }) }}>Row</TableCell>
                                {months.map((m) => (
                                    <TableCell
                                        key={m.mk}
                                        align="center"
                                        sx={{
                                            fontWeight: 700, fontSize: '0.825rem',
                                            backgroundColor: `${m.mk === currentMk ? '#FFD600' : '#42B0D5'} !important`,
                                            color: `${m.mk === currentMk ? '#000' : '#fff'} !important`,
                                            border: m.mk === currentMk ? '1px solid #c8a800 !important' : '1px solid #5bc0de !important',
                                            minWidth: m.mk === currentMk ? 170 : 150, width: m.mk === currentMk ? 170 : 150, letterSpacing: '0.03em',
                                        }}
                                    >
                                        {m.shortLabel}-{String(selectedYear).slice(2)}
                                    </TableCell>
                                ))}
                                <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.825rem', backgroundColor: '#42B0D5 !important', color: '#fff !important', border: '1px solid #5bc0de !important', minWidth: 150, letterSpacing: '0.03em' }}>YTD</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {kpiRows.map((kpi) => {
                                /** Delayed/Cancelled Critical — Coming Soon placeholder */
                                if (kpi.kpiId === 'delayed_cancelled_critical') {
                                    const rowLabelSx = { fontWeight: 400, fontSize: '0.825rem', whiteSpace: 'nowrap' as const, backgroundColor: '#fafafa', borderRight: '1px solid #bdbdbd', textAlign: 'center' as const, ...stickyCol(frozenCols.row, { last: true }) };
                                    return (
                                        <>
                                            <TableRow key="delayed_cancelled_critical_target" className="dcm-row-target" sx={{ height: 30, borderTop: '2px solid #90a4ae' }}>
                                                <TableCell rowSpan={2} sx={{ fontWeight: 700, fontSize: '0.85rem', backgroundColor: '#fafafa', borderRight: '1px solid #bdbdbd', verticalAlign: 'middle', ...stickyCol(frozenCols.metric) }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{KPI_TOOLTIP[kpi.kpiId] ? <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{KPI_TOOLTIP[kpi.kpiId]}</span>} arrow placement="top"><span style={{ cursor: 'help' }}>{kpi.kpiLabel}</span></Tooltip> : <span>{kpi.kpiLabel}</span>}</Box>
                                                </TableCell>
                                                <TableCell rowSpan={2} align="center" sx={{ backgroundColor: '#fafafa', borderRight: '1px solid #bdbdbd', verticalAlign: 'middle', ...stickyCol(frozenCols.uom) }}>{kpi.uom}</TableCell>
                                                <TableCell rowSpan={2} align="center" sx={{ backgroundColor: '#fafafa', borderRight: '2px solid #bdbdbd', fontWeight: 700, verticalAlign: 'middle', ...stickyCol(frozenCols.fyTarget) }}>{kpi.fyTargetDisplay ?? '-'}</TableCell>
                                                <TableCell align="center" sx={{ ...rowLabelSx, borderBottom: '1px solid #cfcfcf !important' }}>Target</TableCell>
                                                <TableCell rowSpan={2} colSpan={months.length + 1} align="center" sx={{ backgroundColor: '#fafafa', color: '#9e9e9e', fontStyle: 'italic', fontWeight: 600, fontSize: '0.9rem', letterSpacing: '0.05em' }}>
                                                    Coming Soon
                                                </TableCell>
                                            </TableRow>
                                            <TableRow key="delayed_cancelled_critical_actual" className="dcm-row-actual" sx={{ height: 30 }}>
                                                <TableCell align="center" sx={{ ...rowLabelSx, borderTop: '1px solid #cfcfcf !important' }}>Actual</TableCell>
                                            </TableRow>
                                        </>
                                    );
                                }

                                /** Cost Leakage — Coming Soon placeholder */
                                if (kpi.kpiId === 'cost_leakage') {
                                    const rowLabelSx = { fontWeight: 400, fontSize: '0.825rem', whiteSpace: 'nowrap' as const, backgroundColor: '#fafafa', borderRight: '1px solid #bdbdbd', textAlign: 'center' as const, ...stickyCol(frozenCols.row, { last: true }) };
                                    return (
                                        <>
                                            <TableRow key="cost_leakage_target" className="dcm-row-target" sx={{ height: 30, borderTop: '2px solid #90a4ae' }}>
                                                <TableCell rowSpan={2} sx={{ fontWeight: 700, fontSize: '0.85rem', backgroundColor: '#fafafa', borderRight: '1px solid #bdbdbd', verticalAlign: 'middle', ...stickyCol(frozenCols.metric) }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{KPI_TOOLTIP[kpi.kpiId] ? <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{KPI_TOOLTIP[kpi.kpiId]}</span>} arrow placement="top"><span style={{ cursor: 'help' }}>{kpi.kpiLabel}</span></Tooltip> : <span>{kpi.kpiLabel}</span>}</Box>
                                                </TableCell>
                                                <TableCell rowSpan={2} align="center" sx={{ backgroundColor: '#fafafa', borderRight: '1px solid #bdbdbd', verticalAlign: 'middle', ...stickyCol(frozenCols.uom) }}>{kpi.uom}</TableCell>
                                                <TableCell rowSpan={2} align="center" sx={{ backgroundColor: '#fafafa', borderRight: '2px solid #bdbdbd', fontWeight: 700, verticalAlign: 'middle', ...stickyCol(frozenCols.fyTarget) }}>{kpi.fyTargetDisplay ?? '-'}</TableCell>
                                                <TableCell align="center" sx={{ ...rowLabelSx, borderBottom: '1px solid #cfcfcf !important' }}>Target</TableCell>
                                                <TableCell rowSpan={2} colSpan={months.length + 1} align="center" sx={{ backgroundColor: '#fafafa', color: '#9e9e9e', fontStyle: 'italic', fontWeight: 600, fontSize: '0.9rem', letterSpacing: '0.05em' }}>
                                                    Coming Soon
                                                </TableCell>
                                            </TableRow>
                                            <TableRow key="cost_leakage_actual" className="dcm-row-actual" sx={{ height: 30 }}>
                                                <TableCell align="center" sx={{ ...rowLabelSx, borderTop: '1px solid #cfcfcf !important' }}>Actual</TableCell>
                                            </TableRow>
                                        </>
                                    );
                                }

                                /** Get actual value for a given month key */
                                const getActual = (mk: string): number | null => {
                                    // No actuals for future months
                                    if (mk > currentMk) return null;
                                    // Cost KPI: return 0 for past months with a real $ target but no realized cost
                                    if (kpi.kpiId === 'cost_savings') {
                                        const v = cumulativeCostActuals[mk];
                                        if ((v === undefined || Number.isNaN(v)) && cumulativeCostTargets[mk] != null && !Number.isNaN(cumulativeCostTargets[mk])) return 0;
                                        return v ?? null;
                                    }
                                    // Slippage KPI
                                    if (kpi.kpiId === 'slippage_rate') return slippageActuals[mk] ?? null;
                                    // Cyber Risk Exposure KPI
                                    if (kpi.kpiId === 'cyber_risk_exposure') return cyberActuals[mk] ?? null;
                                    // Cost Leakage KPI
                                    if (kpi.kpiId === 'cost_leakage') return costLeakageActuals[mk] ?? null;
                                    // BIA Validity KPI
                                    if (kpi.kpiId === 'bia_validity') return biaCountActuals[mk] ?? null;
                                    // MVC Resilience Test Gap KPI
                                    if (kpi.kpiId === 'mvc_failover_overdue') return mvcResilienceActuals[mk] ?? null;
                                    // Percentage / rate KPIs
                                    if (RATE_KPI_IDS.includes(kpi.kpiId)) {
                                        return rateActuals[kpi.kpiId]?.[mk] ?? null;
                                    }
                                    if (kpi.kpiId !== 'decom_apps') {
                                        // Snapshot KPI: use Trends-style revision-aware computed actuals
                                        return snapshotActuals[kpi.kpiId]?.[mk] ?? null;
                                    }
                                    // Cumulative KPI (decom_apps)
                                    return actuals[mk] ?? null;
                                };

                                // Compute YTD actual
                                const ytdActual = (() => {
                                    if (kpi.kpiId === 'cost_savings') {
                                        // Use pre-computed cumulative actuals for YTD
                                        const v = cumulativeCostActuals[currentMk];
                                        return v != null ? v : null;
                                    }
                                    for (let m = new Date().getUTCMonth() + 1; m >= 1; m--) {
                                        const mk = `${selectedYear}${String(m).padStart(2, '0')}`;
                                        const v = getActual(mk);
                                        if (v != null) return v;
                                    }
                                    return null;
                                })();
                                // FY target = last month's target (cumulative)
                                const fyTarget = kpi.target[`${selectedYear}12`] ?? null;
                                // FY forecast = last month's forecast
                                const fyForecast = kpi.forecast[`${selectedYear}12`] ?? null;
                                // PY YTD (for decom_apps and cost_savings; snapshot KPIs leave blank)
                                const pyYtd = (kpi.kpiId !== 'decom_apps' && kpi.kpiId !== 'cost_savings') ? null : (() => {
                                    if (kpi.kpiId === 'cost_savings') {
                                        let sum = 0, hasAny = false;
                                        for (let m = 1; m <= 12; m++) {
                                            const mk = `${selectedYear - 1}${String(m).padStart(2, '0')}`;
                                            const v = pyCostActuals[mk];
                                            if (v != null && !Number.isNaN(v)) { sum += v; hasAny = true; }
                                        }
                                        return hasAny ? sum : null;
                                    }
                                    for (let m = 12; m >= 1; m--) {
                                        const mk = `${selectedYear - 1}${String(m).padStart(2, '0')}`;
                                        if (pyActuals[mk] != null) return pyActuals[mk];
                                    }
                                    return null;
                                })();

                                // Determine if actual + forecast should be merged into one split row
                                const _visibleRows = getVisibleRows(kpi);
                                const _hasForecastRow = _visibleRows.some(r => r.key === 'forecast');
                                const _hasActualRow = _visibleRows.some(r => r.key === 'actual');
                                const shouldMergeActualForecast = _hasForecastRow && _hasActualRow;

                                return _visibleRows.map(({ key, label }, rowIdx) => {
                                    // Skip the standalone forecast row — it is merged into the actual row
                                    if (key === 'forecast' && shouldMergeActualForecast) return null;

                                    const isFirstRow = rowIdx === 0;
                                    const visibleRowCount = _visibleRows.length;
                                    // Grey divider between Target and Actual/Forecast rows (same KPI) — keeps them visually grouped
                                    const innerDivider = key === 'target'
                                        ? { borderBottom: '1px solid #cfcfcf !important' }
                                        : { borderTop: '1px solid #cfcfcf !important' };
                                    // Effective row count used for rowSpan (forecast merged = 1 fewer row)
                                    const effectiveRowCount = shouldMergeActualForecast ? visibleRowCount - 1 : visibleRowCount;
                                    const curMonthMk = `${selectedYear}${String(new Date().getUTCMonth() + 1).padStart(2, '0')}`;
                                    // Slippage YTD/FY helpers
                                    const slippageYtdPct = kpi.kpiId === 'slippage_rate' ? (() => { for (let m = new Date().getUTCMonth() + 1; m >= 1; m--) { const mk = `${selectedYear}${String(m).padStart(2, '0')}`; const v = slippageActuals[mk]; if (v != null) return v === 0 ? null : v; } return null; })() : null;
                                    const slippageFyPct = kpi.kpiId === 'slippage_rate' ? (() => { const v = slippageActuals[`${selectedYear}12`]; return (v != null && v === 0) ? null : (v ?? null); })() : null;
                                    const slippageYtdCount = kpi.kpiId === 'slippage_rate' ? (() => { for (let m = new Date().getUTCMonth() + 1; m >= 1; m--) { const mk = `${selectedYear}${String(m).padStart(2, '0')}`; const v = slippageCountActuals[mk]; if (v != null) return v === 0 ? null : v; } return null; })() : null;
                                    const slippageFyCount = kpi.kpiId === 'slippage_rate' ? (() => { const v = slippageCountActuals[`${selectedYear}12`]; return (v != null && v === 0) ? null : (v ?? null); })() : null;
                                    const costTargetYtd = kpi.kpiId === 'cost_savings' ? (() => {
                                        // Use cumulative cost target up to current month
                                        const v = cumulativeCostTargets[curMonthMk];
                                        return v != null ? v : null;
                                    })() : null;
                                    const costTargetFy = kpi.kpiId === 'cost_savings' ? (() => {
                                        // FY cumulative target = December cumulative value
                                        const v = cumulativeCostTargets[`${selectedYear}12`];
                                        return v != null ? v : null;
                                    })() : null;
                                    // Cumulative: YTD = last non-null value up to current month; FY = December value
                                    const decomAppTargetYtd = kpi.kpiId === 'decom_apps' ? (() => {
                                        let last: number | null = null;
                                        for (let m = 1; m <= 12; m++) {
                                            const mk = `${selectedYear}${String(m).padStart(2, '0')}`;
                                            if (mk > curMonthMk) break;
                                            const v = decomAppTargets[mk];
                                            if (v != null) last = v;
                                        }
                                        return last;
                                    })() : null;
                                    const decomAppTargetFy = kpi.kpiId === 'decom_apps'
                                        ? (decomAppTargets[`${selectedYear}12`] ?? null)
                                        : null;
                                    const ytdVal = key === 'target'
                                        ? (kpi.kpiId === 'cost_savings' ? costTargetYtd : kpi.kpiId === 'decom_apps' ? decomAppTargetYtd : kpi.kpiId === 'decom_completion_rate' ? (decomRateTargets[curMonthMk] ?? null) : (kpi.target[curMonthMk] ?? null))
                                        : key === 'actual_pct' ? slippageYtdPct
                                        : key === 'actual_count' ? slippageYtdCount
                                        : key === 'forecast' ? (kpi.kpiId === 'decom_completion_rate' ? null : (kpi.forecast[curMonthMk] ?? null)) // forecast YTD not shown for decom_rate (future-only)
                                            : key === 'actual' ? ytdActual
                                                : pyYtd;
                                    const fyVal = key === 'target'
                                        ? (kpi.kpiId === 'cost_savings' ? costTargetFy : kpi.kpiId === 'decom_apps' ? decomAppTargetFy : kpi.kpiId === 'decom_completion_rate' ? (decomRateTargets[`${selectedYear}12`] ?? null) : fyTarget)
                                        : key === 'actual_pct' ? slippageFyPct
                                        : key === 'actual_count' ? slippageFyCount
                                        : key === 'forecast' ? (kpi.kpiId === 'decom_completion_rate' ? (decomRateForecast[`${selectedYear}12`] ?? null) : fyForecast)
                                            : key === 'actual' ? ytdActual
                                                : pyYtd;

                                    return (
                                        <TableRow
                                            key={`${kpi.kpiId}-${key}`}
                                            className={key === 'target' ? 'dcm-row-target' : 'dcm-row-actual'}
                                            sx={{ height: 30, borderTop: isFirstRow ? '2px solid #90a4ae' : undefined }}
                                        >
                                            {/* Fixed cols - only rendered on first sub-row (rowSpan=4) */}
                                            {isFirstRow && (
                                                <>
                                                    <TableCell rowSpan={effectiveRowCount} sx={{
                                                        fontWeight: 700, fontSize: '0.85rem',
                                                        backgroundColor: '#fafafa',
                                                        borderRight: '1px solid #bdbdbd',
                                                        verticalAlign: 'middle',
                                                        ...stickyCol(frozenCols.metric),
                                                    }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            {KPI_TOOLTIP[kpi.kpiId] ? (
                                                                <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{KPI_TOOLTIP[kpi.kpiId]}</span>} arrow placement="top">
                                                                    <span style={{ cursor: 'help' }}>{kpi.kpiLabel}{kpi.kpiId === 'cost_savings' && <sup style={{ fontSize: '0.7em', color: '#000', fontWeight: 800, marginLeft: '1px' }}>*</sup>}</span>
                                                                </Tooltip>
                                                            ) : (
                                                                <span>{kpi.kpiLabel}{kpi.kpiId === 'cost_savings' && <sup style={{ fontSize: '0.7em', color: '#000', fontWeight: 800, marginLeft: '1px' }}>*</sup>}</span>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell rowSpan={effectiveRowCount} align="center" sx={{ backgroundColor: '#fafafa', borderRight: '1px solid #bdbdbd', verticalAlign: 'middle', ...stickyCol(frozenCols.uom) }}>{kpi.uom}</TableCell>
                                                    <TableCell rowSpan={effectiveRowCount} align="center" sx={{ backgroundColor: '#fafafa', borderRight: '2px solid #bdbdbd', fontWeight: 700, verticalAlign: 'middle', ...stickyCol(frozenCols.fyTarget) }}>
                                                        {kpi.kpiId === 'cost_savings' && costTargetFy != null
                                                            ? (() => { const fym = costTargetFy / 1_000_000; return `$${fym.toFixed(1)}m`; })()
                                                            : kpi.kpiId === 'decom_completion_rate'
                                                            ? <Box sx={{ lineHeight: 1.3 }}>100%{fyErmAppCount > 0 && <Box component="span" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 400, color: '#555' }}>({fyErmAppCount} apps)</Box>}</Box>
                                                            : (kpi.fyTargetDisplay ?? '-')}
                                                    </TableCell>
                                                </>
                                            )}
                                            {/* Row label */}
                                            <TableCell align="center" sx={{
                                                fontWeight: 400, fontSize: '0.825rem', whiteSpace: 'nowrap',
                                                backgroundColor: '#fafafa', borderRight: '1px solid #bdbdbd',
                                                textAlign: 'center', padding: 0,
                                                ...stickyCol(frozenCols.row, { last: true }),
                                                ...innerDivider,
                                            }}>
                                                {shouldMergeActualForecast && key === 'actual' ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
                                                        <Box sx={{ width: '50%', px: '4px', py: '3px', lineHeight: 1.43, borderRight: '1px solid #e0e0e0', fontWeight: 400, fontSize: '0.825rem', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>Actual</Box>
                                                        <Box sx={{ width: '50%', px: '4px', py: '3px', lineHeight: 1.43, color: '#555', fontWeight: 400, fontSize: '0.825rem', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>Forecast</Box>
                                                    </Box>
                                                ) : label}
                                            </TableCell>
                                            {/* Monthly values */}
                                            {months.map((m) => {
                                                let value: number | null = null;
                                                if (key === 'target') value = kpi.kpiId === 'cost_savings' ? (cumulativeCostTargets[m.mk] ?? null) : kpi.kpiId === 'decom_apps' ? (decomAppTargets[m.mk] ?? null) : kpi.kpiId === 'decom_completion_rate' ? (decomRateTargets[m.mk] ?? null) : (kpi.target[m.mk] ?? null);
                                                if (key === 'actual_pct') { const _v = slippageActuals[m.mk]; value = (_v != null && _v === 0) ? null : (_v ?? null); }
                                                if (key === 'actual_count') { const _v = slippageCountActuals[m.mk]; value = (_v != null && _v === 0) ? null : (_v ?? null); }
                                                if (key === 'forecast') value = kpi.kpiId === 'decom_completion_rate' ? (decomRateForecast[m.mk] ?? null) : kpi.kpiId === 'cost_savings' ? (costForecast[m.mk] ?? null) : (kpi.forecast[m.mk] ?? null);
                                                if (key === 'actual') value = getActual(m.mk);
                                                if (key === 'py') {
                                                    if (kpi.kpiId !== 'decom_apps') {
                                                        value = null;
                                                    } else {
                                                        const pyMk = `${selectedYear - 1}${m.mk.slice(4)}`;
                                                        value = pyActuals[pyMk] ?? null;
                                                    }
                                                }
                                                // Use cumulative target for cost_savings so the colour comparison is apples-to-apples
                                                const targetVal = kpi.kpiId === 'cost_savings' ? (cumulativeCostTargets[m.mk] ?? null) : kpi.kpiId === 'decom_apps' ? (decomAppTargets[m.mk] ?? null) : kpi.kpiId === 'decom_completion_rate' ? (decomRateTargets[m.mk] ?? null) : (kpi.target[m.mk] ?? null);
                                                const isActualLike = key === 'actual' || key === 'actual_pct' || key === 'actual_count';
                                                const bgKey: 'actual' | 'py' = isActualLike ? 'actual' : 'py';
                                                // actual_count row: colour using the % value so it matches the actual_pct row
                                                const bgValue = key === 'actual_count' ? (slippageActuals[m.mk] ?? null) : value;
                                                const rawBg = (isActualLike || key === 'py') && m.mk < currentMk ? cellBg(bgKey, bgValue, targetVal, kpi.direction) : 'transparent';
                                                // Cost Savings N/A: only show red when there was a real $ target but nothing was realized
                                                const costSavingsNa = kpi.kpiId === 'cost_savings' && isActualLike && m.mk < currentMk
                                                    && (value == null || Number.isNaN(value as number))
                                                    && cumulativeCostTargets[m.mk] != null && !Number.isNaN(cumulativeCostTargets[m.mk]);
                                                const bg = costSavingsNa ? '#ffcdd2' : rawBg;
                                                const isClickable = (isActualLike && value != null) ||
                                                    (key === 'target' && (kpi.kpiId === 'cost_savings' || kpi.kpiId === 'decom_apps' || kpi.kpiId === 'decom_completion_rate') && value != null) ||
                                                    (key === 'forecast' && (kpi.kpiId === 'decom_completion_rate' || kpi.kpiId === 'cost_savings') && value != null);
                                                const isActive = isClickable && drillDown?.kpiId === kpi.kpiId && drillDown?.mk === m.mk &&
                                                    drillDown?.rowType === (key === 'target' ? 'target' : key === 'forecast' ? 'forecast' : 'actual') &&
                                                    (drillDown?.subKey ?? 'actual') === (key === 'target' ? 'target' : key);

                                                const formatVal = (v: number | null, cellKey: string) => {
                                                    if (v != null && !(kpi.kpiId === 'cost_savings' && Number.isNaN(v))) {
                                                        if (kpi.kpiId === 'cyber_risk_exposure' && cellKey === 'target') return v === 0 ? '0' : `<${v}`;
                                                        if (RATE_KPI_IDS.includes(kpi.kpiId) || cellKey === 'actual_pct' || (kpi.kpiId === 'slippage_rate' && cellKey === 'target')) return `${v}%`;
                                                        if (kpi.kpiId === 'cost_savings') return formatUSD(v);
                                                        if (kpi.kpiId === 'cost_leakage') return formatUSD(v);
                                                        return v;
                                                    }
                                                    if (cellKey === 'forecast') return <span style={{ color: '#888', fontSize: '0.75rem' }}>—</span>;
                                                    if (m.mk <= currentMk) {
                                                        const hasDataNoCost = kpi.kpiId === 'cost_savings' && (
                                                            (cellKey === 'target' && Number.isNaN(costTargets[m.mk])) ||
                                                            (cellKey === 'actual' && Number.isNaN(costActuals[m.mk]))
                                                        );
                                                        return <span style={{ color: hasDataNoCost ? '#424242' : '#888', fontSize: '0.75rem', fontWeight: hasDataNoCost ? 700 : 600 }}>N/A</span>;
                                                    }
                                                    return '';
                                                };

                                                // --- Merged Actual + Forecast cell ---
                                                if (shouldMergeActualForecast && key === 'actual') {
                                                    const forecastValue = kpi.kpiId === 'decom_completion_rate' ? (decomRateForecast[m.mk] ?? null) : kpi.kpiId === 'cost_savings' ? (costForecast[m.mk] ?? null) : (kpi.forecast[m.mk] ?? null);
                                                    const isFcClickable = (kpi.kpiId === 'decom_completion_rate' || kpi.kpiId === 'cost_savings') && forecastValue != null;
                                                    const isFcActive = isFcClickable && drillDown?.kpiId === kpi.kpiId && drillDown?.mk === m.mk && drillDown?.rowType === 'forecast';
                                                    const isActActive = isClickable && drillDown?.kpiId === kpi.kpiId && drillDown?.mk === m.mk && drillDown?.rowType === 'actual';
                                                    const fcBg = m.mk < currentMk ? forecastBg(forecastValue, targetVal, kpi.direction) : 'transparent';
                                                    const actBg = isActActive ? '#42B0D5' : bg;
                                                    const fcActiveBg = isFcActive ? '#42B0D5' : fcBg;
                                                    return (
                                                        <TableCell
                                                            key={m.mk}
                                                            align="center"
                                                            sx={{
                                                                padding: 0, overflow: 'hidden', verticalAlign: 'middle',
                                                                background: `linear-gradient(to right, ${actBg} 0 50%, ${fcActiveBg} 50% 100%)`,
                                                                ...innerDivider,
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', height: '100%' }}>
                                                                {/* Actual compartment */}
                                                                <Box
                                                                    onClick={isClickable ? () => handleActualClick(kpi.kpiId, m.mk, `${m.shortLabel}-${String(selectedYear).slice(2)}`, 'actual', 'actual') : undefined}
                                                                    sx={{
                                                                        width: '50%', px: '4px', py: '3px', lineHeight: 1.43,
                                                                        borderRight: '1px solid #bdbdbd',
                                                                        color: isActActive ? '#fff' : (bg === '#ffcdd2' ? '#c62828' : '#333'),
                                                                        fontWeight: m.mk === currentMk ? 700 : 400,
                                                                        fontSize: '0.825rem',
                                                                        whiteSpace: 'nowrap',
                                                                        cursor: isClickable ? 'pointer' : 'default',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        '&:hover': isClickable ? { opacity: 0.85 } : {},
                                                                    }}
                                                                >
                                                                    {formatVal(value, 'actual')}
                                                                </Box>
                                                                {/* Forecast compartment */}
                                                                <Box
                                                                    onClick={isFcClickable ? () => handleActualClick(kpi.kpiId, m.mk, `${m.shortLabel}-${String(selectedYear).slice(2)}`, 'forecast', 'forecast') : undefined}
                                                                    sx={{
                                                                        width: '50%', px: '4px', py: '3px', lineHeight: 1.43,
                                                                        color: isFcActive ? '#fff' : fcBg === '#ffcdd2' ? '#c62828' : fcBg === '#c8e6c9' ? '#333' : '#555',
                                                                        fontWeight: m.mk === currentMk ? 700 : 400,
                                                                        cursor: isFcClickable ? 'pointer' : 'default',
                                                                        fontSize: '0.825rem',
                                                                        whiteSpace: 'nowrap',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        '&:hover': isFcClickable ? { opacity: 0.85 } : {},
                                                                    }}
                                                                >
                                                                    {formatVal(forecastValue, 'forecast')}
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                    );
                                                }

                                                return (
                                                    <TableCell
                                                        key={m.mk}
                                                        align="center"
                                                        onClick={isClickable ? () => handleActualClick(kpi.kpiId, m.mk, `${m.shortLabel}-${String(selectedYear).slice(2)}`, key === 'target' ? 'target' : key === 'forecast' ? 'forecast' : 'actual', key) : undefined}
                                                        sx={{
                                                            bgcolor: isActive ? '#42B0D5' : bg,
                                                            color: isActive ? '#fff' : (bg === '#ffcdd2' ? '#c62828' : '#333'),
                                                            fontWeight: m.mk === currentMk ? 700 : 400,
                                                            cursor: isClickable ? 'pointer' : 'default',
                                                            '&:hover': isClickable ? { opacity: 0.8 } : {},
                                                            ...innerDivider,
                                                        }}
                                                    >
                                                        {value != null && !(kpi.kpiId === 'cost_savings' && Number.isNaN(value as number)) ? (
                                                            kpi.kpiId === 'cyber_risk_exposure' && key === 'target'
                                                                ? (value === 0 ? '0' : `<${value}`)
                                                                : (RATE_KPI_IDS.includes(kpi.kpiId) || key === 'actual_pct' || (kpi.kpiId === 'slippage_rate' && key === 'target')) ? `${value}%` :
                                                                kpi.kpiId === 'cost_savings' ? formatUSD(value) :
                                                                kpi.kpiId === 'cost_leakage' ? formatUSD(value) :
                                                                value
                                                        ) : (key === 'forecast' ? <span style={{ color: '#888', fontSize: '0.75rem' }}>-</span> : m.mk <= currentMk ? (() => {
                                                            // cost_savings NaN = apps exist but cost unmapped → darker N/A
                                                            const hasDataNoCost = kpi.kpiId === 'cost_savings' && (
                                                                (key === 'target' && Number.isNaN(costTargets[m.mk])) ||
                                                                (key === 'actual' && Number.isNaN(costActuals[m.mk]))
                                                            );
                                                            return <span style={{ color: hasDataNoCost ? '#424242' : '#888', fontSize: '0.75rem', fontWeight: hasDataNoCost ? 700 : 600 }}>N/A</span>;
                                                        })() : '')}
                                                    </TableCell>
                                                );
                                            })}
                                            {/* YTD */}
                                            {(() => {
                                                const ytdMk = curMonthMk;
                                                const isYtdActualLike = key === 'actual' || key === 'actual_pct' || key === 'actual_count';
                                                const isYtdClickable = (isYtdActualLike && ytdVal != null) ||
                                                    (key === 'target' && (kpi.kpiId === 'cost_savings' || kpi.kpiId === 'decom_apps' || kpi.kpiId === 'decom_completion_rate') && ytdVal != null);
                                                const isYtdActive = isYtdClickable && drillDown?.kpiId === kpi.kpiId && drillDown?.mk === ytdMk &&
                                                    drillDown?.rowType === (key === 'target' ? 'target' : 'actual') &&
                                                    (drillDown?.subKey ?? 'actual') === (key === 'target' ? 'target' : key);

                                                // Merged YTD: show Actual + Forecast stacked
                                                if (shouldMergeActualForecast && key === 'actual') {
                                                    const fcYtdVal = kpi.kpiId === 'decom_completion_rate' ? null : (kpi.forecast[ytdMk] ?? null);
                                                    const isActYtdActive = isYtdClickable && drillDown?.kpiId === kpi.kpiId && drillDown?.mk === ytdMk && drillDown?.rowType === 'actual';
                                                    const ytdTargetVal = kpi.kpiId === 'cost_savings' ? (cumulativeCostTargets[ytdMk] ?? null) : kpi.kpiId === 'decom_completion_rate' ? (decomRateTargets[ytdMk] ?? null) : (kpi.target[ytdMk] ?? null);
                                                    const fcYtdBg = 'transparent';
                                                    return (
                                                        <TableCell align="center" sx={{ padding: 0, ...innerDivider }}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
                                                                <Box
                                                                    onClick={isYtdClickable ? () => handleActualClick(kpi.kpiId, ytdMk, 'YTD', 'actual', 'actual') : undefined}
                                                                    sx={{ width: '50%', px: '4px', py: '3px', borderRight: '1px solid #e0e0e0', fontWeight: 700, fontSize: '0.825rem', cursor: isYtdClickable ? 'pointer' : 'default', bgcolor: isActYtdActive ? '#42B0D5' : 'transparent', color: isActYtdActive ? '#fff' : 'inherit', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': isYtdClickable ? { opacity: 0.8 } : {} }}
                                                                >
                                                                    {ytdVal != null ? (RATE_KPI_IDS.includes(kpi.kpiId) ? `${ytdVal}%` : kpi.kpiId === 'cost_savings' ? formatUSD(ytdVal) : ytdVal) : <span style={{ color: '#888', fontSize: '0.75rem', fontWeight: 600 }}>N/A</span>}
                                                                </Box>
                                                                <Box sx={{ width: '50%', px: '4px', py: '3px', bgcolor: fcYtdBg, color: '#555', fontWeight: 700, textAlign: 'center', fontSize: '0.825rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    {fcYtdVal != null ? (RATE_KPI_IDS.includes(kpi.kpiId) ? `${fcYtdVal}%` : kpi.kpiId === 'cost_savings' ? formatUSD(fcYtdVal) : fcYtdVal) : <span style={{ color: '#888', fontSize: '0.75rem' }}>—</span>}
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                    );
                                                }

                                                return (
                                                    <TableCell
                                                        align="center"
                                                        onClick={isYtdClickable ? () => handleActualClick(kpi.kpiId, ytdMk, 'YTD', key === 'target' ? 'target' : 'actual', key) : undefined}
                                                        sx={{
                                                            fontWeight: 700,
                                                            cursor: isYtdClickable ? 'pointer' : 'default',
                                                            bgcolor: isYtdActive ? '#42B0D5' : 'transparent',
                                                            color: isYtdActive ? '#fff' : 'inherit',
                                                            '&:hover': isYtdClickable ? { opacity: 0.8 } : {},
                                                            ...innerDivider,
                                                        }}
                                                    >
                                                        {ytdVal != null ? (
                                                            kpi.kpiId === 'cyber_risk_exposure' && key === 'target'
                                                                ? (ytdVal === 0 ? '0' : `<${ytdVal}`)
                                                                : (RATE_KPI_IDS.includes(kpi.kpiId) || key === 'actual_pct' || (kpi.kpiId === 'slippage_rate' && key === 'target')) ? `${ytdVal}%` :
                                                                kpi.kpiId === 'cost_savings' ? formatUSD(ytdVal) :
                                                                kpi.kpiId === 'cost_leakage' ? formatUSD(ytdVal) :
                                                                ytdVal
                                                        ) : <span style={{ color: '#424242', fontSize: '0.75rem', fontWeight: 600 }}>N/A</span>}
                                                    </TableCell>
                                                );
                                            })()}

                                        </TableRow>
                                    );
                                });
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#888' }}>
                Actuals are derived from decommissioning records. Click an <strong>Actual</strong> cell to see the apps decommissioned that month.
                <br />
                <strong>Annualised Cost realised from decommissioning<sup style={{ fontSize: '0.7em', color: '#000', fontWeight: 800, marginLeft: '1px' }}>*</sup></strong> reflects only costs that are currently mapped to applications (e.g., infrastructure costs, major software licences and major support costs). Some costs, including niche software licences and smaller vendor support costs, are not yet mapped to individual applications and are therefore excluded from this figure. This is a known gap that is being addressed.
            </Typography>

            {/* -- Drill-down detail table ----------------------------------- */}
            {drillDown && (
                <Box sx={{ mt: 3 }} ref={(el: HTMLDivElement | null) => el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })}>
                    {/* Title bar */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e3a5f' }}>
                            {kpiRows.find((k) => k.kpiId === drillDown.kpiId)?.kpiLabel ?? '# Apps Decommissioned'}{drillDown.kpiId !== 'slippage_rate' ? ` \u2014 ${drillDown.monthLabel}` : ''}{drillDown.rowType === 'target' ? ' (Target)' : drillDown.rowType === 'actual' ? ' (Actual)' : drillDown.rowType === 'forecast' ? ' (Forecast)' : ''}
                        </Typography>
                        {(() => {
                            const isSnap = drillDown.kpiId !== 'decom_apps';
                            const isRate = RATE_KPI_IDS.includes(drillDown.kpiId);
                            const isSlippage = drillDown.kpiId === 'slippage_rate';
                            const isPast = drillDown.mk < currentMk;
                            const isCumulativeActual = (drillDown.kpiId === 'decom_apps' || drillDown.kpiId === 'cost_savings' || drillDown.kpiId === 'decom_completion_rate') && drillDown.rowType === 'actual';
                            const isForecast = drillDown.rowType === 'forecast';
                            const caption = isForecast
                                ? drillDown.kpiId === 'cost_savings'
                                    ? `Apps projected to be decommissioned by ${drillDown.monthLabel} with their mapped cost savings (based on revised target dates)`
                                    : `Apps projected to be decommissioned by ${drillDown.monthLabel} (based on current revised target dates)`
                                : isCumulativeActual
                                ? `Apps decommissioned cumulative to ${drillDown.monthLabel}`
                                : isSlippage
                                ? `ERM apps with slipped decom target - cumulative to ${drillDown.monthLabel}`
                                : drillDown.kpiId === 'bia_validity'
                                ? (isPast ? 'Apps with delayed decom target as of end of month' : 'Apps with delayed decom target')
                                : isRate
                                ? (isPast ? 'In-scope apps as of end of month' : 'Current in-scope apps')
                                : (isPast ? 'Apps overdue as of this month' : 'Showing current live state');
                            return (
                                <>
                                    {isSnap && (
                                        <Typography variant="caption" sx={{ color: '#888', fontStyle: 'italic' }}>
                                            {caption}
                                        </Typography>
                                    )}
                                    <Chip
                                        label={
                                            drillApps.length === 0
                                                ? 'No applications in this month'
                                                : (filteredDrillApps.length === drillApps.length
                                                    ? `${drillApps.length} app${drillApps.length !== 1 ? 's' : ''}`
                                                    : `${filteredDrillApps.length} / ${drillApps.length} apps`)
                                        }
                                        size="small"
                                        sx={{ bgcolor: '#1976d2', color: '#fff', fontWeight: 600 }}
                                    />
                                </>
                            );
                        })()}
                        <Box sx={{ flexGrow: 1 }} />
                        {/* Search all columns */}
                        <TextField
                            size="small"
                            placeholder="Search all columns..."
                            value={drillSearch}
                            onChange={(e) => setDrillSearch(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: '#aaa' }} /></InputAdornment>,
                                endAdornment: drillSearch ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setDrillSearch('')}><Close sx={{ fontSize: 14 }} /></IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                            sx={{ minWidth: 220, '& .MuiInputBase-root': { fontSize: '13px', bgcolor: '#fff' } }}
                        />
                        <Button size="small" variant="outlined" onClick={clearDrillFilters} sx={{ textTransform: 'none', fontSize: '12px' }}>
                            Clear filters
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<Settings sx={{ fontSize: 16 }} />} onClick={() => setColSettingsOpen(true)} sx={{ textTransform: 'none', fontSize: '12px' }}>
                            Columns{hiddenDrillCols.filter(h => drillHeaders.includes(h)).length > 0 ? ` (${drillHeaders.filter(h => !hiddenDrillCols.includes(h)).length}/${drillHeaders.length})` : ''}
                        </Button>
                        <IconButton size="small" onClick={() => setDrillDown(null)} title="Close">
                            <Close sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>



                    <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2, overflow: 'auto', maxHeight: 420 }}>
                        <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { fontSize: '0.75rem', verticalAlign: 'middle' }, '& .MuiTableCell-root:first-of-type': { whiteSpace: 'nowrap', width: '1%', px: '6px' }, ...Object.fromEntries(drillHeaders.map((h, i) => hiddenDrillCols.includes(h) ? [`& th:nth-of-type(${i + 1}), & td:nth-of-type(${i + 1})`, { display: 'none' }] : null).filter(Boolean) as [string, object][]), ...(DEBUG_ALIGN ? {
                            '& .dbg-header .MuiTableCell-root': { border: '2px solid rgba(255,0,0,0.3)' },
                            '& .dbg-filters .MuiTableCell-root': { border: '2px solid rgba(0,128,0,0.25)' },
                            '& .dbg-body .MuiTableCell-root': { border: '1px dashed rgba(0,0,255,0.15)' },
                        } : {}) }}>
                            <TableHead>
                                {/* Headers computed ONCE - both text row and filter row use the same array to guarantee alignment */}
                                {(() => {
                                    const headers = drillHeaders;
                                    return (
                                        <>
                                            <TableRow className={DEBUG_ALIGN ? 'dbg-header' : ''} sx={{ bgcolor: '#1e3a5f' }}>
                                                {headers.map((h, idx) => {
                                                    const wrapHeaders = ['Original Target Decomm Date', 'New Target Decomm Date', 'Decommissioned Date', 'Annualised Savings (USD)', 'Decomm Status', 'Reason for Delay', 'Portfolio', 'Owner', 'Cyber Risk Exposure'];
                                                    const isWrap = wrapHeaders.includes(h);
                                                    const isFiltered = (colFilters[h]?.length ?? 0) > 0;
                                                    return (
                                                    <TableCell key={`hdr-${idx}`} onClick={() => { if (drillSortCol === h) { setDrillSortDir(d => d === 'asc' ? 'desc' : 'asc'); } else { setDrillSortCol(h); setDrillSortDir('asc'); } }} sx={{ position: 'sticky', top: 0, zIndex: 5, bgcolor: drillSortCol === h ? '#2e5090' : '#1e3a5f', color: '#fff', fontWeight: 700, whiteSpace: isWrap ? 'normal' : 'nowrap', ...(isWrap ? { minWidth: 120, maxWidth: 150 } : {}), cursor: 'pointer', userSelect: 'none', borderBottom: drillSortCol === h ? '2px solid #90caf9' : undefined }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                            <Box component="span" sx={{ flexGrow: 1 }}>
                                                                {h === 'Annualised Savings (USD)' ? <Tooltip title="Based on 2025 app cost" arrow placement="top"><span>{h}</span></Tooltip> : h}<span style={{ marginLeft: 5, fontSize: '0.8em', verticalAlign: 'middle', opacity: drillSortCol === h ? 1 : 0.35 }}>{drillSortCol === h ? (drillSortDir === 'asc' ? '▲' : '▼') : '↕'}</span>
                                                            </Box>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => { e.stopPropagation(); setFilterMenuCol(h); setFilterMenuAnchor(e.currentTarget); setFilterMenuSearch(''); }}
                                                                sx={{ p: '2px', color: isFiltered ? '#1976d2' : '#607d8b', '&:hover': { color: '#1976d2', bgcolor: 'rgba(25,118,210,0.10)' } }}
                                                            >
                                                                {isFiltered ? <FilterAlt sx={{ fontSize: 16 }} /> : <FilterAltOutlined sx={{ fontSize: 16 }} />}
                                                            </IconButton>
                                                        </Box>
                                                    </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        </>
                                    );
                                })()}
                            </TableHead>
                            <TableBody className={DEBUG_ALIGN ? 'dbg-body' : ''}>
                                {filteredDrillApps.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={(() => { const ecRaw = getDrillExtraCol(drillDown.kpiId, drillDown.mk); const ec = (drillDown.kpiId === 'failover_completeness' || drillDown.kpiId === 'restore_completeness') ? (ecRaw ? ecRaw.filter(c => c.header !== 'Original TDD') : null) : ecRaw; const isCat = (drillDown.kpiId === 'decom_apps' || drillDown.kpiId === 'cost_savings' || drillDown.kpiId === 'decom_completion_rate') && drillDown.rowType === 'actual'; const isCostFc = drillDown.kpiId === 'cost_savings' && drillDown.rowType === 'forecast'; const isDA = (drillDown.kpiId === 'decom_apps' || drillDown.kpiId === 'decom_completion_rate') && drillDown.rowType === 'actual'; const isDAT = drillDown.kpiId === 'decom_apps' && drillDown.rowType === 'target'; const isSR = drillDown.kpiId === 'slippage_rate'; const isCL = drillDown.kpiId === 'cost_leakage'; return isSR ? 11 : isCL ? 9 : ec ? 6 + ec.length + (isCat || isCostFc ? 1 : 0) + (isCostFc ? 1 : 0) : 7 + (isCat ? 1 : 0) + (isDA ? 1 : 0) + (isDAT ? 3 : 0); })()} align="center" sx={{ py: 4, color: '#888', fontStyle: 'italic' }}>
                                            {drillDown.kpiId !== 'decom_apps'
                                                ? 'No apps found matching this criteria for the selected period.'
                                                : 'No apps currently match this criteria.'}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sortedDrillApps.map((app, i) => {
                                        const d = parseDate(app.decom_date);
                                        const extraColsRaw = getDrillExtraCol(drillDown.kpiId, drillDown.mk);
                                        const extraCols = (drillDown.kpiId === 'failover_completeness' || drillDown.kpiId === 'restore_completeness')
                                            ? (extraColsRaw ? extraColsRaw.filter(c => c.header !== 'Original TDD') : null)
                                            : extraColsRaw;
                                        const isSlippageRow = drillDown.kpiId === 'slippage_rate';
                                        const decomDateStr = d
                                            ? `${String(d.getUTCDate()).padStart(2, '0')}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${d.getUTCFullYear()}`
                                            : '-';  // always center-aligned via TableCell align="center"
                                        const targetDecomDate = (() => {
                                            const td = parseDate(app.target_date_decom);
                                            return td
                                                ? `${String(td.getUTCDate()).padStart(2, '0')}-${String(td.getUTCMonth() + 1).padStart(2, '0')}-${td.getUTCFullYear()}`
                                                : '-';
                                        })();
                                        // Delay in months: diff between current TDD and original erm_tdd
                                        const delayMonths = (() => {
                                            const orig = parseDate(app.erm_tdd);
                                            const curr = parseDate(app.target_date_decom);
                                            if (!orig || !curr) return null;
                                            return (curr.getUTCFullYear() - orig.getUTCFullYear()) * 12 + (curr.getUTCMonth() - orig.getUTCMonth());
                                        })();
                                        const tddColor = isAppDateSlipped(app) ? '#c62828' : '#555';
                                        // If this is the decom_apps / decom_completion_rate drill, render cells driven by the header columns to guarantee alignment
                                        if (drillDown.kpiId === 'decom_apps' || drillDown.kpiId === 'decom_completion_rate') {
                                            const isDecomRateTarget = drillDown.kpiId === 'decom_completion_rate' && drillDown.rowType === 'target';
                                            const isDecomRateForecastRow = drillDown.kpiId === 'decom_completion_rate' && drillDown.rowType === 'forecast';
                                            const cols: string[] = isDecomRateTarget
                                                ? ['Portfolio', 'Platform', 'App ID', 'Application', 'Owner', 'Original Target Decomm Date', 'New Target Decomm Date', 'Decommissioned Date', 'Reason for Delay', 'MAC Status', 'Decomm Status', 'Suspended']
                                                : isDecomRateForecastRow
                                                ? ['Portfolio', 'Platform', 'App ID', 'Application', 'Owner', 'Original Target Decomm Date', 'New Target Decomm Date', 'Decommissioned Date', 'MAC Status', 'Decomm Status', 'Suspended']
                                                : ['Portfolio', 'Platform', 'App ID', 'Application', 'Owner', 'Original Target Decomm Date', 'New Target Decomm Date', 'Decommissioned Date', 'MAC Status', ...((drillDown.rowType === 'actual' || drillDown.kpiId === 'decom_apps') ? ['Decomm Status'] : []), 'Suspended'];
                                            return (
                                                <TableRow key={app.app_id ?? i} hover sx={{ bgcolor: i % 2 === 0 ? '#fff' : '#f9fbfd' }}>
                                                    {cols.map((col, ci) => {
                                                        switch (col) {
                                                            case 'App ID':
                                                                return (
                                                                    <TableCell key={ci} sx={{ fontWeight: 500 }}>
                                                                        {app.app_id ? (
                                                                            <a href={`https://mac.maersk.io/application/${app.app_id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'none', fontWeight: 500 }}
                                                                                onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                                                                                onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}>
                                                                                {app.app_id}
                                                                            </a>
                                                                        ) : '-'}
                                                                    </TableCell>
                                                                );
                                                            case 'Application': return <TableCell key={ci}>{app.app_name}</TableCell>;
                                                            case 'Portfolio': return <TableCell key={ci}>{app.portfolio}</TableCell>;
                                                            case 'Platform': return <TableCell key={ci}>{app.platform}</TableCell>;
                                                            case 'Owner': return <TableCell key={ci} sx={{ whiteSpace: 'normal', wordBreak: 'break-word', minWidth: 130, maxWidth: 160 }}>{app.owner_names ?? '-'}</TableCell>;
                                                            case 'Original Target Decomm Date': {
                                                                const td = parseDate(app.erm_tdd); const s = td ? `${String(td.getUTCDate()).padStart(2,'0')}-${String(td.getUTCMonth()+1).padStart(2,'0')}-${td.getUTCFullYear()}` : '-';
                                                                return <TableCell key={ci} align="center" sx={{ color: '#555' }}>{s}</TableCell>;
                                                            }
                                                            case 'New Target Decomm Date': return <TableCell key={ci} align="center" sx={{ color: tddColor, fontWeight: tddColor === '#c62828' ? 600 : undefined }}>{targetDecomDate}</TableCell>;
                                                            case 'Decommissioned Date': return <TableCell key={ci} align="center" sx={{ color: '#1565c0', fontWeight: 500 }}>{decomDateStr}</TableCell>;
                                                            case 'Reason for Delay': return (
                                                                <TableCell key={ci} align="center" sx={{ color: '#555', whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 200 }}>
                                                                    {app.decom_extension_reason ?? <span style={{ color: '#bbb', fontStyle: 'italic' }}>-</span>}
                                                                </TableCell>
                                                            );
                                                            case 'MAC Status':
                                                            case 'Status':
                                                                return (
                                                                    <TableCell key={ci}>
                                                                        <Chip label={app.status ?? '-'} size="small" sx={{ fontSize: '0.7rem', height: 20, fontWeight: 700, bgcolor: 'transparent !important', color: `${getMacStatusColor(app)} !important`, border: `1px solid ${getMacStatusColor(app)}`, '& .MuiChip-label': { color: `${getMacStatusColor(app)} !important` } }} />
                                                                    </TableCell>
                                                                );
                                                            case 'Decomm Status': {
                                                                if (isDecomRateTarget) {
                                                                    const statusLabel = getTargetDecommStatus(app);
                                                                    const displayLabel = statusLabel.replace(' (Delayed)', '');
                                                                    return (
                                                                        <TableCell key={ci}>
                                                                            <Tooltip title={TARGET_DECOMM_STATUS_TOOLTIP[displayLabel] ?? ''} arrow placement="top">
                                                                                <Chip label={displayLabel} size="small" sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600, bgcolor: 'transparent !important', color: `${TARGET_DECOMM_STATUS_COLOR[statusLabel] ?? '#9e9e9e'} !important`, border: `1px solid ${TARGET_DECOMM_STATUS_COLOR[statusLabel] ?? '#9e9e9e'}`, '& .MuiChip-label': { color: `${TARGET_DECOMM_STATUS_COLOR[statusLabel] ?? '#9e9e9e'} !important` }, }} />
                                                                            </Tooltip>
                                                                        </TableCell>
                                                                    );
                                                                }
                                                                const statusLabel = getTargetDecommStatus(app);
                                                                const displayLabel = statusLabel.replace(' (Delayed)', '');
                                                                return (
                                                                    <TableCell key={ci}>
                                                                        <Tooltip title={TARGET_DECOMM_STATUS_TOOLTIP[displayLabel] ?? ''} arrow placement="top">
                                                                            <Chip label={displayLabel} size="small" sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600, bgcolor: 'transparent !important', color: `${TARGET_DECOMM_STATUS_COLOR[statusLabel] ?? '#9e9e9e'} !important`, border: `1px solid ${TARGET_DECOMM_STATUS_COLOR[statusLabel] ?? '#9e9e9e'}`, '& .MuiChip-label': { color: `${TARGET_DECOMM_STATUS_COLOR[statusLabel] ?? '#9e9e9e'} !important` }, }} />
                                                                        </Tooltip>
                                                                    </TableCell>
                                                                );
                                                            }
                                                            case 'Suspended': return (
                                                                <TableCell key={ci} align="center">
                                                                    {app.suspended === true
                                                                        ? <Chip label="Yes" size="small" sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600, bgcolor: 'transparent !important', color: '#c62828 !important', border: '1px solid #c62828', '& .MuiChip-label': { color: '#c62828 !important' } }} />
                                                                        : app.suspended === false
                                                                        ? <Chip label="No" size="small" sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600, bgcolor: 'transparent !important', color: '#2e7d32 !important', border: '1px solid #2e7d32', '& .MuiChip-label': { color: '#2e7d32 !important' } }} />
                                                                        : <span style={{ color: '#bbb' }}>-</span>}
                                                                </TableCell>
                                                            );
                                                            default: return <TableCell key={ci} />;
                                                        }
                                                    })}
                                                </TableRow>
                                            );
                                        }

                                        // Cost Leakage: column-driven rendering for delayed apps
                                        if (drillDown.kpiId === 'cost_leakage') {
                                            const cols = ['Portfolio', 'Platform', 'App ID', 'Application', 'Original Target Decomm Date', 'New Target Decomm Date', 'Delay in Months', 'Potential Cost Impact', 'MAC Status', 'Suspended'];
                                            return (
                                                <TableRow key={app.app_id ?? i} hover sx={{ bgcolor: i % 2 === 0 ? '#fff' : '#f9fbfd' }}>
                                                    {cols.map((col, ci) => {
                                                        switch (col) {
                                                            case 'App ID':
                                                                return (
                                                                    <TableCell key={ci} sx={{ fontWeight: 500 }}>
                                                                        {app.app_id ? (
                                                                            <a href={`https://mac.maersk.io/application/${app.app_id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'none', fontWeight: 500 }}
                                                                                onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                                                                                onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}>
                                                                                {app.app_id}
                                                                            </a>
                                                                        ) : '-'}
                                                                    </TableCell>
                                                                );
                                                            case 'Application': return <TableCell key={ci}>{app.app_name}</TableCell>;
                                                            case 'Portfolio': return <TableCell key={ci}>{app.portfolio}</TableCell>;
                                                            case 'Platform': return <TableCell key={ci}>{app.platform}</TableCell>;
                                                            case 'Original Target Decomm Date': {
                                                                const td = parseDate(app.erm_tdd);
                                                                const s = td ? `${String(td.getUTCDate()).padStart(2,'0')}-${String(td.getUTCMonth()+1).padStart(2,'0')}-${td.getUTCFullYear()}` : '-';
                                                                return <TableCell key={ci} align="center" sx={{ color: '#555' }}>{s}</TableCell>;
                                                            }
                                                            case 'New Target Decomm Date': return <TableCell key={ci} align="center" sx={{ color: tddColor, fontWeight: tddColor === '#c62828' ? 600 : undefined }}>{targetDecomDate}</TableCell>;
                                                            case 'Delay in Months': return (
                                                                <TableCell key={ci} align="center">
                                                                    {delayMonths != null && delayMonths > 0
                                                                        ? <Chip label={`+${delayMonths}mo`} size="small" sx={{ fontSize: '0.68rem', height: 20, bgcolor: delayMonths > 3 ? '#c62828 !important' : '#ef6c00 !important', color: '#fff !important', '& .MuiChip-label': { color: '#fff !important' } }} />
                                                                        : <span style={{ color: '#bbb' }}>-</span>}
                                                                </TableCell>
                                                            );
                                                            case 'Potential Cost Impact': return (
                                                                <TableCell key={ci}>
                                                                    {(() => {
                                                                        const c = getItemCost(app);
                                                                        if (c == null || delayMonths == null || delayMonths <= 0)
                                                                            return <span style={{ color: '#888', fontStyle: 'italic' }}>N/A</span>;
                                                                        const impact = Math.round((c / 12) * delayMonths);
                                                                        return <span style={{ fontWeight: 600, color: '#e65100' }}>${impact.toLocaleString('en-US')}</span>;
                                                                    })()}
                                                                </TableCell>
                                                            );
                                                            case 'MAC Status':
                                                                return (
                                                                    <TableCell key={ci}>
                                                                        <Chip label={app.status ?? '-'} size="small" sx={{ fontSize: '0.7rem', height: 20, fontWeight: 700, bgcolor: 'transparent !important', color: `${getMacStatusColor(app)} !important`, border: `1px solid ${getMacStatusColor(app)}`, '& .MuiChip-label': { color: `${getMacStatusColor(app)} !important` } }} />
                                                                    </TableCell>
                                                                );
                                                            case 'Suspended': return (
                                                                <TableCell key={ci} align="center">
                                                                    {app.suspended === true
                                                                        ? <Chip label="Yes" size="small" sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600, bgcolor: 'transparent !important', color: '#c62828 !important', border: '1px solid #c62828', '& .MuiChip-label': { color: '#c62828 !important' } }} />
                                                                        : app.suspended === false
                                                                        ? <Chip label="No" size="small" sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600, bgcolor: 'transparent !important', color: '#2e7d32 !important', border: '1px solid #2e7d32', '& .MuiChip-label': { color: '#2e7d32 !important' } }} />
                                                                        : <span style={{ color: '#bbb' }}>-</span>}
                                                                </TableCell>
                                                            );
                                                            default: return <TableCell key={ci} />;
                                                        }
                                                    })}
                                                </TableRow>
                                            );
                                        }

                                        // BIA Validity: column-driven rendering for delayed/cancelled apps with expired BIA
                                        if (drillDown.kpiId === 'bia_validity') {
                                            const cols = ['Portfolio', 'Platform', 'App ID', 'Application', 'Original Target Decomm Date', 'New Target Decomm Date', 'BIA Status', 'MAC Status', 'Suspended'];
                                            return (
                                                <TableRow key={app.app_id ?? i} hover sx={{ bgcolor: i % 2 === 0 ? '#fff' : '#f9fbfd' }}>
                                                    {cols.map((col, ci) => {
                                                        switch (col) {
                                                            case 'App ID':
                                                                return (
                                                                    <TableCell key={ci} sx={{ fontWeight: 500 }}>
                                                                        {app.app_id ? (
                                                                            <a href={`https://mac.maersk.io/application/${app.app_id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'none', fontWeight: 500 }}
                                                                                onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                                                                                onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}>
                                                                                {app.app_id}
                                                                            </a>
                                                                        ) : '-'}
                                                                    </TableCell>
                                                                );
                                                            case 'Application': return <TableCell key={ci}>{app.app_name}</TableCell>;
                                                            case 'Portfolio': return <TableCell key={ci}>{app.portfolio}</TableCell>;
                                                            case 'Platform': return <TableCell key={ci}>{app.platform}</TableCell>;
                                                            case 'Is MVC': return (
                                                                <TableCell key={ci} align="center">
                                                                    {app.mvc_group ? <Chip label="MVC" size="small" sx={{ fontSize: '0.68rem', height: 20, bgcolor: '#1565c0 !important', color: '#fff !important', '& .MuiChip-label': { color: '#fff !important' } }} /> : <span style={{ color: '#bbb' }}>-</span>}
                                                                </TableCell>
                                                            );
                                                            case 'Original Target Decomm Date': {
                                                                const td = parseDate(app.erm_tdd);
                                                                const s = td ? `${String(td.getUTCDate()).padStart(2,'0')}-${String(td.getUTCMonth()+1).padStart(2,'0')}-${td.getUTCFullYear()}` : '-';
                                                                return <TableCell key={ci} align="center" sx={{ color: '#555' }}>{s}</TableCell>;
                                                            }
                                                            case 'New Target Decomm Date': return <TableCell key={ci} align="center" sx={{ color: tddColor, fontWeight: tddColor === '#c62828' ? 600 : undefined }}>{targetDecomDate}</TableCell>;
                                                            case 'BIA Status': return (
                                                                <TableCell key={ci}>
                                                                    <Chip
                                                                        label={app.bia_status === false ? 'Expired' : app.bia_status == null ? '-' : 'Valid'}
                                                                        size="small"
                                                                        sx={{
                                                                            fontSize: '0.7rem', height: 20, fontWeight: 600,
                                                                            bgcolor: 'transparent !important',
                                                                            color: `${app.bia_status === false ? '#c62828' : app.bia_status == null ? '#888' : '#2e7d32'} !important`,
                                                                            border: `1px solid ${app.bia_status === false ? '#c62828' : app.bia_status == null ? '#ccc' : '#2e7d32'}`,
                                                                            '& .MuiChip-label': { color: `${app.bia_status === false ? '#c62828' : app.bia_status == null ? '#888' : '#2e7d32'} !important` },
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                            );
                                                            case 'MAC Status':
                                                            case 'Status': return (
                                                                <TableCell key={ci}>
                                                                    <Chip label={app.status ?? '-'} size="small" sx={{ fontSize: '0.7rem', height: 20, fontWeight: 700, bgcolor: 'transparent !important', color: `${getMacStatusColor(app)} !important`, border: `1px solid ${getMacStatusColor(app)}`, '& .MuiChip-label': { color: `${getMacStatusColor(app)} !important` } }} />
                                                                </TableCell>
                                                            );
                                                            case 'Suspended': return (
                                                                <TableCell key={ci} align="center">
                                                                    {app.suspended === true
                                                                        ? <Chip label="Yes" size="small" sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600, bgcolor: 'transparent !important', color: '#c62828 !important', border: '1px solid #c62828', '& .MuiChip-label': { color: '#c62828 !important' } }} />
                                                                        : app.suspended === false
                                                                        ? <Chip label="No" size="small" sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600, bgcolor: 'transparent !important', color: '#2e7d32 !important', border: '1px solid #2e7d32', '& .MuiChip-label': { color: '#2e7d32 !important' } }} />
                                                                        : <span style={{ color: '#bbb' }}>-</span>}
                                                                </TableCell>
                                                            );
                                                            default: return <TableCell key={ci} />;
                                                        }
                                                    })}
                                                </TableRow>
                                            );
                                        }

                                        return (
                                            <TableRow key={app.app_id ?? i} hover sx={{ bgcolor: i % 2 === 0 ? '#fff' : '#f9fbfd' }}>
                                                <TableCell>{app.portfolio}</TableCell>
                                                <TableCell>{app.platform}</TableCell>
                                                <TableCell sx={{ fontWeight: 500 }}>
                                                    {app.app_id ? (
                                                        <a href={`https://mac.maersk.io/application/${app.app_id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'none', fontWeight: 500 }}
                                                            onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                                                            onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}>
                                                            {app.app_id}
                                                        </a>
                                                    ) : '-'}
                                                </TableCell>
                                                <TableCell>{app.app_name}</TableCell>
                                                {isSlippageRow ? (
                                                    <>
                                                        {/* Is MVC */}
                                                        <TableCell align="center">
                                                            {app.mvc_group ? <Chip label="MVC" size="small" sx={{ fontSize: '0.68rem', height: 20, bgcolor: '#1565c0 !important', color: '#fff !important', '& .MuiChip-label': { color: '#fff !important' } }} /> : <span style={{ color: '#bbb' }}>-</span>}
                                                        </TableCell>
                                                        {/* Original Target Decomm Date (erm_tdd) */}
                                                        <TableCell align="center" sx={{ color: '#555' }}>
                                                            {(() => { const td = parseDate(app.erm_tdd); return td ? `${String(td.getUTCDate()).padStart(2,'0')}-${String(td.getUTCMonth()+1).padStart(2,'0')}-${td.getUTCFullYear()}` : '-'; })()}
                                                        </TableCell>
                                                        {/* TDD as on Month (the TDD set in the slip revision) */}
                                                        <TableCell align="center" sx={{ color: '#1565c0', fontWeight: 500 }}>
                                                            {(() => { const raw = getSlipRevisionTdd(app, parseInt(drillDown.mk.slice(0, 4), 10)); const td = parseDate(raw); return td ? `${String(td.getUTCDate()).padStart(2,'0')}-${String(td.getUTCMonth()+1).padStart(2,'0')}-${td.getUTCFullYear()}` : '-'; })()}
                                                        </TableCell>
                                                        {/* New Target Decomm Date (current TDD from MAC) */}
                                                        <TableCell align="center" sx={{ color: '#e65100', fontWeight: 500 }}>{targetDecomDate}</TableCell>
                                                        {/* Delay in Months */}
                                                        <TableCell align="center">
                                                            {delayMonths != null ? <Chip label={`+${delayMonths}mo`} size="small" sx={{ fontSize: '0.68rem', height: 20, bgcolor: delayMonths > 3 ? '#c62828 !important' : '#ef6c00 !important', color: '#fff !important', '& .MuiChip-label': { color: '#fff !important' } }} /> : '-'}
                                                        </TableCell>
                                                        {/* Reason for Delay */}
                                                        <TableCell align="center" sx={{ color: '#555', minWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.5 }}>
                                                            {app.decom_extension_reason ?? <span style={{ color: '#bbb', fontStyle: 'italic' }}>-</span>}
                                                        </TableCell>
                                                        {/* MAC Status */}
                                                        <TableCell>
                                                            <Chip label={app.status ?? '-'} size="small" sx={{ fontSize: '0.7rem', height: 20, fontWeight: 700, bgcolor: 'transparent !important', color: `${getMacStatusColor(app)} !important`, border: `1px solid ${getMacStatusColor(app)}`, '& .MuiChip-label': { color: `${getMacStatusColor(app)} !important` } }} />
                                                        </TableCell>
                                                    </>
                                                ) : extraCols ? (
                                                    // Special rendering for BIA validity drill to enforce column order
                                                    drillDown.kpiId === 'bia_validity' ? (
                                                        <>
                                                            {/* Original Target Decomm Date */}
                                                            <TableCell>
                                                                {(() => {
                                                                    const td = parseDate(app.erm_tdd);
                                                                    if (!td) return <span style={{ color: '#bbb' }}>-</span>;
                                                                    const s = `${String(td.getUTCDate()).padStart(2, '0')}-${String(td.getUTCMonth() + 1).padStart(2, '0')}-${td.getUTCFullYear()}`;
                                                                    return <span style={{ color: '#555' }}>{s}</span>;
                                                                })()}
                                                            </TableCell>
                                                            {/* New Target Decomm Date */}
                                                            <TableCell align="center" sx={{ color: tddColor, fontWeight: tddColor === '#c62828' ? 600 : undefined }}>{targetDecomDate}</TableCell>
                                                            {/* Annual Cost */}
                                                            <TableCell>
                                                                {(() => {
                                                                    const c = getItemCost(app);
                                                                    return c == null ? (
                                                                        <span style={{ color: '#888', fontStyle: 'italic' }}>N/A</span>
                                                                    ) : renderCostLink(c);
                                                                })()}
                                                            </TableCell>
                                                            {/* Delay in Months */}
                                                            <TableCell align="center">
                                                                {(() => {
                                                                    const orig = parseDate(app.erm_tdd);
                                                                    const curr = parseDate(app.target_date_decom);
                                                                    if (!orig || !curr) return <span style={{ color: '#bbb' }}>-</span>;
                                                                    const months = (curr.getUTCFullYear() - orig.getUTCFullYear()) * 12 + (curr.getUTCMonth() - orig.getUTCMonth());
                                                                    if (months <= 0) return <span style={{ color: '#bbb' }}>-</span>;
                                                                    return <Chip label={`+${months}mo`} size="small" sx={{ fontSize: '0.68rem', height: 20, bgcolor: months > 3 ? '#c62828 !important' : '#ef6c00 !important', color: '#fff !important', '& .MuiChip-label': { color: '#fff !important' } }} />;
                                                                })()}
                                                            </TableCell>
                                                            {/* Potential Cost Impact */}
                                                            <TableCell>
                                                                {(() => {
                                                                    const c = getItemCost(app);
                                                                    if (c == null) return <span style={{ color: '#888', fontStyle: 'italic' }}>N/A</span>;
                                                                    const origD = parseDate(app.erm_tdd);
                                                                    const currD = parseDate(app.target_date_decom);
                                                                    const dm = (origD && currD) ? (currD.getUTCFullYear() - origD.getUTCFullYear()) * 12 + (currD.getUTCMonth() - origD.getUTCMonth()) : 0;
                                                                    if (dm <= 0) return <span style={{ color: '#888', fontStyle: 'italic' }}>N/A</span>;
                                                                    const impact = Math.round((c / 12) * dm);
                                                                    return <span style={{ fontWeight: 600, color: '#e65100' }}>${impact.toLocaleString('en-US')}</span>;
                                                                })()}
                                                            </TableCell>
                                                            <TableCell>
                                                                {(() => {
                                                                    const expired = app.bia_status === false;
                                                                    const biaColor = expired ? '#c62828' : app.bia_status == null ? '#888' : '#2e7d32';
                                                                    return (
                                                                        <Chip
                                                                            label={expired ? 'Expired' : (app.bia_status == null ? '-' : 'Valid')}
                                                                            size="small"
                                                                            sx={{
                                                                                fontSize: '0.7rem', height: 20, fontWeight: 600,
                                                                                bgcolor: 'transparent !important',
                                                                                color: `${biaColor} !important`,
                                                                                border: `1px solid ${app.bia_status == null ? '#ccc' : biaColor}`,
                                                                                '& .MuiChip-label': { color: `${biaColor} !important` },
                                                                            }}
                                                                        />
                                                                    );
                                                                })()}
                                                            </TableCell>
                                                        </>
                                                    ) : drillDown.kpiId === 'failover_completeness' || drillDown.kpiId === 'restore_completeness' ? (
                                                        <>
                                                            {/* Original Target Decomm Date */}
                                                            <TableCell>
                                                                {(() => {
                                                                    const td = parseDate(app.erm_tdd);
                                                                    if (!td) return <span style={{ color: '#bbb' }}>-</span>;
                                                                    const s = `${String(td.getUTCDate()).padStart(2, '0')}-${String(td.getUTCMonth() + 1).padStart(2, '0')}-${td.getUTCFullYear()}`;
                                                                    return <span style={{ color: '#555' }}>{s}</span>;
                                                                })()}
                                                            </TableCell>
                                                            {/* New Target Decomm Date */}
                                                            <TableCell align="center" sx={{ color: tddColor, fontWeight: tddColor === '#c62828' ? 600 : undefined }}>{targetDecomDate}</TableCell>
                                                            {/* Other extra cols (skip duplicate Original Target Date) */}
                                                            {extraCols ? extraCols.filter(c => c.header !== 'Original Target Decomm Date').map((col, ci) => (
                                                                <TableCell key={ci}>{col.renderCell(app)}</TableCell>
                                                            )) : null}
                                                        </>
                                                    ) : (
                                                        <>
                                                            {drillDown.kpiId === 'mvc_failover_overdue' && <TableCell>{(app as any).app_type ?? '-'}</TableCell>}
                                                            {/* Original Target Decomm Date (erm_tdd) - consistent across all KPIs */}
                                                            <TableCell align="center" sx={{ color: '#555' }}>
                                                                {(() => { const td = parseDate(app.erm_tdd); return td ? `${String(td.getUTCDate()).padStart(2,'0')}-${String(td.getUTCMonth()+1).padStart(2,'0')}-${td.getUTCFullYear()}` : '-'; })()}
                                                            </TableCell>
                                                            {/* New Target Decomm Date (current TDD from MAC) */}
                                                            <TableCell align="center" sx={{ color: tddColor, fontWeight: tddColor === '#c62828' ? 600 : undefined }}>{targetDecomDate}</TableCell>
                                                {/* Decommissioned Date - only for cost_savings (actual and forecast) */}
                                                            {(drillDown.kpiId === 'cost_savings') && (
                                                                <TableCell align="center" sx={{ color: '#1565c0', fontWeight: 500 }}>{decomDateStr}</TableCell>
                                                            )}
                                                            {extraCols ? extraCols.map((col, ci) => {
                                                                if (drillDown.kpiId === 'mvc_failover_overdue' && (col.header === 'Planned Failover Test Date' || col.header === 'Planned Restore Test Date')) {
                                                                    if (i === 0) return <TableCell key={ci} rowSpan={sortedDrillApps.length} align="center" sx={{ color: '#9e9e9e', fontStyle: 'italic', fontWeight: 600, verticalAlign: 'middle', borderLeft: '1px solid #e0e0e0' }}>Coming Soon</TableCell>;
                                                                    return null;
                                                                }
                                                                return <TableCell key={ci}>{col.renderCell(app)}</TableCell>;
                                                            }) : null}
                                                        </>
                                                    )
                                                ) : (
                                                    <>
                                                        {/* For target: show erm_tdd; for actual: Target Decom Date first, then Decom Date */}
                                                        {drillDown.kpiId === 'decom_apps' && drillDown.rowType === 'target' ? (() => {
                                                            const td = parseDate(app.erm_tdd);
                                                            const tdStr = td ? `${String(td.getUTCDate()).padStart(2, '0')}-${String(td.getUTCMonth() + 1).padStart(2, '0')}-${td.getUTCFullYear()}` : '-';
                                                            return (
                                                                <>
                                                                    <TableCell align="center" sx={{ color: '#555' }}>{tdStr}</TableCell>
                                                                    <TableCell align="center" sx={{ color: tddColor, fontWeight: tddColor === '#c62828' ? 600 : undefined }}>{targetDecomDate}</TableCell>
                                                                    <TableCell align="center" sx={{ color: '#1565c0', fontWeight: 500 }}>{decomDateStr}</TableCell>
                                                                </>
                                                            );
                                                        })() : (
                                                            <>
                                                                {/* Target Decom Date */}
                                                                {(() => {
                                                                    const td = parseDate(app.erm_tdd);
                                                                    if (td) {
                                                                        const s = `${String(td.getUTCDate()).padStart(2, '0')}-${String(td.getUTCMonth() + 1).padStart(2, '0')}-${td.getUTCFullYear()}`;
                                                                        return <TableCell align="center" sx={{ color: '#555' }}>{s}</TableCell>;
                                                                    }
                                                                    const tagDate = parseDecommTagDate(app.tag?.decomm_tag);
                                                                    return <TableCell align="center" sx={{ color: '#888' }}>{tagDate ? `${MONTH_NAMES[tagDate.month - 1].slice(0, 3)} ${tagDate.year}` : '-'}</TableCell>;
                                                                })()}
                                                                {/* Current TDD from MAC (for decom_apps actual/target) */}
                                                                {(drillDown.kpiId === 'decom_apps' && drillDown.rowType === 'actual') && (
                                                                    <TableCell align="center" sx={{ color: '#e65100', fontWeight: 500 }}>{targetDecomDate}</TableCell>
                                                                )}
                                                                {/* Decom Date */}
                                                                <TableCell align="center" sx={{ color: '#1565c0', fontWeight: 500 }}>{decomDateStr}</TableCell>
                                                            </>
                                                        )}
                                                        <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', minWidth: 130, maxWidth: 160 }}>{app.owner_names ?? '-'}</TableCell>
                                                    </>
                                                )}
                                                {/* Status column - always shows app.status (skipped for slippage which renders its own MAC Status cell) */}
                                                {!isSlippageRow && <TableCell>
                                                    <Chip label={app.status ?? '-'} size="small"
                                                        sx={{
                                                            fontSize: '0.7rem', height: 20, fontWeight: 700,
                                                            bgcolor: 'transparent !important',
                                                            color: `${getMacStatusColor(app)} !important`,
                                                            border: `1px solid ${getMacStatusColor(app)}`,
                                                            '& .MuiChip-label': { color: `${getMacStatusColor(app)} !important` },
                                                        }} />
                                                </TableCell>}
                                                {/* Decomm Status column - for decom_apps (target & actual) and cost_savings actual/forecast */}
                                                {(((drillDown.kpiId === 'decom_apps' || drillDown.kpiId === 'cost_savings') && drillDown.rowType === 'actual') || (drillDown.kpiId === 'decom_apps' && drillDown.rowType === 'target') || (drillDown.kpiId === 'cost_savings' && drillDown.rowType === 'forecast')) && (() => {
                                                    const statusLabel = getTargetDecommStatus(app);
                                                    const displayLabel = statusLabel.replace(' (Delayed)', '');
                                                    return (
                                                        <TableCell>
                                                            <Tooltip title={TARGET_DECOMM_STATUS_TOOLTIP[displayLabel] ?? ''} arrow placement="top">
                                                                <Chip label={displayLabel} size="small"
                                                                    sx={{
                                                                        fontSize: '0.68rem', height: 20, fontWeight: 600,
                                                                        bgcolor: 'transparent !important',
                                                                        color: `${TARGET_DECOMM_STATUS_COLOR[statusLabel] ?? '#9e9e9e'} !important`,
                                                                        border: `1px solid ${TARGET_DECOMM_STATUS_COLOR[statusLabel] ?? '#9e9e9e'}`,
                                                                        '& .MuiChip-label': { color: `${TARGET_DECOMM_STATUS_COLOR[statusLabel] ?? '#9e9e9e'} !important` },
                                                                    }} />
                                                            </Tooltip>
                                                        </TableCell>
                                                    );
                                                })()}
                                                {/* Suspended column (optional — hidden by default via Column Settings) */}
                                                <TableCell align="center">
                                                    {app.suspended === true
                                                        ? <Chip label="Yes" size="small" sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600, bgcolor: 'transparent !important', color: '#c62828 !important', border: '1px solid #c62828', '& .MuiChip-label': { color: '#c62828 !important' } }} />
                                                        : app.suspended === false
                                                        ? <Chip label="No" size="small" sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600, bgcolor: 'transparent !important', color: '#2e7d32 !important', border: '1px solid #2e7d32', '& .MuiChip-label': { color: '#2e7d32 !important' } }} />
                                                        : <span style={{ color: '#bbb' }}>-</span>}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Per-column funnel filter popover */}
                    <Popover
                        open={Boolean(filterMenuAnchor && filterMenuCol)}
                        anchorEl={filterMenuAnchor}
                        onClose={() => { setFilterMenuAnchor(null); setFilterMenuCol(null); }}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                        marginThreshold={16}
                    >
                        {(() => {
                            const col = filterMenuCol;
                            if (!col) return null;
                            const allValues = Array.from(new Set(drillApps.map((a) => getColFilterValue(a, col))))
                                .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
                            const selected = colFilters[col];
                            const isChecked = (v: string) => !selected || selected.includes(v);
                            const visibleValues = allValues.filter((v) => v.toLowerCase().includes(filterMenuSearch.toLowerCase()));
                            const allSelected = !selected || selected.length === allValues.length;
                            const toggle = (v: string) => {
                                const base = selected ?? allValues;
                                const next = base.includes(v) ? base.filter((x) => x !== v) : [...base, v];
                                setColFilters((prev) => {
                                    const cp = { ...prev };
                                    if (next.length === allValues.length) delete cp[col];
                                    else cp[col] = next;
                                    return cp;
                                });
                            };
                            return (
                                <Box sx={{ width: 260, maxHeight: 380, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ p: 1, pb: 0.5, fontWeight: 700, fontSize: '0.8rem', color: '#1e3a5f' }}>Filter: {col}</Box>
                                    <Box sx={{ px: 1, pb: 0.5 }}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            autoFocus
                                            placeholder="Search values..."
                                            value={filterMenuSearch}
                                            onChange={(e) => setFilterMenuSearch(e.target.value)}
                                            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16 }} /></InputAdornment> }}
                                            sx={{ '& .MuiInputBase-root': { fontSize: '12px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, px: 1, pb: 0.5 }}>
                                        <Button size="small" sx={{ fontSize: '0.7rem', minWidth: 0 }} onClick={() => setColFilters((prev) => { const cp = { ...prev }; delete cp[col]; return cp; })}>Select all</Button>
                                        <Button size="small" sx={{ fontSize: '0.7rem', minWidth: 0 }} onClick={() => setColFilters((prev) => ({ ...prev, [col]: [] }))}>Clear</Button>
                                    </Box>
                                    <Divider />
                                    <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
                                        {visibleValues.length === 0 ? (
                                            <Box sx={{ p: 1.5, fontSize: '0.75rem', color: '#888', fontStyle: 'italic' }}>No values</Box>
                                        ) : visibleValues.map((v) => (
                                            <MenuItem key={v} dense onClick={() => toggle(v)} sx={{ py: 0 }}>
                                                <Checkbox size="small" checked={isChecked(v)} sx={{ p: 0.5 }} />
                                                <ListItemText primaryTypographyProps={{ fontSize: '0.78rem', noWrap: true }} primary={v} />
                                            </MenuItem>
                                        ))}
                                    </Box>
                                    <Divider />
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
                                        <Button size="small" variant="contained" sx={{ fontSize: '0.7rem' }} onClick={() => { setFilterMenuAnchor(null); setFilterMenuCol(null); }}>Done</Button>
                                    </Box>
                                </Box>
                            );
                        })()}
                    </Popover>

                    {/* Column show/hide settings dialog */}
                    <Dialog open={colSettingsOpen} onClose={() => setColSettingsOpen(false)} maxWidth="sm" fullWidth>
                        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Settings sx={{ color: '#666' }} /> Column Settings
                        </DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>Select which columns to display:</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                                {drillHeaders.map((h) => (
                                    <FormControlLabel key={h}
                                        control={<Checkbox size="small" checked={!hiddenDrillCols.includes(h)} onChange={(e) => setHiddenDrillCols((prev) => e.target.checked ? prev.filter((x) => x !== h) : [...prev, h])} />}
                                        label={<Typography variant="body2">{h}</Typography>} />
                                ))}
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setHiddenDrillCols([])} sx={{ mr: 'auto', textTransform: 'none' }}>Show all</Button>
                            <Button onClick={() => setColSettingsOpen(false)} variant="contained" sx={{ textTransform: 'none' }}>Done</Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            )}
        </Box>
    );
};
export default DecomBowlerMetrics;